import {
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  type IAction,
  type IDisplayItem,
  type IActionDefinitionDto,
  type IActionParameter,
  type IEvent,
  type ISymbol,
  type ITargetScreenInputParam
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import type { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { filterActionDefinitions } from "../../action-definition-filter";
import { reloadActionDefinitions } from "../../action-definition-refresh";
import {
  type AiLocale,
  type ConfirmationDefaults,
  type ConfirmationKind,
  getConfirmationDefaults,
  resolveProjectLocale
} from "../../ai-localized-defaults";
import {
  addDesignerActionBlankCondition,
  addDesignerActionToCollection,
  addDesignerActionConfirmation,
  addDesignerActionRecurring,
  buildDesignerActionParameterSymbol,
  findDesignerActionParameter,
  getDesignerActionCollectionActions,
  listDesignerActionParameterBindingOptions,
  reorderDesignerActionCollection,
  removeDesignerActionCondition,
  removeDesignerActionConfirmation,
  removeDesignerActionRecurring,
  setDesignerActionAddGoBackAction,
  setDesignerActionAuditEnabled,
  setDesignerActionAuditLevel,
  setDesignerActionEnabled,
  setDesignerActionCondition,
  setDesignerActionConfirmation,
  setDesignerActionDontTriggerValidations,
  setDesignerActionForEachData,
  setDesignerActionManagedDbData,
  setDesignerActionMqttSubscribeData,
  setDesignerActionParameter,
  setDesignerActionRecurringActionsData,
  setDesignerActionValues,
  setDesignerActionRecurringInterval,
  setDesignerActionNote,
  setDesignerActionSqlLikeSetting,
  setDesignerActionTag,
  isBooleanActionParameterType
} from "../../designer-action-actions";
import { AUTO_SET_PARAMS, getDesignerActionParameterOptions } from "../../designer-action-catalog-actions";
import { buildDesignerActionClipboard, pasteDesignerActionClipboard } from "../../designer-action-clipboard-actions";
import {
  collectDesignerActionsFromCollection,
  deleteDesignerActionFromTree,
  findDesignerActionInCurrentContext,
  replaceDesignerActionInTree,
  resolveDesignerActionScope
} from "../../designer-action-context-actions";
import { getDesignerActionParameterEditorConfig } from "../../designer-action-parameter-editor-actions";
import { getValues, type ScreenDesignInstance } from "../../designer-context";
import { buildDesignerSymbol, enrichCurrentRowSymbol, hostHasRowContext } from "../../designer-property-actions";
import {
  buildActionSummaryScreenIndex,
  summarizeActionBindings,
  type IActionBindingSummary
} from "../../designer-screen-query-actions";
import {
  applyDefaultStartProcessProjectId,
  buildWorkflowStartExpansionContext,
  expandWorkflowResumeOutputParameters,
  expandWorkflowStartInputParameters,
  isWorkflowResumeAction,
  isWorkflowStartAction,
  type ISdWorkflowStartSource,
  type IWorkflowResumeSource
} from "../../designer-workflow-start-params";
import {
  resolveProcessBindingWriteId,
  runProcessBindingWrite,
  type IProcessBindingWriteScope
} from "../../managed-screen-binding-write";
import { fetchManagedScreenWorkflow } from "../../managed-screen-workflow-fetch";
import type { ScreenIndex } from "../../navigation-target-collector";
import { PropertySdHelper } from "../../property-sd-helper";
import { repairTaskFeedColumns, type ITaskFeedColumnRepair } from "../../task-feed-column-repair";
import { isTaskFeedAction, listTaskFeedFieldOptions, type ITaskFeedFieldOption } from "../../task-feed-process-scope";
import {
  TASK_FEED_FIELDS_PARAM,
  TASK_FEED_PROCESS_PARAM,
  clearedTaskFeedScope,
  isTaskFeedScopeRefusal,
  resolveTaskFeedScope,
  type ITaskFeedScopePlan,
  type ITaskFeedWorkflowLike,
  type TaskFeedScopeOutcome
} from "../../task-feed-process-scope";
import {
  collectTaskFeedHostedComponents,
  readTaskFeedScreen,
  type ITaskFeedBoundField,
  type ITaskFeedScreenReading,
  type ITaskFeedUnshowableColumn
} from "../../task-feed-reconcile";
import { getSdInstance, getAvailableEvents } from "../helpers";
import { validateNavigateBinding } from "../navigate-binding-validator";
import { validateParameterSourceAffinity } from "../parameter-source-name-validator";
import { hidePlatformActionsFromLlm } from "../platform-action-visibility";
import { classifySqlMutationShape, classifySqlStatementKind } from "../sql-statement-kind";
import type { SqlMutationShape, SqlStatementKind } from "../sql-statement-kind";
import type { ToolHandlerGroup, ToolInput } from "../types";
import { describeCurrentView, type CurrentViewDescription } from "./navigation-tools";

/**
 * KOA-18226 — attach an action to a screen component's event, and refuse to claim success
 * unless it is actually there afterwards.
 *
 * `saveActionToEvent` routes by `dashboardState` and several of its branches are empty, so
 * a designer sitting on another tool discards every screen attach; the component path
 * itself no-ops when the item is missing from the dictionary it reads. This handler used to
 * call it and return `{success: true, actionId}` unconditionally, because the write
 * returned `void` and there was nothing to check.
 *
 * The cost of that silence, measured on `chat_1787691143910_9s8aur`: four attaches reported
 * success and none landed, the Binder could not bind onto events that were never created,
 * the Wirer re-attached four times (its own duplicate guard never fired — there was nothing
 * to find), a residual replan then rewrote a plan that was never wrong, and three further
 * screens were built from that page's brief. Twenty-nine Designer turns, one silent
 * `return`.
 *
 * The error text carries the branch and the live view because the tool result is the only
 * channel that reaches telemetry from here — nothing designer-side can write a validator
 * signal. That makes the next occurrence self-describing instead of another archaeology
 * session.
 *
 * Returns an error object to hand straight back, or null when the action is on the event.
 */
export function attachToScreenEventOrExplain(
  sd: ScreenDesignInstance,
  kuikaId: string,
  eventName: string,
  action: IAction
): { success: false; error: string } | null {
  const event: IEvent = { eventName, actions: [] } as unknown as IEvent;
  const outcome = sd.propertyProvider.saveActionToEvent(kuikaId, action, event);
  if (outcome.attachedToComponent) return null;
  const view: CurrentViewDescription = describeCurrentView(sd);
  return {
    success: false,
    error:
      `Attach did NOT persist: "${action.label}" on ${eventName} of component "${kuikaId}". ` +
      `The write was routed to "${outcome.owner}" while the designer is on view "${view.view}" ` +
      `(left panel "${view.leftPanel}"). A screen action-attach only lands while the design ` +
      `canvas is the active view — switch back to it and retry. Do NOT re-issue this attach ` +
      `as-is; it will keep reporting the same thing.`
  };
}

function safeParse(val: any): any {
  if (val == null) return val;
  if (typeof val === "string") {
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  }
  return val;
}

// ---------------------------------------------------------------------------
// LLM-side id-shape ergonomics — auto-resolve friendlyName / actionDefinitionId
//
// The Binder LLM consistently makes two id-shape mistakes that cost dozens
// of turns per build:
//
//   1. Passes a kebab-case `friendlyName` (e.g. `"btn-save"`, `"input-title"`)
//      where a numeric `kuikaId` is required (`"154875"`). The component
//      lookup then fails with "Component with kuikaId '<x>' not found" and
//      the Binder retries with the same wrong shape because nothing in the
//      error message hints at the shape mismatch.
//   2. Passes the catalog's `actionDefinitionId` (a 36-char Guid like
//      `"8d6c88c3-129e-..."`) where a per-instance `actionInstanceId` is
//      required. The action-scope resolver then fails with
//      "Action '<defGuid>' not found in screen_event" and the Binder
//      retries identically.
//
// Both shapes are DETERMINISTICALLY resolvable from the canvas state, so
// rather than ask the LLM to learn a fragile id-discipline rule, we
// auto-resolve at handler entry. The original value is kept in the error
// path so genuinely-unknown ids still surface a useful message.
//
// `resolveFriendlyKuikaId` returns the numeric kuikaId when the input
// looks like a friendlyName / id-alias and a unique match exists in the
// screen dictionary; otherwise returns the input unchanged.
// Special cases handled here:
//   - Already-numeric string ("154875") → passthrough
//   - "-1" (screen-level event target) → passthrough
//   - "pageInit" (legacy alias for "-1") → "-1"

const NUMERIC_KUIKA_ID_PATTERN = /^-?\d+$/;
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PAGEINIT_KUIKA_ALIASES = new Set(["pageinit", "screen", "page", "body"]);

function normalizeFriendlyMatchKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+|_|\.|"|'/g, "")
    .replace(/-/g, "");
}

export function resolveFriendlyKuikaId(input: unknown, sd: any): string | undefined {
  if (input == null) return undefined;
  const raw = String(input).trim();
  if (raw.length === 0) return raw;
  // Numeric kuikaId — already in the right shape. Includes "-1" (the
  // screen-level event target) which is the most common false positive
  // for a "looks like an alias" check.
  if (NUMERIC_KUIKA_ID_PATTERN.test(raw)) return raw;
  // Screen-level event aliases the LLM sometimes invents.
  if (PAGEINIT_KUIKA_ALIASES.has(raw.toLowerCase())) return "-1";
  // Bare placeholder shapes like "<btn-save>" / "{input-title}" that
  // sometimes leak into the LLM's call. Strip wrappers before matching.
  const stripped = raw.replace(/^[<{[]+|[>}\]]+$/g, "").trim();
  const dict = sd?.screenProvider?.state?.screenDesignDictionary;
  if (!dict) return raw;
  const targetKey = normalizeFriendlyMatchKey(stripped);
  if (targetKey.length === 0) return raw;
  // Walk the dictionary once, collect matches by both friendlyName and
  // component-tree id alias. A unique hit wins; an ambiguous match
  // (e.g. two Buttons share the same label) falls back to the raw input
  // so the downstream "not found" error keeps the LLM honest.
  const matches: string[] = [];
  for (const key of Object.keys(dict)) {
    const di = dict[key];
    const friendly = typeof di?.friendlyName === "string" ? di.friendlyName : undefined;
    if (friendly && normalizeFriendlyMatchKey(friendly) === targetKey) {
      matches.push(String(di.id));
    }
  }
  if (matches.length === 1) return matches[0];
  return raw;
}

/**
 * Resolve a `kuikaId`-shaped field in-place. Mutates the input object
 * so all downstream consumers see the resolved id. Idempotent: passing
 * the same input twice yields the same output.
 */
export function autoResolveKuikaIdField(input: Record<string, any>, fieldName: string, sd: any): void {
  if (input == null || typeof input !== "object") return;
  const raw = input[fieldName];
  if (raw == null) return;
  const resolved = resolveFriendlyKuikaId(raw, sd);
  if (resolved !== undefined && resolved !== raw) {
    input[fieldName] = resolved;
  }
}

/**
 * Fix #31 — Reject literal `<...>` placeholder values for kuikaId-shaped
 * fields at handler entry.
 *
 * Reproducer (May 2026, fikir-havuzu trace): the Wirer LLM emitted
 *   `ui_action.add_to_event({ kuikaId: "<button-id>", eventName: "onClick", actionDefinitionId: "Idea_Insert" })`
 * — a literal angle-bracketed placeholder, evidently from a skill
 * template the LLM failed to fill in. The handler accepted it silently
 * (resolveFriendlyKuikaId fell through to the raw string, then the
 * downstream dispatch returned `success: true` with a fresh actionId
 * that wasn't actually attached anywhere on the screen). The Binder
 * then couldn't find the Idea_Insert instance for binding and burned
 * the full maxTurns budget retrying. Rejecting the malformed input at
 * handler entry surfaces the failure immediately so the Wirer either
 * fills in the real kuikaId or — if it genuinely doesn't know it —
 * fails loudly instead of producing a ghost attachment.
 *
 * The pattern is narrow on purpose: a value must literally start with
 * `<` and end with `>` and contain no other angle brackets. This
 * matches the LLM's placeholder shape without false-positives on
 * legitimate identifiers (no real friendlyName starts with `<`).
 */
const PLACEHOLDER_LITERAL_PATTERN = /^<[^<>]+>$/;

export function isPlaceholderLiteralKuikaId(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return PLACEHOLDER_LITERAL_PATTERN.test(trimmed);
}

function placeholderLiteralError(fieldName: string, value: string): string {
  return (
    `[VALIDATION] ${fieldName}="${value}" looks like an unfilled placeholder — ` +
    `the value still has the literal angle-brackets from a skill template ` +
    `that wasn't substituted with the real id. ` +
    `Resolve a real kuikaId first: call \`ui_screen_query.find_by_type\` ` +
    `(or use the friendlyName from the brief's component inventory) and ` +
    `pass the numeric id like \`"154875"\` or the friendlyName like ` +
    `\`"save-button"\`. Do NOT pass angle-bracket placeholders — they ` +
    `silently route attachments to nowhere and the Binder downstream ` +
    `can't find the action.`
  );
}

/**
 * Auto-resolve `actionId` when it carries an `actionDefinitionId` instead
 * of the per-instance actionInstanceId. Strategy:
 *
 *   1. If `actionId` is missing or doesn't look like a Guid → leave alone.
 *   2. If `actionId` matches an existing action INSTANCE in the screen
 *      dictionary → already correct, no change.
 *   3. If `actionId` matches an action DEFINITION Guid (catalog id) AND
 *      a unique attached instance for that definition exists in the
 *      (kuikaId, eventName) scope provided → rewrite to that instance.
 *   4. Otherwise leave alone so the existing "Action not found" error
 *      keeps the LLM honest.
 *
 * The `scope` argument carries the host component + event for the
 * binding — the resolver uses these to pick the right instance when
 * the same action definition is attached to multiple events. When the
 * scope is partial (only kuikaId provided), the resolver still works
 * if there's only ONE instance of that definition on the screen.
 */
interface ActionInstanceResolveScope {
  kuikaId?: unknown;
  eventName?: unknown;
}

export function autoResolveActionInstanceId(
  input: Record<string, any>,
  sd: any,
  scope?: ActionInstanceResolveScope
): void {
  if (input == null || typeof input !== "object") return;
  const raw = typeof input.actionId === "string" ? input.actionId : undefined;
  if (!raw || !GUID_PATTERN.test(raw)) return;
  const dict = sd?.screenProvider?.state?.screenDesignDictionary;
  if (!dict) return;

  // Pass 1 — check whether the id is already a real instance id on this
  // screen. If yes, no resolution needed.
  const instanceMatches: Array<{ id: string; defId: string; kuikaId: string; eventName: string }> = [];
  const definitionInstanceCandidates: Array<{ id: string; kuikaId: string; eventName: string }> = [];

  // Fix #48 — walk IfThenElse branches as well. Mutations attached
  // INSIDE `ifThenElseData.ifActions[]` / `elseActions[]` (e.g. via
  // `ui_action.add_to_branch`) were invisible to the previous shallow
  // pass, so the May-2026 fikir-havuzu rerun at 10:50 left every
  // branch-bound Idea_Insert / Idea_Update with `Action <defId> not
  // found in screen_event` when the Binder tried to bind their
  // parameters by definition id. The recursive walk surfaces those
  // instances under the host kuikaId + event of the OUTER trigger
  // (where the IfThenElse itself is attached).
  function walkAction(
    action: { id?: unknown; actionDefinitionId?: unknown; ifThenElseData?: unknown } | undefined,
    hostKuikaId: string,
    eventName: string
  ): void {
    if (!action || typeof action !== "object") return;
    if (typeof action.id !== "string") return;
    if (action.id === raw) {
      instanceMatches.push({
        id: action.id,
        defId: typeof action.actionDefinitionId === "string" ? action.actionDefinitionId : "",
        kuikaId: hostKuikaId,
        eventName
      });
    } else if (typeof action.actionDefinitionId === "string" && action.actionDefinitionId === raw) {
      definitionInstanceCandidates.push({
        id: action.id,
        kuikaId: hostKuikaId,
        eventName
      });
    }
    const ifte = action.ifThenElseData as { ifActions?: unknown; elseActions?: unknown } | undefined;
    if (ifte && typeof ifte === "object") {
      if (Array.isArray(ifte.ifActions)) {
        for (const child of ifte.ifActions) walkAction(child, hostKuikaId, eventName);
      }
      if (Array.isArray(ifte.elseActions)) {
        for (const child of ifte.elseActions) walkAction(child, hostKuikaId, eventName);
      }
    }
  }

  for (const key of Object.keys(dict)) {
    const di = dict[key];
    const events = di?.item?.events;
    if (!Array.isArray(events)) continue;
    for (const evt of events) {
      const actions = evt?.actions;
      if (!Array.isArray(actions)) continue;
      const hostKuikaId: string = String(di.id);
      const eventName: string = typeof evt.eventName === "string" ? evt.eventName : "";
      for (const action of actions) {
        walkAction(action, hostKuikaId, eventName);
      }
    }
  }

  // Already a real instance — leave alone.
  if (instanceMatches.length > 0) return;

  // Definition-Guid input — pick the matching instance by (kuikaId, eventName)
  // when both are provided; otherwise accept a unique single-instance match.
  if (definitionInstanceCandidates.length === 0) return;

  const scopedKuikaId =
    scope?.kuikaId != null && scope.kuikaId !== "" ? resolveFriendlyKuikaId(scope.kuikaId, sd) : undefined;
  const scopedEvent = typeof scope?.eventName === "string" && scope.eventName.length > 0 ? scope.eventName : undefined;

  let scoped = definitionInstanceCandidates;
  if (scopedKuikaId !== undefined) {
    scoped = scoped.filter((c) => c.kuikaId === scopedKuikaId);
  }
  if (scopedEvent !== undefined) {
    scoped = scoped.filter((c) => c.eventName === scopedEvent);
  }
  if (scoped.length === 1) {
    input.actionId = scoped[0].id;
    return;
  }
  // Fix #19-revise — Narrow strict-scope to the screen-root pageInit
  // case only. The original Bug H reproducer was:
  //   scope = { kuikaId: "-1", eventName: "pageInit" }, scope-filter=0,
  //   global single instance was a different-host onClick → fallback
  //   routed the binding to the wrong handler.
  // Other explicit scopes (e.g. Binder passing the SOURCE component's
  // kuikaId by mistake) STILL need the fallback so
  // `correctParameterBindingHostScope` can run host recovery against
  // a resolved instance id. Without fallback, those bindings now fail
  // with "Action <definition-id> not found in screen_event" (observed
  // in production trace, May 2026).
  const isScreenRootPageInit = scopedKuikaId === "-1" && scopedEvent === "pageInit";
  if (isScreenRootPageInit) return;
  if (definitionInstanceCandidates.length === 1) {
    input.actionId = definitionInstanceCandidates[0].id;
  }
}

/**
 * Find the (kuikaId, eventName) host scope of an action instance on the
 * current screen. Returns `null` when the instance isn't attached.
 *
 * Used by `correctParameterBindingHostScope` (Fix #12) to recover from
 * the very common LLM mistake of passing the SOURCE component's kuikaId
 * (the input the value comes FROM) in the `kuikaId` slot of
 * `ui_action.set_multiple_parameter_bindings` — that slot is for the
 * HOST component (the button the action lives on). Without recovery
 * the handler returns "Event 'onClick' not found on component '<input-id>'"
 * and the LLM loops indefinitely (we've seen 22-turn loops on this in
 * production traces).
 */
interface ActionHostScope {
  kuikaId: string;
  eventName: string;
}

export function findActionHostByInstanceId(actionInstanceId: string, sd: any): ActionHostScope | null {
  const dict = sd?.screenProvider?.state?.screenDesignDictionary;
  if (!dict) return null;
  for (const key of Object.keys(dict)) {
    const di = dict[key];
    const events = di?.item?.events;
    if (!Array.isArray(events)) continue;
    for (const evt of events) {
      const actions = evt?.actions;
      if (!Array.isArray(actions)) continue;
      for (const action of actions) {
        if (typeof action?.id === "string" && action.id === actionInstanceId) {
          return {
            kuikaId: String(di.id),
            eventName: typeof evt.eventName === "string" ? evt.eventName : ""
          };
        }
      }
    }
  }
  return null;
}

/**
 * KOA-18422 — the action INSTANCE on this screen whose definition is named `name`.
 *
 * The `actionField:<Action>.<Field>` operand the wiring layer emits names the action
 * the way the contract does — by NAME, because the instance id exists only here. The
 * resolution is therefore this side's job, and it is done the same way
 * `delete_from_event` resolves an action named by type: through `actionMatchesType`,
 * against the instance's own label and its resolved definition's name.
 *
 * DEFINITIONS ARE READ UNFILTERED. A user-authored SQL action (`GetTask`) carries
 * `actionCategory: "Other"`, which the LLM-facing `getActionDefinitions` hides; using
 * that accessor here would make the screen's own loader unresolvable.
 *
 * PAGE INIT WINS A TIE. The operand means "the record this screen loaded", so when
 * the same action is attached more than once (a page load AND a refresh button) the
 * page-load instance is the one it names. Its result is also the one every other
 * handler can read, since `pageInit` has completed by then. With no page-load
 * instance a single match still answers; anything else returns null — two instances
 * write two different state keys (`ToActionName` appends the instance Tag) and
 * picking one would be a guess about which record the target opens with.
 */
interface ScreenActionInstanceMatch {
  id: string;
  eventName: string;
}

const PAGE_INIT_EVENT_NAME: string = "pageinit";

export function resolveScreenActionInstanceIdByName(actionName: string, sd: any): string | null {
  const trimmed: string = actionName.trim();
  if (trimmed.length === 0) return null;
  const dict = sd?.screenProvider?.state?.screenDesignDictionary;
  if (!dict) return null;
  const defs: readonly IActionDefinitionDto[] = getAllActionDefinitions(sd);

  const matches: ScreenActionInstanceMatch[] = [];
  const seen: Set<string> = new Set<string>();
  const walk = (action: IAction | undefined, eventName: string): void => {
    if (!action || typeof action !== "object") return;
    const id: unknown = (action as { id?: unknown }).id;
    if (typeof id === "string" && id.length > 0 && !seen.has(id) && actionMatchesType(action, trimmed, defs)) {
      seen.add(id);
      matches.push({ id, eventName: eventName.toLowerCase() });
    }
    // Branch-bound instances count too: a load placed inside an IfThenElse still
    // writes the same state key, and `autoResolveActionInstanceId` already walks
    // them for the same reason.
    const branches = (action as { ifThenElseData?: { ifActions?: unknown; elseActions?: unknown } }).ifThenElseData;
    if (branches && typeof branches === "object") {
      if (Array.isArray(branches.ifActions)) for (const child of branches.ifActions) walk(child as IAction, eventName);
      if (Array.isArray(branches.elseActions)) {
        for (const child of branches.elseActions) walk(child as IAction, eventName);
      }
    }
  };

  for (const key of Object.keys(dict)) {
    const di = dict[key];
    const events = di?.item?.events;
    if (!Array.isArray(events)) continue;
    for (const evt of events) {
      const actions = evt?.actions;
      if (!Array.isArray(actions)) continue;
      const eventName: string = typeof evt?.eventName === "string" ? evt.eventName : "";
      for (const action of actions) walk(action as IAction, eventName);
    }
  }

  const onPageInit: readonly ScreenActionInstanceMatch[] = matches.filter(
    (match: ScreenActionInstanceMatch): boolean => match.eventName === PAGE_INIT_EVENT_NAME
  );
  if (onPageInit.length === 1) return onPageInit[0].id;
  if (matches.length === 1) return matches[0].id;
  return null;
}

/**
 * Set of component types whose primary role is "value holder" — when
 * one of these is passed in the `kuikaId` slot of a parameter-binding
 * call with `bindingType: "componentValue"`, the LLM almost certainly
 * meant `sourceKuikaId` (where the value comes FROM). We use this to
 * decide whether to recover the misplaced field into sourceKuikaId.
 */
const VALUE_HOLDER_COMPONENT_TYPES: ReadonlySet<string> = new Set([
  "TextInput",
  "TextArea",
  "NumberInput",
  "SelectBox",
  "MultiSelect",
  "MultiSelectBox",
  "AutoComplete",
  "Date",
  "DateTime",
  "DateRange",
  "DateTimeRange",
  "TimePicker",
  "Checkbox",
  "Switch",
  "RadioButton",
  "RadioGroup",
  "RadioButtonGroup",
  "Slider",
  "Rate",
  "Percent",
  "Currency",
  "Password",
  "Email",
  "BarcodeInput",
  "PhoneInput",
  "MaskedInput",
  "NumericTextInput",
  "FileUpload",
  "ImageUpload"
]);

function isValueHolderComponent(kuikaId: unknown, sd: any): boolean {
  if (kuikaId == null) return false;
  const raw = String(kuikaId).trim();
  if (raw.length === 0) return false;
  const dict = sd?.screenProvider?.state?.screenDesignDictionary;
  if (!dict) return false;
  const numericId = parseInt(raw, 10);
  if (Number.isNaN(numericId)) return false;
  const di = dict[numericId];
  const componentName = di?.item?.name;
  return typeof componentName === "string" && VALUE_HOLDER_COMPONENT_TYPES.has(componentName);
}

/**
 * Fix #17 — Auto-default `sourcePropertyName` for `componentValue` bindings.
 *
 * Background
 * ----------
 * `ui_action.set_multiple_parameter_bindings` with
 * `bindingType: "componentValue"` requires both `sourceKuikaId` AND
 * `sourcePropertyName` — the slot on the source component to read
 * (`value` for most inputs, `checked` for Checkbox / Switch /
 * RadioButton). The Binder LLM consistently forgets the latter and the
 * symbol builder rejects every binding with
 * `"sourcePropertyName is required for 'componentValue' bindingType"`.
 *
 * Resolution
 * ----------
 * When the field is missing, infer it from the source component's type:
 * 99% of value-holders use `"value"`. The handful that don't (boolean
 * toggles) are listed in `CHECKED_PROPERTY_COMPONENT_TYPES`. When the
 * source can't be inspected (missing dict, non-numeric id), fall back
 * to `"value"` — that's the right answer for the long tail.
 *
 * Mutates the binding in place.
 */
const CHECKED_PROPERTY_COMPONENT_TYPES: ReadonlySet<string> = new Set(["Checkbox", "Switch", "RadioButton"]);

export function applyComponentValueSourcePropertyDefault(binding: Record<string, any>, sd: any): void {
  if (binding == null || typeof binding !== "object") return;
  if (binding.bindingType !== "componentValue") return;
  if (typeof binding.sourcePropertyName === "string" && binding.sourcePropertyName.length > 0) return;

  let resolved: string = "value";
  const sourceKuikaId = binding.sourceKuikaId;
  if (sourceKuikaId != null) {
    try {
      const dict = sd?.screenProvider?.state?.screenDesignDictionary;
      if (dict) {
        const numericId = parseInt(String(sourceKuikaId), 10);
        if (!Number.isNaN(numericId)) {
          const componentName = dict[numericId]?.item?.name;
          if (typeof componentName === "string" && CHECKED_PROPERTY_COMPONENT_TYPES.has(componentName)) {
            resolved = "checked";
          }
        }
      }
    } catch {
      // best-effort — sourcePropertyName default falls back to "value"
    }
  }
  binding.sourcePropertyName = resolved;
}

/**
 * Fix #12 — Correct the host/source confusion that the Wirer + Binder
 * LLMs consistently produce in `set_multiple_parameter_bindings` calls.
 *
 * Two distinct LLM mistakes are recovered:
 *
 *   (a) Wrong-host: `kuikaId` is set to the SOURCE component
 *       (e.g. `idea-title` TextInput) rather than the HOST component
 *       (e.g. `idea-save` Button where the action lives). The handler
 *       then complains "Event 'onClick' not found on component
 *       '<input-id>'" because inputs don't fire onClick. Recovery:
 *       find the action's real host via `findActionHostByInstanceId`
 *       and use those values for `kuikaId` / `eventName`. If the
 *       binding's `bindingType` is `componentValue` AND `sourceKuikaId`
 *       is empty AND the displaced kuikaId pointed at a value-holder
 *       component, also move the original kuikaId to `sourceKuikaId`
 *       — the LLM almost certainly meant the source field.
 *
 *   (b) Source-as-defaultName: LLM emits
 *       `bindingType: "componentValue", defaultName: "idea-title"`
 *       (defaultName is for `bindingType: "default"`). Recovery: read
 *       `defaultName`, resolve as a friendlyName via Fix #8, and move
 *       to `sourceKuikaId` if a unique component matches.
 *
 * Mutates the input record in place. Runs AFTER Fix #8 (friendlyName
 * resolve) and Fix #9 (actionDefinitionId → actionInstanceId) so the
 * actionId field is already a real instance id when we look up the host.
 */
export function correctParameterBindingHostScope(binding: Record<string, any>, sd: any): void {
  if (binding == null || typeof binding !== "object") return;

  // Recovery (b) — defaultName misused as a source identifier.
  if (
    binding.bindingType === "componentValue" &&
    (binding.sourceKuikaId == null || binding.sourceKuikaId === "") &&
    typeof binding.defaultName === "string" &&
    binding.defaultName.length > 0
  ) {
    const resolved = resolveFriendlyKuikaId(binding.defaultName, sd);
    if (resolved !== undefined && resolved !== binding.defaultName && /^-?\d+$/.test(String(resolved))) {
      // Successful friendlyName → numeric kuikaId resolution. Move it
      // into sourceKuikaId and drop defaultName so the symbol-builder
      // doesn't see a contradictory shape.
      binding.sourceKuikaId = resolved;
      delete binding.defaultName;
    }
  }

  // Recovery (a) — wrong host. Requires a known actionId to look up.
  const actionId = typeof binding.actionId === "string" ? binding.actionId : undefined;
  if (!actionId || !GUID_PATTERN.test(actionId)) return;
  const host = findActionHostByInstanceId(actionId, sd);
  if (!host) return;

  const passedKuikaId = binding.kuikaId != null ? String(binding.kuikaId) : "";
  const passedEvent = typeof binding.eventName === "string" ? binding.eventName : "";

  const hostMatches = passedKuikaId === host.kuikaId && (passedEvent === "" || passedEvent === host.eventName);
  if (hostMatches) return;

  // Host differs from what the LLM passed. Three cases:
  //   - The LLM meant the source component, left sourceKuikaId empty
  //     (Fix the source slot, then fix the host).
  //   - The LLM passed the SAME id in both slots (kuikaId === sourceKuikaId)
  //     because it confused "host" and "source" entirely. The May-2026
  //     fikir-havuzu rerun at 11:35 hit this — the Binder used
  //     `kuikaId: "idea-title"` AND `sourceKuikaId: "idea-title"` for
  //     a Title binding whose actual host is `btn-save`. The original
  //     recovery only triggered on empty sourceKuikaId, so this shape
  //     slipped through and every form-field binding failed.
  //   - The LLM just got confused (fix the host only).
  if (
    binding.bindingType === "componentValue" &&
    (binding.sourceKuikaId == null || binding.sourceKuikaId === "") &&
    passedKuikaId.length > 0 &&
    /^-?\d+$/.test(passedKuikaId) &&
    isValueHolderComponent(passedKuikaId, sd)
  ) {
    binding.sourceKuikaId = passedKuikaId;
  } else if (
    // Fix #58 — same-id-in-both-slots case. When kuikaId and sourceKuikaId
    // are both the source component (string-equal after friendlyName→numeric
    // resolution, or already numeric-equal) AND that component is a value
    // holder, keep sourceKuikaId as-is and swap kuikaId to the actual host.
    binding.bindingType === "componentValue" &&
    binding.sourceKuikaId != null &&
    binding.sourceKuikaId !== ""
  ) {
    const passedSource: string = String(binding.sourceKuikaId);
    const resolvedKuikaId: string = /^-?\d+$/.test(passedKuikaId)
      ? passedKuikaId
      : String(resolveFriendlyKuikaId(passedKuikaId, sd) ?? passedKuikaId);
    const resolvedSource: string = /^-?\d+$/.test(passedSource)
      ? passedSource
      : String(resolveFriendlyKuikaId(passedSource, sd) ?? passedSource);
    if (
      resolvedKuikaId === resolvedSource &&
      /^-?\d+$/.test(resolvedKuikaId) &&
      isValueHolderComponent(resolvedKuikaId, sd)
    ) {
      // Both slots resolve to the same value-holder component, but the
      // action's true host is elsewhere. Keep the source, swap the host.
      binding.sourceKuikaId = resolvedKuikaId;
      binding.kuikaId = host.kuikaId;
      binding.eventName = host.eventName;
      return;
    }
  }
  binding.kuikaId = host.kuikaId;
  binding.eventName = host.eventName;
}

/**
 * NAVIGATE-specific parameter alias normalisation. The Binder/Wirer
 * routinely calls add_to_event with `parameters: [{name: "ScreenName",
 * value: "..."}]` even though NAVIGATE's real parameter is `toScreenId`.
 * The current handler drops the unknown name silently and the action
 * lands with toScreenId="" — at runtime the button does nothing.
 *
 * Two normalisations:
 *   1. Parameter NAME: ScreenName / screenName / toScreen / targetScreen
 *      → toScreenId.
 *   2. Parameter VALUE: when value is a screen name (not a Guid) and we
 *      have a matching screen in screenList, rewrite to that screen's
 *      id.
 */
const NAVIGATE_PARAM_ALIASES: ReadonlyMap<string, string> = new Map([
  ["screenname", "toScreenId"],
  ["screen_name", "toScreenId"],
  ["screen", "toScreenId"],
  ["toscreen", "toScreenId"],
  ["targetscreen", "toScreenId"],
  ["targetscreenname", "toScreenId"],
  ["target", "toScreenId"],
  ["navigateto", "toScreenId"],
  ["to", "toScreenId"]
]);

function normalizeNavigateAlias(rawName: string): string {
  const key = rawName.toLowerCase().replace(/[\s_-]/g, "");
  return NAVIGATE_PARAM_ALIASES.get(key) ?? rawName;
}

/**
 * KOA-17797 — custom-panel actions (every `*-symbols-group` editor: SetValueOf,
 * ChangeVisibilityOf, ToggleVisibilityOf, ScrollTo, SetFocusTo, GetFromBag,
 * SetEntityAttributeValue, …) hide their TARGET component under a verb-specific
 * parameter name the LLM rarely guesses right (`targetDiName` / `componentName`
 * / `element` / …). This is the SAME silent-drop trap as NAVIGATE (Fix #10):
 * the LLM emits e.g. `{name:"targetComponentKuikaId", value:"<kuikaId>"}`, the
 * name matches no real param, the override loop `continue`s, and the action
 * lands with an EMPTY target (observed: a SetValueOf onClick wired with no
 * target → its value never set; telemetry session af3771db).
 *
 * DATA-DRIVEN, not a hardcoded per-verb list: read the action's OWN parameter
 * names, find the one that denotes a UI-component target, and rewrite any
 * LLM target-alias override onto it. Only the parameter NAME is rewritten; the
 * value (a kuikaId) is untouched. Overrides whose name already matches a real
 * param (the target itself, `value`, `visibilityType`, …) are left alone.
 */
const CUSTOM_ACTION_TARGET_ALIASES: ReadonlySet<string> = new Set<string>([
  "targetcomponentkuikaid",
  "targetcomponent",
  "targetcomponentname",
  "targetcomponentid",
  "componentkuikaid",
  "targetkuikaid",
  "targetdiname",
  "targetdiid",
  "targetdi",
  "targetid",
  "target",
  "targetelement",
  "kuikaid",
  "componentid",
  "componentname",
  "component",
  "element",
  "diid",
  "di"
]);

/**
 * Real param names that denote a UI-component target across the custom panels —
 * the target lives under `*DiName` (SetValueOf), `componentName` (visibility /
 * enable verbs), `element` (scroll / focus), `*DiId` (TriggerEvent) or `diId`
 * (the dynamic-form verbs). Matches the component-targeting class data-drivenly.
 */
const TARGET_PARAM_NAME_PATTERN: RegExp = /^element$|(?:diname|diid|componentname|componentid)$/;

function normParamKey(raw: string): string {
  return raw.toLowerCase().replace(/[\s_-]/g, "");
}

export function normalizeCustomActionTargetParam(realParams: unknown, paramOverrides: unknown): void {
  if (!Array.isArray(realParams) || !Array.isArray(paramOverrides)) return;
  const realKeys: { raw: string; key: string }[] = [];
  for (const p of realParams) {
    const n: unknown = p?.name ?? p?.Name;
    if (typeof n === "string" && n.length > 0) realKeys.push({ raw: n, key: normParamKey(n) });
  }
  const target: { raw: string; key: string } | undefined = realKeys.find((p) => TARGET_PARAM_NAME_PATTERN.test(p.key));
  if (target === undefined) return;
  const realKeySet: Set<string> = new Set<string>(realKeys.map((p) => p.key));
  for (const override of paramOverrides) {
    if (!override || typeof override !== "object") continue;
    const nameField: unknown = typeof override.name === "string" ? override.name : override.parameterName;
    if (typeof nameField !== "string") continue;
    const key: string = normParamKey(nameField);
    if (realKeySet.has(key)) continue; // already a real param (target itself, value, visibilityType, …)
    if (!CUSTOM_ACTION_TARGET_ALIASES.has(key)) continue; // not a component-target alias — leave it
    if (typeof override.name === "string") override.name = target.raw;
    if (typeof override.parameterName === "string") override.parameterName = target.raw;
  }
}

function resolveScreenNameToId(value: unknown, sd: any): string | undefined {
  if (typeof value !== "string") return undefined;
  const raw = value.trim();
  if (raw.length === 0) return undefined;
  if (GUID_PATTERN.test(raw)) return undefined;
  const screenList = sd?.screenProvider?.state?.screenList;
  if (!Array.isArray(screenList)) return undefined;
  const findByName = (name: string): string | undefined => {
    const targetKey = normalizeFriendlyMatchKey(name);
    const match = screenList.find((s: any) => {
      const candidate = typeof s?.name === "string" ? s.name : "";
      return normalizeFriendlyMatchKey(candidate) === targetKey;
    });
    return match?.id ?? undefined;
  };

  // Primary lookup — exact (normalized) match on the full value.
  const direct = findByName(raw);
  if (direct !== undefined) return direct;

  // Fix #14 — logical-event-suffix strip. The App-Planner emits
  // navigation hand-off names of the shape `<ScreenName>.<event-name>`
  // (e.g. `NewIdea.save-success`, `IdeaList.cancel`) for cross-screen
  // routing semantics. BRAID sometimes carries those verbatim into a
  // `toScreenId` slot, where the value must be a real screen name /
  // id. Without this recovery, NAVIGATE lands with a bogus toScreenId
  // and the runtime button does nothing.
  //
  // Strip after the FIRST dot — the convention is "screen.event"
  // (single dot). If that stem matches a real screen, use it.
  const dotIdx = raw.indexOf(".");
  if (dotIdx > 0) {
    const stem = raw.slice(0, dotIdx).trim();
    if (stem.length > 0) {
      const stemMatch = findByName(stem);
      if (stemMatch !== undefined) return stemMatch;
    }
  }
  return undefined;
}

/**
 * Fix #26 — Detect NAVIGATE actions whose `toScreenId` (after alias /
 * screen-name normalisation) points at the CURRENT screen.
 *
 * In a real reproducer (May 2026 fikir-havuzu build), BRAID emitted
 * `Trigger idea-save.onClick triggers Navigate NewIdea.save-success`.
 * App-Planner's outbound nav table for that anchor said "go to
 * IdeaList", but the BRAID parser kept the literal `NewIdea.save-success`,
 * which the screen-name suffix-strip then resolved to `NewIdea` — the
 * very screen the button lives on. The runtime then renders a save
 * button that navigates from NewIdea back to NewIdea (no-op loop).
 *
 * This guard runs AFTER `normalizeNavigateParameterOverrides`, so
 * `toScreenId` is already the resolved screen id (guid). When it
 * equals the active screen's id, return a teaching message so the
 * caller can pick a real outbound target.
 *
 * `resolveAndCreateAction` REFUSES the attach by default — that is the guard the
 * chat pipeline relies on. A caller that really wants a self-navigate (a refresh,
 * or a navigation menu's entry for the open screen) passes `allowSameScreen: true`;
 * the action is then attached and this text comes back as `navigateSameScreenWarning`.
 */
export function checkNavigateSameScreen(actionDef: any, paramOverrides: unknown, sd: any): string | null {
  const isNavigate = actionDef?.name === "NAVIGATE" || actionDef?.displayName === "NAVIGATE";
  if (!isNavigate) return null;
  if (!Array.isArray(paramOverrides)) return null;

  let toScreenIdValue: unknown;
  for (const override of paramOverrides) {
    if (!override || typeof override !== "object") continue;
    const name = typeof override.name === "string" ? override.name : override.parameterName;
    if (name === "toScreenId") {
      toScreenIdValue = override.value;
      break;
    }
  }
  if (typeof toScreenIdValue !== "string" || toScreenIdValue.length === 0) return null;

  const currentScreenId: string =
    typeof sd?.screenProvider?.getScreenDesign === "function"
      ? (sd.screenProvider.getScreenDesign()?.id ?? "")
      : (sd?.screenProvider?.state?.sdId ?? "");
  if (currentScreenId.length === 0) return null;
  if (toScreenIdValue !== currentScreenId) return null;

  const currentScreenName: string =
    typeof sd?.screenProvider?.getScreenDesign === "function"
      ? (sd.screenProvider.getScreenDesign()?.name ?? "(current)")
      : (sd?.screenProvider?.state?.screenName ?? "(current)");
  return (
    `[VALIDATION] NAVIGATE.toScreenId resolves to the CURRENT screen "${currentScreenName}" — ` +
    `that's a button that navigates a screen to itself, a no-op at runtime. ` +
    `The intent likely encoded an anchor (e.g. "${currentScreenName}.save-success") that maps to a ` +
    `DIFFERENT target screen: pick that outbound target, or, if the user meant "stay here after save", ` +
    `attach no NAVIGATE and rely on the mutation alone. ` +
    `If a self-navigate is genuinely intended (a refresh, or the navigation-menu entry of this screen), ` +
    `repeat the call with allowSameScreen: true.`
  );
}

/**
 * `screenInputs` entries the caller asked for that the TARGET screen does not
 * declare. `applyTargetScreenInputParams` skips them with a bare `continue`, so
 * without this the result payload looked identical whether the row id reached
 * the detail screen or was thrown away. Returns them tagged so they read
 * distinctly from dropped action parameters in the same list.
 */
function unmatchedScreenInputNames(screenInputOverrides: unknown, action: any): string[] {
  if (!Array.isArray(screenInputOverrides) || screenInputOverrides.length === 0) return [];
  const landed = new Set<string>(
    (action?.targetScreenInputParams ?? [])
      .map((p: any) => (typeof p?.name === "string" ? p.name.toLowerCase() : ""))
      .filter((n: string) => n.length > 0)
  );
  const unmatched: string[] = [];
  for (const override of screenInputOverrides) {
    const name = override?.name ?? override?.parameterName;
    if (typeof name !== "string" || name.length === 0) continue;
    if (landed.has(name.toLowerCase())) continue;
    unmatched.push(`screenInputs.${name}`);
  }
  return unmatched;
}

/** Comma-joined screen names for a validation message (capped, never throws). */
function availableScreenNamesForMessage(sd: any): string {
  const screenList = sd?.screenProvider?.state?.screenList;
  if (!Array.isArray(screenList) || screenList.length === 0) return "(no screens loaded)";
  return screenList
    .map((s: any) => (typeof s?.name === "string" ? s.name : ""))
    .filter((n: string) => n.length > 0)
    .slice(0, 25)
    .join(", ");
}

/**
 * KOA-17797 (AC-02) — Reject a NAVIGATE that cannot resolve a target screen,
 * instead of silently landing a dead button and reporting success. Two cases,
 * one remedy:
 *
 *   - `toScreenId` is a NON-GUID value no screen resolves to (the `scr_11`
 *     hallucination). Runs AFTER `normalizeNavigateParameterOverrides`, which
 *     already rewrites a real screen NAME to its id, so a surviving non-guid
 *     value is genuinely unresolvable — a fabricated/stale target.
 *   - `toScreenId` is ABSENT or empty. This used to pass: the handler created
 *     the NAVIGATE with `openAs` only and answered `success: true`, so a table
 *     row button rendered and did nothing while the backend reported
 *     `KV1170 (Parameter: 'toScreenId')` far too late for anything to act on.
 *     Callers that genuinely do not know the destination must not attach the
 *     action at all — an unwired button is recoverable, a dead one is not.
 *
 * Scope is deliberately narrow on the first case: it NEVER rejects a
 * GUID-shaped value. Real screen ids are GUIDs; a just-created screen may not
 * be in the (possibly stale) `screenList` yet, and false-rejecting a real
 * navigation is worse than letting a rare hallucinated-GUID through (the
 * same-screen guard and codegen still catch other cases).
 */
export function checkNavigateUnknownScreen(actionDef: any, paramOverrides: unknown, sd: any): string | null {
  const isNavigate = actionDef?.name === "NAVIGATE" || actionDef?.displayName === "NAVIGATE";
  if (!isNavigate) return null;

  let toScreenIdValue: unknown;
  if (Array.isArray(paramOverrides)) {
    for (const override of paramOverrides) {
      if (!override || typeof override !== "object") continue;
      const name = typeof override.name === "string" ? override.name : override.parameterName;
      if (name === "toScreenId") {
        toScreenIdValue = override.value;
        break;
      }
    }
  }

  if (typeof toScreenIdValue !== "string" || toScreenIdValue.trim().length === 0) {
    return (
      `[VALIDATION] NAVIGATE requires a toScreenId — this call passed none, which would create a button ` +
      `that renders but does nothing (backend KV1170). Available screens: ${availableScreenNamesForMessage(sd)}. ` +
      `Pass parameters: [{ name: "toScreenId", value: "<screen id or EXACT screen name>" }]. ` +
      `If the destination is genuinely unknown, do NOT attach NAVIGATE at all.`
    );
  }
  const raw = toScreenIdValue.trim();
  // A GUID is assumed to be a real screen id (name→id resolution already ran;
  // don't false-reject on a stale screenList). Only a non-guid survivor is a
  // fabricated target.
  if (GUID_PATTERN.test(raw)) return null;

  return (
    `[VALIDATION] NAVIGATE.toScreenId "${raw}" is not a real screen in this project. ` +
    `Available screens: ${availableScreenNamesForMessage(sd)}. Pass an existing screen's id (a GUID) or its EXACT name — ` +
    `do not invent a screen id.`
  );
}

/**
 * Apply NAVIGATE parameter normalisation when the action being created
 * is the NAVIGATE built-in. Rewrites parameter names from common aliases
 * to `toScreenId` and resolves screen-name values to screen ids. Safe
 * for non-NAVIGATE actions — no-op.
 */
export function normalizeNavigateParameterOverrides(actionDef: any, paramOverrides: unknown, sd: any): void {
  const isNavigate = actionDef?.name === "NAVIGATE" || actionDef?.displayName === "NAVIGATE";
  if (!isNavigate) return;
  if (!Array.isArray(paramOverrides)) return;
  for (const override of paramOverrides) {
    if (!override || typeof override !== "object") continue;
    const nameField = typeof override.name === "string" ? override.name : override.parameterName;
    if (typeof nameField === "string") {
      const normalized = normalizeNavigateAlias(nameField);
      if (normalized !== nameField) {
        if (typeof override.name === "string") override.name = normalized;
        if (typeof override.parameterName === "string") override.parameterName = normalized;
      }
    }
    // Only rewrite VALUE when this is the toScreenId slot.
    const effectiveName = typeof override.name === "string" ? override.name : override.parameterName;
    if (effectiveName === "toScreenId") {
      const screenId = resolveScreenNameToId(override.value, sd);
      if (screenId !== undefined) {
        override.value = screenId;
      }
    }
  }
}

/**
 * KOA-18193 — one row of `list_screen_actions`, carrying enough structure to
 * answer "does this action run BEFORE that one".
 *
 * `orderIndex` is the position within the action's OWN list — the event's
 * `actions`, or the enclosing IfThenElse's `ifActions` / `elseActions`. It is
 * deliberately NOT the position in this flat result array: the walk is a
 * pre-order DFS, so a branch child is emitted before its parent's next sibling
 * and would take a lower flat index than an action that genuinely runs after it.
 * A flat counter here would read as an execution order and be wrong.
 *
 * `parentActionId` + `branch` say which list that index belongs to, so a reader
 * can only compare indexes that are actually comparable. `hasCondition` and
 * `enabled` are the two remaining reasons a top-level action may not run at all.
 */
interface ScreenActionRow extends IActionBindingSummary {
  actionId: string;
  label: string;
  actionDefinitionId: string | null;
  eventName: string;
  componentKuikaId: string | undefined;
  componentFriendlyName: string | null;
  /** Enclosing IfThenElse action, or null at the event's top level. */
  parentActionId: string | null;
  /** Which branch of `parentActionId` holds this action. */
  branch: "if" | "else" | null;
  /** Position within this action's own sibling list. */
  orderIndex: number;
  /** The action carries its own guard, so reaching it is not certain. */
  hasCondition: boolean;
  /** Explicitly disabled actions do not run. Absent in the model means enabled. */
  enabled: boolean;
}

function toScreenActionRow(
  action: any,
  di: any,
  eventName: string,
  parentActionId: string | null,
  branch: "if" | "else" | null,
  orderIndex: number,
  screenIndex: ScreenIndex | null
): ScreenActionRow {
  return {
    actionId: action.id,
    label: action.label || action.description || "Unnamed",
    actionDefinitionId: action.actionDefinitionId || null,
    eventName,
    componentKuikaId: di.id?.toString(),
    componentFriendlyName: di.friendlyName || null,
    parentActionId,
    branch,
    orderIndex,
    hasCondition: action.condition !== null && action.condition !== undefined,
    enabled: action.enabled !== false,
    // Read-back of what the action is wired to (parameters, navigate target,
    // screen-input bindings) — compact, and absent when there is nothing to show.
    ...summarizeActionBindings(action, screenIndex)
  };
}

/**
 * The raw action instance behind a `ScreenActionRow`, found by id across every
 * event chain (if/else branches included). `get_details` returns it so an action
 * outside the current scope can still be read back in full.
 */
function findActionInstanceInDisplayItems(displayItems: any[], actionId: string): unknown {
  const findInActions = (actions: any[] | undefined): unknown => {
    for (const action of actions ?? []) {
      if (action?.id === actionId) return action;
      const nested: unknown =
        findInActions(action?.ifThenElseData?.ifActions) ?? findInActions(action?.ifThenElseData?.elseActions);
      if (nested !== undefined) return nested;
    }
    return undefined;
  };
  for (const di of displayItems ?? []) {
    for (const evt of di?.item?.events ?? []) {
      const hit: unknown = findInActions(evt.actions);
      if (hit !== undefined) return hit;
    }
    const inChildren: unknown = findActionInstanceInDisplayItems(di?.children ?? [], actionId);
    if (inChildren !== undefined) return inChildren;
  }
  return undefined;
}

/**
 * Loop / subscription containers are NOT descended into, so their inner actions
 * never appear in this list. That is deliberate and conservative: a value
 * produced inside a `forEach` iteration or an MQTT subscription is not a
 * page-level result, and emitting it would invite a reader to treat it as one.
 * The container action itself is listed, as the page-level step it is.
 */
function collectActionsFromDisplayItems(
  displayItems: any[],
  screenIndex: ScreenIndex | null = null
): ScreenActionRow[] {
  const results: ScreenActionRow[] = [];

  function walkDi(di: any) {
    const events = di.item?.events || [];
    for (const evt of events) {
      const actions = evt.actions || [];
      for (let i = 0; i < actions.length; i++) {
        const action = actions[i];
        results.push(toScreenActionRow(action, di, evt.eventName, null, null, i, screenIndex));
        // Recurse into if/then/else chains
        if (action.ifThenElseData) {
          collectIfThenElse(action.ifThenElseData, action.id, di, evt.eventName, results);
        }
      }
    }
    if (di.children) {
      for (const child of di.children) {
        walkDi(child);
      }
    }
  }

  function collectIfThenElse(data: any, parentActionId: string, di: any, eventName: string, out: ScreenActionRow[]) {
    const branches: ["if" | "else", any[]][] = [
      ["if", data.ifActions || []],
      ["else", data.elseActions || []]
    ];
    for (const [branch, actions] of branches) {
      for (let i = 0; i < actions.length; i++) {
        const action = actions[i];
        out.push(toScreenActionRow(action, di, eventName, parentActionId, branch, i, screenIndex));
        if (action.ifThenElseData) {
          collectIfThenElse(action.ifThenElseData, action.id, di, eventName, out);
        }
      }
    }
  }

  for (const di of displayItems) {
    walkDi(di);
  }

  return results;
}

function getActionDefinitions(sd: ScreenDesignInstance): IActionDefinitionDto[];
function getActionDefinitions(sd: IScreenDesignContextState): IActionDefinitionDto[];
function getActionDefinitions(sd: ScreenDesignInstance | IScreenDesignContextState): IActionDefinitionDto[] {
  // ScreenDesignInstance (provider) — has sub-providers, can filter
  // KOA-18179 — both branches are LLM-facing, so both drop the withheld
  // platform-infrastructure families (audit / category / document management).
  // See `platform-action-visibility.ts`; the human designer's picker is
  // unaffected because it reads `filterActionDefinitions` directly.
  if ("projectProvider" in sd) {
    const all: IActionDefinitionDto[] =
      sd.projectProvider.state.actionDefinitions ?? sd.projectProvider.getProviderValues().actionDefinitions ?? [];
    return hidePlatformActionsFromLlm(filterActionDefinitions(all, sd));
  }
  // IScreenDesignContextState (flat state) — already filtered at entry point
  return hidePlatformActionsFromLlm(sd.actionDefinitions ?? []);
}

/**
 * Get ALL action definitions without visibility filtering.
 * Used by resolveAndCreateAction to find action definitions by ID/name —
 * user-created SQL actions have actionCategory "Other" which is filtered out
 * by the UI-facing getActionDefinitions, but must still be resolvable by ID.
 */
export function getAllActionDefinitions(sd: ScreenDesignInstance | IScreenDesignContextState): IActionDefinitionDto[] {
  if ("projectProvider" in sd) {
    return sd.projectProvider.state.actionDefinitions ?? sd.projectProvider.getProviderValues().actionDefinitions ?? [];
  }
  return sd.actionDefinitions ?? [];
}

/** The parts of an action definition an attach needs to build its instance. */
interface IAttachableActionDefinition {
  id?: string;
  name?: string;
  displayName?: string;
  actionParameters?: unknown[];
  returnTypeName?: string;
  returnCardinality?: number;
}

/** The action instance an attach writes onto an event. */
interface IAttachedActionInstance {
  id: string;
  actionDefinitionId: string | undefined;
  label: string | undefined;
  description: string | undefined;
  parameters: unknown[];
  targetScreenInputParams?: unknown[];
  returnType: string | undefined;
  returnCardinality: number | undefined;
  dontTriggerValidations: boolean;
  appSupportType: number;
}

/**
 * KOA-18448 — is this definition a SCREEN navigation, i.e. one that opens a screen the designer
 * picked at design time? `NavigateToTask` is deliberately NOT one: it opens whatever screen the
 * task at hand belongs to, resolved at runtime, so it carries no target screen.
 */
export function isScreenNavigationDefinition(actionDef: IAttachableActionDefinition): boolean {
  return actionDef?.name === "NAVIGATE" || actionDef?.displayName === "NAVIGATE";
}

/**
 * KOA-18448 — `targetScreenInputParams` belongs to a screen navigation and to nothing else.
 *
 * It used to be stamped onto EVERY action this tool created. The code generator decides "treat
 * this action like a screen navigation" from that field's PRESENCE, not its contents, so an empty
 * list was enough to send a `NavigateToTask` down the navigation path — where its (necessarily
 * empty) target screen was parsed as a screen id, threw, and, because generators run in parallel,
 * took the whole project's code generation down with it. The manual panel never hit this: it
 * writes the field only when the user actually PICKS a target screen, which is why the same action
 * wired by hand generates fine and the same action wired by the AI did not.
 */
export function buildAttachedActionInstance(
  actionId: string,
  actionDef: IAttachableActionDefinition
): IAttachedActionInstance {
  const instance: IAttachedActionInstance = {
    id: actionId,
    actionDefinitionId: actionDef?.id,
    label: actionDef?.name,
    description: actionDef?.name,
    parameters: (actionDef?.actionParameters || []).map((p: unknown) => cloneActionParameter(p)),
    returnType: actionDef?.returnTypeName,
    returnCardinality: actionDef?.returnCardinality,
    dontTriggerValidations: false,
    appSupportType: 0
  };
  if (isScreenNavigationDefinition(actionDef)) {
    instance.targetScreenInputParams = [];
  }
  return instance;
}

function cloneActionParameter(param: any): any {
  const cloned = JSON.parse(JSON.stringify(param));
  if (cloned.name === "executePageInitActionsAfterClose" && !cloned.symbol) {
    cloned.symbol = {
      category: ESymbolCategory.fixed,
      isFixed: true,
      label: "Fixed Value",
      fixedValue: true,
      description: ""
    };
  }
  return cloned;
}

const UNARY_OPERATORS = ["isEmpty", "isNotEmpty", "isGuid"];
const PARAMETER_SENTINELS = new Set(["__KEEP_EMPTY__", "__CUSTOM__"]);

/**
 * Reject incompatible binding-type/source-property combinations the Wirer
 * sometimes invents. Returns a teaching-error string when the shape is
 * fundamentally wrong, `null` otherwise.
 *
 * The load-bearing case is the one this guard is named after: a row-scope
 * expression like `current.Id` paired with `bindingType: "componentValue"`.
 * `current.X` only resolves inside a repeater's row template; the
 * `componentValue` shape reads a real property (`value`, `text`,
 * `selectedDate`) off a specific kuikaId. Mixing them silently lands a
 * binding that points at the button's literal `current.Id` property —
 * which doesn't exist — and at runtime the button onClick fires with
 * `Id: null`, so VucutOlcumu_Delete deletes nothing or throws.
 *
 * Live regression — the entire 35-step retry cascade in the
 * "Cannot add components into Label" log started here:
 *
 *   1. Wirer bound RowAction Sil → VucutOlcumu_Delete.Id with
 *      `bindingType: "componentValue", sourceKuikaId: 577154 (the
 *      button itself), sourcePropertyName: "current.Id"`. Handler
 *      let it through with a soft "non-data source" warning.
 *   2. Wirer noticed its own warning, panicked, bounceBack to Designer.
 *   3. Designer "rework" hallucinated kuikaIds (812015, 837799, 314655)
 *      and misidentified Label/VerticalStack as Panel/Table, generating
 *      30+ "Cannot add components into Label" runtime errors.
 *   4. Final validator verdict: `aligned: false, blocker`.
 *
 * Rejecting the bad shape at step 1 short-circuits the entire cascade
 * and forces the Wirer to retry with the correct binding type for
 * RowAction params: `bindingType: "default", defaultName: "current.Id"`.
 */
function validateRowScopeBindingShape(binding: any): string | null {
  if (!binding || typeof binding !== "object") return null;
  const bindingType = typeof binding.bindingType === "string" ? binding.bindingType : "";
  const sourcePropertyName = typeof binding.sourcePropertyName === "string" ? binding.sourcePropertyName : "";

  // Case 1: componentValue + current.X — fundamentally wrong combo.
  // `current.X` is row-template scope; componentValue reads a literal
  // property off a kuikaId. The right shape is bindingType: "default"
  // with defaultName: "current.X" (the platform's row context resolver).
  if (bindingType === "componentValue" && sourcePropertyName.startsWith("current.")) {
    const fieldName = sourcePropertyName.slice("current.".length);
    return (
      `Invalid binding combination: bindingType="componentValue" with sourcePropertyName="${sourcePropertyName}". ` +
      `"current.${fieldName}" is row-template scope syntax — it only resolves inside a repeater (Table / GalleryView / ` +
      `KanbanBoard / Calendar) row, and componentValue reads a literal property (value, text, selectedDate) off a ` +
      `specific kuikaId.\n\n` +
      `For a RowAction parameter (e.g. a Delete button inside <TableTemplateColumn>), use:\n` +
      `  { "bindingType": "default", "defaultName": "current.${fieldName}" }\n\n` +
      `For a binding that reads from a different datasource action's row, use:\n` +
      `  { "bindingType": "actionField", "actionId": "<source-action-id>", "fieldName": "${fieldName}" }\n\n` +
      `Do NOT pass current.* as componentValue.sourcePropertyName — the binding will silently land with no resolvable ` +
      `source and the action will fire with the parameter unset.`
    );
  }

  // Case 2: a `current.X` row-scope source on a host that is NOT inside a data
  // repeater. `current.*` resolves against the enclosing repeater's row; with no
  // repeater there is no row, and codegen proves it — `ClientCodeGenerationBase`'s
  // `Current` case returns null when `FindParentHavingDataBindingOptions()` finds
  // nothing, so the parameter ships EMPTY. The symbol picker already refuses to
  // OFFER "Current Row" outside a repeater (`generateCurrentRow` emits no children
  // when no ancestor `isDataBindingOptions`), so accepting it here let the
  // programmatic path create a binding a human could not: a form screen's
  // screen-level button was handed `current.Id` and shipped dead.
  const rowScopeSource: string =
    sourcePropertyName.startsWith("current.") || sourcePropertyName.startsWith("current:")
      ? sourcePropertyName
      : ([binding.defaultName, binding.value].find(
          (candidate: unknown): boolean => typeof candidate === "string" && /^current[.:]/.test(candidate)
        ) ?? "");
  if (rowScopeSource.length > 0 && !hostHasRowContext(getSdInstance(), binding.kuikaId)) {
    return (
      `[Row-context guard] "${rowScopeSource}" is row-template scope, but component ${binding.kuikaId} is not inside a ` +
      `data repeater (Table / GalleryView / FlexGrid / KanbanBoard / Calendar) — there is no row for it to read, so the ` +
      `parameter would ship empty (backend KV1170). The symbol picker does not offer "Current Row" here either.\n\n` +
      `Use instead:\n` +
      `  • a value the user typed on THIS screen → { "bindingType": "componentValue", "sourceKuikaId": "<field kuikaId>", "sourcePropertyName": "value" }\n` +
      `  • a value this screen RECEIVED from its caller → { "bindingType": "screenInput", "inputName": "<declared input>" }\n` +
      `  • another action's output → { "bindingType": "actionField", "actionId": "<source action>", "fieldName": "<field>" }\n` +
      `If this really is a row action, attach it to a button inside the repeater's row template instead.`
    );
  }

  return null;
}

// Built-in actions where multiple instances on the same event are normal —
// the dispatcher's "actionDefinitionId already exists, skip duplicate"
// dedup MUST be bypassed for these so e.g. a Clear button can stack three
// SetValueOf instances (one per input). The KOA-17026 trace shipped with
// btn-clear half-wired because the second and third SetValueOf adds were
// silently skipped, the parameter binding then overwrote the same single
// instance, and only the last input got cleared at runtime.
//
// Names below come from the BRAID built-in verb set in
// pipeline-coordinator BRAID_BUILT_IN_ACTIONS. Match is case-insensitive.
// Singletons (NAVIGATE, GoBack, IfThenElse, Search, SendMail, OpenInBrowser,
// OpenPdf, PrintReport) and user-defined SQL actions stay subject to dedup —
// stacking two NAVIGATE / two _Insert actions on one click is almost
// always a nudge-induced duplicate, not legitimate intent.
const MULTI_INSTANCE_FRIENDLY_ACTION_NAMES = new Set<string>([
  "setvalueof",
  "notify",
  "changevisibilityof",
  "changeenabledof",
  "togglevisibilityof",
  "toggleeditabilityof",
  "opencollapsepanelof",
  "closecollapsepanelof",
  "togglecollapsepanelof",
  "scrollto",
  "setfocusto",
  "triggerevent"
]);

function isMultiInstanceFriendlyAction(
  actionDefIdOrName: string | undefined,
  resolvedDef: ResolvedActionDefMeta | undefined
): boolean {
  const candidates = [actionDefIdOrName, resolvedDef?.name, resolvedDef?.displayName]
    .filter((s): s is string => typeof s === "string" && s.length > 0)
    .map((s) => s.toLowerCase().replace(/\s+/g, ""));
  for (const c of candidates) {
    if (MULTI_INSTANCE_FRIENDLY_ACTION_NAMES.has(c)) return true;
  }
  return false;
}

// Fix #32 — detect user-defined mutation actions by name suffix.
// BRAID consistently names mutations `<Entity>_Insert`, `<Entity>_Update`,
// `<Entity>_Delete`, `<Entity>_Upsert`, `<Entity>_Save`. When the event
// already carries an IfThenElse, the Wirer is expected to put the
// mutation INSIDE one of the branches (the canonical
// "if PK is empty then Insert else Update" pattern). Adding the
// mutation as a top-level sibling next to the IfThenElse instead leaves
// an empty IfThenElse container and a runtime that fires the mutation
// unconditionally — observed repeatedly on the fikir-havuzu reproducer.
const USER_MUTATION_SUFFIX_PATTERN = /_(?:Insert|Update|Delete|Upsert|Save)$/i;

/**
 * The fields of a resolved `IActionDefinitionDto` the MCP guards need.
 *
 * `statement` is what makes a guard trustworthy: an action's NAME follows the
 * designer's generated CRUD convention only when the designer generated it, and
 * custom SQL actions are authored verb-first (`CreateExpense`). The SQL is the
 * fact (KOA-18227).
 */
export interface ResolvedActionDefMeta {
  name?: string;
  displayName?: string;
  /** Raw SQL of a datasource action; absent for built-ins, Swagger, SAP, C#. */
  statement?: string;
}

/**
 * A USER-DEFINED data mutation, as opposed to a built-in verb.
 *
 * KOA-18227 — resolved from the action's SQL when a statement is available:
 * only a datasource action HAS a statement, and a statement that writes is
 * exactly "a user-defined mutation". This is what makes the guard fire for
 * `CreateExpense` / `SubmitExpense`, which the CRUD-suffix pattern below never
 * matched.
 *
 * The name pattern survives as the no-statement fallback and stays deliberately
 * narrow (Insert / Update / Delete / Upsert / Save; NOT Add / Create / Remove):
 * the IfThenElse sibling guard exists to catch the canonical "if PK is empty
 * then Insert else Update" pattern, and built-in verbs (Add / Create / Remove on
 * collections, Notify, …) have no such canonical pair, so they must NOT trigger
 * it. Widening the fallback would reject legitimate built-in batches.
 */
export function isUserDefinedMutationAction(
  actionDefIdOrName: string | undefined,
  resolvedDef: ResolvedActionDefMeta | undefined
): boolean {
  const sqlKind: SqlStatementKind | null = classifySqlStatementKind(resolvedDef?.statement);
  if (sqlKind !== null) return sqlKind === "mutation";
  const candidates = [actionDefIdOrName, resolvedDef?.name, resolvedDef?.displayName].filter(
    (s): s is string => typeof s === "string" && s.length > 0
  );
  for (const c of candidates) {
    if (USER_MUTATION_SUFFIX_PATTERN.test(c)) return true;
  }
  return false;
}

export interface IfThenElseOnEvent {
  actionId: string;
  label: string;
}

export function findIfThenElseOnEvent(eventActions: unknown): IfThenElseOnEvent | undefined {
  if (!Array.isArray(eventActions)) return undefined;
  for (const a of eventActions as { id?: string; label?: string; description?: string }[]) {
    if (a?.label === "IfThenElse" || a?.description === "IfThenElse") {
      return { actionId: a.id ?? "", label: a.label ?? "IfThenElse" };
    }
  }
  return undefined;
}

export function ifThenElseMutationBlockError(
  mutationName: string,
  ifThenElse: IfThenElseOnEvent,
  eventName: string,
  hostKuikaId: string
): string {
  // The exact `ui_logic_add_action_to_branch` payload — emitted verbatim
  // in the teaching error so the LLM doesn't have to guess parameter
  // names. The handler's real schema is:
  //   { kuikaId, eventName, actionId, branch: "if"|"else", actionDefinitionId, parameters? }
  // where `actionId` is the IfThenElse INSTANCE id (NOT `parentActionId`
  // or `branchActionId`) and `branch` is the literal string "if" or
  // "else" (NOT "thenActions"/"elseActions"). This error previously
  // suggested the latter names and the Wirer LLM looped 30 turns
  // hitting "branch must be 'if' or 'else'" then "actionId is required"
  // — see the May 2026 fikir-havuzu rerun (NewIdea.btn-save).
  const exampleParams: string =
    `{\n` +
    `  "kuikaId": "${hostKuikaId}",\n` +
    `  "eventName": "${eventName}",\n` +
    `  "actionId": "${ifThenElse.actionId}",\n` +
    `  "branch": "if",\n` +
    `  "actionDefinitionId": "${mutationName}",\n` +
    `  "parameters": []\n` +
    `}`;
  return (
    `[IfThenElse sibling guard] Event "${eventName}" already has an IfThenElse ` +
    `action (id: ${ifThenElse.actionId}). Adding "${mutationName}" as a sibling ` +
    `next to it leaves the IfThenElse container empty and makes the mutation ` +
    `run unconditionally. If the intent is the canonical "if PK is empty then Insert ` +
    `else Update" pattern, attach "${mutationName}" INSIDE the appropriate branch ` +
    `via \`ui_logic_add_action_to_branch\`. Call it like:\n\n` +
    `  ui_action.add_to_branch(${exampleParams})\n\n` +
    `Required fields: \`kuikaId\` (the host component, NOT the IfThenElse), ` +
    `\`eventName\` (same event the IfThenElse is on), \`actionId\` (the IfThenElse ` +
    `INSTANCE id — NOT \`parentActionId\` or \`branchActionId\`), ` +
    `\`branch\` (the literal string \`"if"\` or \`"else"\` — NOT ` +
    `\`"thenActions"\`/\`"elseActions"\`), and \`actionDefinitionId\` ` +
    `(the mutation to add). If the IfThenElse on this event is wrong and ` +
    `should be removed before re-attaching, call ` +
    `\`ui_logic_delete_action_from_event\` first. Do NOT retry this exact ` +
    `add_to_event call — it will keep being rejected.`
  );
}

function isSentinelParameterValue(value: any): boolean {
  return typeof value === "string" && PARAMETER_SENTINELS.has(value.trim());
}

function normalizeOverrideName(override: any): string | undefined {
  return override.name || override.parameterName;
}

function resolveParameterSymbol(override: any, sd: any, action?: any, parameter?: any): any | null {
  const overrideName = normalizeOverrideName(override);
  if (!overrideName) return null;
  if ((override.optionPath || override.pickerPath || override.selectedPath) && action && parameter) {
    const symbol = buildDesignerActionParameterSymbol(sd, action, parameter, override);
    if ((symbol as any)?.error) return null;
    return symbol;
  }
  if (override.bindingType) {
    // KOA-18136 — route through the parameter-aware builder when the parameter
    // is known, so the inline `add_to_event` lane defaults `targetType` from the
    // signature exactly like the standalone `set_parameter_binding` lane does.
    // Falling back to the bare builder keeps the pre-existing behavior for
    // callers that resolve a symbol without a parameter in hand.
    const symbol =
      action && parameter
        ? buildDesignerActionParameterSymbol(sd, action, parameter, override)
        : buildDesignerSymbol(override, sd);
    if (!symbol || "error" in symbol) return null;
    return symbol;
  }
  if (isSentinelParameterValue(override.value)) return null;
  if (override.value === undefined) return null;

  // Fix #42 — auto-resolve `value: "current.<field>"` to a row-scope symbol.
  // Background: row-action attachments (Sil/Düzenle/Edit on a Table row)
  // historically came through with `parameters: [{name:"Id", value:"current.Id"}]`
  // inline at `add_to_event` time. The fallback below would then store the
  // literal string "current.Id" as a fixed-value, so at runtime the platform
  // would call e.g. `Ideas_Delete(Id="current.Id")` — a guaranteed 404 on
  // every row click. The May-2026 fikir-havuzu reproducer hit this on the
  // Sil button.
  //
  // The canonical row-scope shape is `ESymbolCategory.current` with
  // `fieldName: "<field>"` (per the BRAID parser's `current` case in
  // bindables/symbol-resolver.ts). We promote any literal string of the
  // exact form `current.<identifier>` to that symbol. Detection is
  // intentionally narrow (no whitespace, no path nesting) — anything else
  // falls through to the legacy fixed-value path.
  if (typeof override.value === "string") {
    const currentMatch: RegExpMatchArray | null = override.value.match(/^current\.([A-Za-z_][A-Za-z0-9_]*)$/);
    if (currentMatch) {
      const fieldName: string = currentMatch[1];
      return {
        category: ESymbolCategory.current,
        label: fieldName,
        fieldName,
        description: `current.${fieldName}`,
        currentType: "String",
        targetType: "String"
      };
    }

    // Fix #52 — auto-resolve `value: "default.<name>"` and
    // `value: "screenInput.<name>"` to canonical symbols.
    //
    // Background: Wirer (and occasionally Binder) emit these literal
    // strings inline at `add_to_event` time for IfThenElse branch
    // parameters, e.g. `[{name:"Id", value:"default.NewGuid"}]` and
    // `[{name:"Id", value:"screenInput.id"}]`. Previously the fallback
    // wrapped them as `ESymbolCategory.fixed` with `fixedValue: "default.NewGuid"`,
    // so the runtime would write the literal string "default.NewGuid"
    // into the Id parameter instead of generating a fresh Guid. The
    // May-2026 fikir-havuzu rerun at 10:50 hit this on branch-bound
    // Idea_Insert / Idea_Update.
    //
    // Both `default.X` and `screenInput.X` map cleanly through the
    // canonical `buildDesignerSymbol` factory. Conservative regex:
    // single dot, identifier-only field, no nesting / whitespace.
    const defaultMatch: RegExpMatchArray | null = override.value.match(/^default\.([A-Za-z_][A-Za-z0-9_]*)$/);
    if (defaultMatch) {
      const defaultName: string = defaultMatch[1];
      const built = buildDesignerSymbol({ bindingType: "default", defaultName } as never, sd);
      if (!("error" in built)) return built;
    }

    const screenInputMatch: RegExpMatchArray | null = override.value.match(/^screenInput[.:]([A-Za-z_][A-Za-z0-9_]*)$/);
    if (screenInputMatch) {
      const inputName: string = screenInputMatch[1];
      const built = buildDesignerSymbol({ bindingType: "screenInput", inputName } as never, sd);
      if (!("error" in built)) return built;
    }

    // KOA-18422 — `actionField:<Action>.<Field>`: the record THIS screen loaded.
    //
    // The third operand grammar, beside `current.<field>` (the clicked row) and
    // `screenInput:<name>` (what this screen was opened with). A detail screen holds
    // its parent's key in its own page load — `GetTask` returns `Project_Id` — and
    // before this the value was unreachable, so a move to the project's task list
    // opened with an empty Guid.
    //
    // The action is named, never id'd: the instance id exists only in this screen
    // design, which is why the resolution happens here (see
    // `resolveScreenActionInstanceIdByName`). Narrow by design — one dot, identifier
    // segments, no nesting — so the id-based `actionField:<guid>:<Field>` symbolRef
    // grammar cannot collide with it.
    //
    // AN UNRESOLVED ACTION RETURNS NULL, never the literal string. That is the
    // `current.Id`-as-fixed-value defect class: the caller keeps the target input's
    // placeholder instead of shipping "actionField:GetTask.Project_Id" as a value.
    const actionFieldMatch: RegExpMatchArray | null = override.value.match(
      /^actionField[.:]([A-Za-z_][A-Za-z0-9_]*)\.([A-Za-z_][A-Za-z0-9_]*)$/
    );
    if (actionFieldMatch) {
      const sourceActionName: string = actionFieldMatch[1];
      const fieldName: string = actionFieldMatch[2];
      const sourceActionId: string | null = resolveScreenActionInstanceIdByName(sourceActionName, sd);
      if (!sourceActionId) {
        console.warn(
          `[resolveParameterSymbol] dropped "${override.value}" — no single action instance named ` +
            `"${sourceActionName}" is attached on this screen`
        );
        return null;
      }
      const built = buildDesignerSymbol({ bindingType: "actionField", sourceActionId, fieldName } as never, sd);
      if (!("error" in built)) return built;
      return null;
    }
  }

  return {
    category: ESymbolCategory.fixed,
    isFixed: true,
    label: "Fixed Value",
    fixedValue: override.value,
    description: ""
  };
}

/**
 * Auto-resolve actionId when it's missing or looks like a kuikaId (numeric string).
 * This handles the case where add_to_event and add_confirmation are called in parallel —
 * the model doesn't have the actionId from add_to_event's response yet and guesses the kuikaId.
 */
function autoResolveActionId(input: any): any {
  if (input.actionId && !/^\d+$/.test(String(input.actionId))) return input;

  const scope = resolveDesignerActionScope(getSdInstance(), input);
  if ("error" in scope || !scope.actions || scope.actions.length === 0) return input;

  if (scope.actions.length === 1) {
    return { ...input, actionId: scope.actions[0].id };
  }
  // Multiple actions — return input unchanged, findContextAction will produce a clear error
  return input;
}

function findContextAction(input: any) {
  return findDesignerActionInCurrentContext(getSdInstance(), input);
}

function findActionParameter(action: any, parameterName: string): any {
  return findDesignerActionParameter(action?.parameters, parameterName);
}

/**
 * KOA-18136 — resolve a workflow-start INPUT / workflow-resume OUTPUT parameter
 * by its EXACT name, materializing it onto the action when it is not there yet.
 *
 * These params exist only in the workflow definition; the action's stored
 * signature carries the platform built-ins. Since the built-in lookup is
 * case-insensitive, a dynamic param that differs from a built-in only by case
 * (`ProjectId` vs `projectId`) was shadowed by it — see the call site for what
 * that shipped. Matching exact-case here, ahead of the built-ins, gives the
 * author's own spelling precedence without touching any other action.
 *
 * Returns `undefined` for a non-process action, a name that is not a dynamic
 * param, or a workflow that cannot be resolved — every one of which falls
 * through to the unchanged lookup chain.
 */
/**
 * KOA-18422 — the parameter the action DEFINITION carries and this instance does
 * not.
 *
 * An attached action is a snapshot: `resolveAndCreateAction` clones the
 * definition's `actionParameters` when the action is dropped on an event, and
 * nothing re-syncs that copy when the definition later grows a parameter. The
 * definitions themselves ARE refreshed (`ui_project.reload_datasources`), so the
 * live answer is one lookup away.
 *
 * Returns a CLONE, so the instance never shares a parameter object with the
 * definition — a bound symbol would otherwise leak into every other screen that
 * attached the same action. `undefined` when the action carries no definition
 * id, the definition is unknown, or it declares no such parameter: each of those
 * means the platform does not have it either, and the caller must say so rather
 * than mint it.
 */
function findDefinitionParameter(action: any, parameterName: string): IActionParameter | undefined {
  const definitionId: string = typeof action?.actionDefinitionId === "string" ? action.actionDefinitionId : "";
  if (definitionId.length === 0) return undefined;
  const definition: IActionDefinitionDto | undefined = getAllActionDefinitions(getSdInstance()).find(
    (entry: IActionDefinitionDto): boolean => entry.id === definitionId
  );
  const declared: any[] = Array.isArray((definition as any)?.actionParameters)
    ? ((definition as any).actionParameters as any[])
    : [];
  if (declared.length === 0) return undefined;
  // Exact name first, then the same X ↔ XId / case tolerance the instance
  // lookup uses — one vocabulary for "is this the parameter they meant".
  const match: any =
    declared.find((entry: any): boolean => entry?.name === parameterName) ??
    findParameterWithTolerance(declared, parameterName);
  return match ? (cloneActionParameter(match) as IActionParameter) : undefined;
}

function findDynamicProcessParameter(action: any, parameterName: string): IActionParameter | undefined {
  const isStart: boolean = isWorkflowStartAction(action);
  const isResume: boolean = isWorkflowResumeAction(action);
  if (!isStart && !isResume) return undefined;

  // Already materialized by an earlier bind — reuse it rather than pushing a
  // second entry the persist step would then have to reconcile.
  const existing: IActionParameter | undefined = Array.isArray(action?.parameters)
    ? action.parameters.find((p: IActionParameter): boolean => p?.name === parameterName)
    : undefined;
  if (existing) return existing;

  const expanded: IActionParameter[] = isStart
    ? expandWorkflowStartInputParameters(
        action,
        buildWorkflowStartExpansionContext(getSdInstance() as unknown as ISdWorkflowStartSource)
      )
    : expandWorkflowResumeOutputParameters(action, getSdInstance() as unknown as IWorkflowResumeSource);

  const match: IActionParameter | undefined = expanded.find((p: IActionParameter): boolean => p.name === parameterName);
  if (!match) return undefined;
  if (!Array.isArray(action.parameters)) action.parameters = [];
  action.parameters.push(match);
  return match;
}

/**
 * Fix #18 — Parameter-name tolerance.
 *
 * Background
 * ----------
 * BRAID emits FK params with the explicit `Id` suffix (`ChannelId`,
 * `StatusId`) but the Backend Architect sometimes generates action
 * signatures with the suffix stripped (`Channel`, `Status`) or vice
 * versa. The Binder then fails with `Parameter "ChannelId" not found
 * on action "Ideas_Insert"` and gives up on the binding.
 *
 * Resolution
 * ----------
 * After an exact lookup misses, try in order:
 *   1. Case-insensitive exact match.
 *   2. Suffix-strip: `ChannelId` → `Channel` (only when ≥3 chars left).
 *   3. Suffix-add:   `Channel`  → `ChannelId`.
 *   4. Case-insensitive variants of #2 and #3.
 *
 * Returns the matched parameter or `undefined`. Conservative — only
 * applies the variants when an exact lookup misses, so legitimate
 * matches still take precedence.
 */
function findParameterWithTolerance(parameters: any[], parameterName: string): any | undefined {
  const exact = findDesignerActionParameter(parameters, parameterName);
  if (exact) return exact;

  if (!Array.isArray(parameters) || parameters.length === 0) return undefined;
  const lcName = parameterName.toLowerCase();
  const ciMatch = parameters.find((p) => typeof p?.name === "string" && p.name.toLowerCase() === lcName);
  if (ciMatch) return ciMatch;

  // KOA-17797 — normalized match (strip spaces / underscores / hyphens), so the
  // LLM's `component_name` / `pdf-url` / `Bag Item` reach `componentName` /
  // `pdfUrl` / `bagItem`. Covers every action, not just a per-verb list.
  const normName = lcName.replace(/[\s_-]/g, "");
  const normMatch = parameters.find(
    (p) => typeof p?.name === "string" && p.name.toLowerCase().replace(/[\s_-]/g, "") === normName
  );
  if (normMatch) return normMatch;

  const candidates: string[] = [];
  if (parameterName.length >= 5 && /id$/i.test(parameterName)) {
    candidates.push(parameterName.slice(0, -2));
  }
  if (!/id$/i.test(parameterName)) {
    candidates.push(parameterName + "Id");
  }
  for (const candidate of candidates) {
    const direct = findDesignerActionParameter(parameters, candidate);
    if (direct) return direct;
    const lcCandidate = candidate.toLowerCase();
    const ci = parameters.find((p) => typeof p?.name === "string" && p.name.toLowerCase() === lcCandidate);
    if (ci) return ci;
  }
  return undefined;
}

function persistResolvedAction(found: any, nextAction: any, changeReason?: any): any {
  const persistedAction =
    found.action.id === found.rootAction.id
      ? nextAction
      : replaceDesignerActionInTree(found.rootAction, found.action.id, nextAction);
  found.scope.saveTopLevelAction(persistedAction, changeReason);
  return persistedAction;
}

function deleteResolvedAction(found: any, changeReason?: any): void {
  if (found.action.id === found.rootAction.id) {
    found.scope.deleteTopLevelAction(found.action, changeReason);
    return;
  }

  const persistedAction = deleteDesignerActionFromTree(found.rootAction, found.action.id);
  found.scope.saveTopLevelAction(persistedAction, changeReason);
}

/** First non-empty, trimmed string among the candidates (else undefined). */
function firstNonEmptyString(...candidates: unknown[]): string | undefined {
  for (const candidate of candidates) {
    if (typeof candidate === "string") {
      const trimmed: string = candidate.trim();
      if (trimmed.length > 0) return trimmed;
    }
  }
  return undefined;
}

/**
 * True when an action INSTANCE is of the requested action type/name.
 *
 * The planner references an action to delete by its TYPE (e.g. "NAVIGATE")
 * rather than a per-instance Guid, because it only ever sees the screen — never
 * a specific event's attached-action instance ids. Match the requested type,
 * case-insensitively, against the instance's own `label` / `actionDefinitionId`
 * and — when the definition id is a catalog Guid — the resolved definition's
 * `name` / `displayName`.
 */
function actionMatchesType(action: IAction, typeName: string, defs: readonly IActionDefinitionDto[]): boolean {
  const target: string = typeName.toLowerCase();
  if (target.length === 0) return false;
  if (typeof action.label === "string" && action.label.toLowerCase() === target) return true;
  const defId: string | undefined =
    typeof action.actionDefinitionId === "string" ? action.actionDefinitionId : undefined;
  if (defId !== undefined && defId.toLowerCase() === target) return true;
  if (defId !== undefined) {
    const def: IActionDefinitionDto | undefined = defs.find((d: IActionDefinitionDto): boolean => d.id === defId);
    if (def !== undefined) {
      if (typeof def.name === "string" && def.name.toLowerCase() === target) return true;
      if (typeof def.displayName === "string" && def.displayName.toLowerCase() === target) return true;
    }
  }
  return false;
}

interface DeleteTargetsResolved {
  ids: string[];
  matchedByType?: string;
}

/**
 * Resolve which top-level action instance id(s) `delete_from_event` should
 * remove. Precedence:
 *   1. Explicit instance ids — `actionId` (single) or `actionIds[]` (batch).
 *   2. Otherwise the caller named the action by TYPE (`action` / `actionName`
 *      / `actionType`): resolve the event scope and match its top-level actions
 *      by type. Zero matches → honest error; a single match → delete it;
 *      multiple matches → refuse and list them (a destructive op must not guess
 *      which of several same-typed actions the user meant).
 */
function resolveDeleteActionTargets(
  input: ToolInput,
  sd: ScreenDesignInstance
): DeleteTargetsResolved | { error: string } {
  const explicitIds: string[] = [];
  if (typeof input.actionId === "string" && input.actionId.length > 0) explicitIds.push(input.actionId);
  const batch: unknown = safeParse(input.actionIds);
  if (Array.isArray(batch)) {
    for (const id of batch) {
      if (typeof id === "string" && id.length > 0) explicitIds.push(id);
    }
  }
  if (explicitIds.length > 0) {
    return { ids: Array.from(new Set(explicitIds)) };
  }

  const typeName: string | undefined = firstNonEmptyString(
    input.action,
    input.actionName,
    input.actionType,
    input.actionDefinitionId
  );
  if (typeName === undefined) {
    return {
      error:
        "actionId is required — pass actionId (or actionIds[]), or an action type/name " +
        '(e.g. "NAVIGATE") together with kuikaId + eventName to match by type.'
    };
  }

  const scope = resolveDesignerActionScope(sd, input);
  if ("error" in scope) return scope;

  const actions: IAction[] = Array.isArray(scope.actions) ? scope.actions : [];
  const defs: IActionDefinitionDto[] = getAllActionDefinitions(sd);
  const matches: IAction[] = actions.filter((a: IAction): boolean => actionMatchesType(a, typeName, defs));
  const label: string = scope.label || "the event";
  if (matches.length === 0) {
    return { error: `No "${typeName}" action found on ${label}.` };
  }
  if (matches.length > 1) {
    const list: string = matches
      .map((a: IAction): string => `${a.label || a.description || "Unnamed"} (${a.id})`)
      .join(", ");
    return {
      error: `Multiple "${typeName}" actions found on ${label}: ${list}. Pass the specific actionId to delete just one.`
    };
  }
  return { ids: [matches[0].id], matchedByType: typeName };
}

function getResolvedActionParameter(input: any): any | { error: string } {
  const { parameterName } = input;
  if (!parameterName) return { error: "parameterName is required" };

  const found = findContextAction(input);
  if ("error" in found) return found;

  // KOA-18136 — the DYNAMIC process parameters are consulted BEFORE the static
  // signature, on an EXACT-CASE name only.
  //
  // `StartProcess`'s own signature carries `workflow`, `projectId` and
  // `initiator`, and `findActionParameter` is case-insensitive. So a workflow
  // whose business-object INPUT is named `ProjectId` — one of the most ordinary
  // names a process can have — resolved to the platform's `projectId` on the
  // first lookup. Two things went wrong at once and neither was visible: the
  // workflow's own INPUT was never materialized (the expansion below sits behind
  // `if (!parameter)`, so it never ran), and the initiator form's SelectBox was
  // written over the platform value the runtime needs to locate the process.
  // The read-back then counted that same parameter and reported every input
  // bound. Same shape for `ResumeProcess` / `SaveStepData` step OUTPUTS.
  //
  // Exact case is the discriminator because it is the one the platform itself
  // guarantees: the built-ins are lower-camel by construction and the dynamic
  // params carry the author's own spelling verbatim. A workflow parameter named
  // EXACTLY like a built-in stays ambiguous and the built-in keeps winning —
  // otherwise attaching the action would break, which is worse.
  // Union because the chain below also resolves NAVIGATE's screen-input params,
  // which are a different shape living on `targetScreenInputParams`.
  let parameter: IActionParameter | ITargetScreenInputParam | undefined = findDynamicProcessParameter(
    found.action,
    parameterName
  );
  if (!parameter) parameter = findActionParameter(found.action, parameterName);
  if (!parameter) {
    // Fix #18 — parameter name tolerance (X ↔ XId, case-insensitive).
    parameter = findParameterWithTolerance(found.action?.parameters, parameterName);
    // NAVIGATE screen-input params (e.g. the row id passed to a detail screen) live on
    // `targetScreenInputParams`, not the signature `parameters`. Resolve them here so a
    // current.X / screenInput binding can be applied — setDesignerActionParameter writes
    // the symbol onto the matching targetScreenInputParams entry.
    if (!parameter && Array.isArray(found.action?.targetScreenInputParams)) {
      const lowerName: string = parameterName.toLowerCase();
      parameter = found.action.targetScreenInputParams.find(
        (p: { name?: unknown }) => typeof p?.name === "string" && p.name.toLowerCase() === lowerName
      );
    }
    // WS1c — StartProcess / StartExternalProcess surface the SELECTED workflow's
    // INPUT params dynamically; only the properties panel expands them, so a bind
    // against a workflow INPUT param would otherwise fail here as "not found".
    // Materialize the requested param onto the action so setDesignerActionParameter
    // (+ persistResolvedAction) persist it, mirroring action-panel.tsx.
    if (!parameter && isWorkflowStartAction(found.action)) {
      const wfExpansionCtx = buildWorkflowStartExpansionContext(getSdInstance() as unknown as ISdWorkflowStartSource);
      const expandedParams: IActionParameter[] = expandWorkflowStartInputParameters(found.action, wfExpansionCtx);
      const expandedMatch: IActionParameter | undefined =
        expandedParams.find((p: IActionParameter): boolean => p.name === parameterName) ??
        findParameterWithTolerance(expandedParams, parameterName);
      if (expandedMatch) {
        if (!Array.isArray(found.action.parameters)) found.action.parameters = [];
        found.action.parameters.push(expandedMatch);
        parameter = expandedMatch;
      }
    }
    // KOA-17362 (F3) — ResumeProcess / SaveStepData surface the current managed
    // task screen's step-OUTPUT params (the approval decision, e.g.
    // managerApproved / hrApproved) dynamically; only the properties panel
    // expands them, so a bind against the real (panel-visible) decision param
    // would otherwise fail here as "not found" against the stale static
    // signature. Materialize the requested param onto the action so
    // setDesignerActionParameter (+ persistResolvedAction) persist it, mirroring
    // the StartProcess branch above and action-panel.tsx's ResumeProcess block.
    if (!parameter && isWorkflowResumeAction(found.action)) {
      const resumeParams: IActionParameter[] = expandWorkflowResumeOutputParameters(
        found.action,
        getSdInstance() as unknown as IWorkflowResumeSource
      );
      const resumeMatch: IActionParameter | undefined =
        resumeParams.find((p: IActionParameter): boolean => p.name === parameterName) ??
        findParameterWithTolerance(resumeParams, parameterName);
      if (resumeMatch) {
        if (!Array.isArray(found.action.parameters)) found.action.parameters = [];
        found.action.parameters.push(resumeMatch);
        parameter = resumeMatch;
      }
    }
    if (!parameter) {
      // KOA-18422 — the signature grew AFTER the action was attached.
      //
      // An attached action carries a COPY of the definition's parameters, taken
      // when it was dropped on the event (`resolveAndCreateAction` clones
      // `actionDef.actionParameters`). Add a column to the table, re-save the
      // SQL action, and the definition gains a parameter the copy on every
      // screen still lacks — which is exactly what an edit turn does. Measured
      // on the apply of `chat_1789041128379_zicjzv`: the backend read-back
      // confirmed `Deadline` on `CreateProject`, and the bind was refused as a
      // "backend signature gap" against an instance whose copy predated it.
      //
      // Materialize from the DEFINITION, never from the request: the platform
      // says the parameter exists, and the stale copy is the only thing that
      // disagrees. A name nobody declares still falls through to the message
      // below. Same shape as the workflow branches above — push it onto the
      // instance and let `setDesignerActionParameter` / `persistResolvedAction`
      // write it down.
      const fromDefinition: IActionParameter | undefined = findDefinitionParameter(found.action, parameterName);
      if (fromDefinition) {
        if (!Array.isArray(found.action.parameters)) found.action.parameters = [];
        found.action.parameters.push(fromDefinition);
        parameter = fromDefinition;
      }
    }
    if (!parameter) {
      // Fix #47 — surface the available parameter names + bounceBack
      // instruction so the Binder doesn't loop on add_to_event trying to
      // "re-attach the action hoping the parameter will appear". The
      // May-2026 fikir-havuzu rerun hit this: BRAID emitted `CreatedBy`
      // but the action signature had `UserId`; the Binder failed, then
      // tried `add_to_event` with `{symbolRef}` payloads until the 3-strike
      // loop-guard fired. The richer error message names the right
      // escalation path so the LLM stops looping.
      const availableNames: string[] = Array.isArray(found.action?.parameters)
        ? found.action.parameters
            .map((p: { name?: unknown }) => (typeof p?.name === "string" ? p.name : ""))
            .filter((name: string) => name.length > 0)
        : [];
      if (Array.isArray(found.action?.targetScreenInputParams)) {
        for (const p of found.action.targetScreenInputParams as { name?: unknown }[]) {
          if (typeof p?.name === "string" && p.name.length > 0) availableNames.push(p.name);
        }
      }
      const actionLabel: string = typeof found.action?.label === "string" ? found.action.label : "(unknown)";
      const available: string =
        availableNames.length > 0 ? availableNames.join(", ") : "(no parameters on this action)";
      return {
        error:
          `Parameter "${parameterName}" not found on action "${actionLabel}" ` +
          `(actionId="${input.actionId}"). Available parameters on this action: ${available}.\n\n` +
          `This is a backend signature gap, NOT something \`add_to_event\` can fix — ` +
          `re-attaching the action with the missing parameter in the payload will not ` +
          `materialise it on the signature. Two options:\n\n` +
          `  1. If the parameter name is a typo / aliasing issue (e.g. you wrote ` +
          `"CreatedBy" but the schema column is "UserId"), retry the SAME bind call ` +
          `with the correct name from the "Available parameters" list above.\n\n` +
          `  2. If the parameter genuinely belongs on the action but isn't there, ` +
          `emit a \`bounceBack\` to \`backend-architect\` with reason "wrong_type" ` +
          `(or "missing_column"), naming the action ("${actionLabel}") and the ` +
          `missing parameter ("${parameterName}"). Do NOT retry via \`add_to_event\` ` +
          `— that path is for ATTACHING actions, not binding parameters, and the ` +
          `Binder-scope guard will hard-reject the call after 3 strikes.`
      };
    }
    // Back-propagate the canonical name so downstream symbol-builder
    // and response payload use the action's real parameter name (not
    // the LLM's variant). Mutating `input` mirrors the pattern used by
    // autoResolveKuikaIdField / autoResolveActionInstanceId above.
    if (typeof parameter.name === "string" && parameter.name.length > 0) {
      input.parameterName = parameter.name;
    }
  }

  return { found, parameter };
}

/**
 * Auto-inject well-known internal parameters (executePageInitActionsAfterClose, fromScreenId, etc.)
 * that the LLM is not expected to set manually.
 */
function applyAutoSetParameters(action: any, sd: any, fromScreenId?: string): void {
  for (const [paramName, rawValue] of Object.entries(AUTO_SET_PARAMS)) {
    const param = findDesignerActionParameter(action.parameters, paramName);
    if (!param) continue;

    // Skip if already has a fixed value
    if (param.symbol?.fixedValue !== undefined && param.symbol.fixedValue !== null && param.symbol.fixedValue !== "") {
      continue;
    }

    // Skip empty sentinel values
    if (rawValue === "") continue;

    // Resolve __CURRENT_SCREEN_ID__ sentinel
    let value: any = rawValue;
    if (rawValue === "__CURRENT_SCREEN_ID__") {
      if (fromScreenId) {
        value = fromScreenId;
      } else {
        const screenDesign = sd.screenProvider?.getScreenDesign?.();
        value = screenDesign?.id || sd.screenProvider?.state?.sdId || "";
      }
      if (!value) continue;
    }

    // Convert "true"/"false" strings to boolean
    if (value === "true") value = true;
    else if (value === "false") value = false;

    const symbol = {
      category: ESymbolCategory.fixed,
      isFixed: true,
      label: "Fixed Value",
      fixedValue: value,
      description: ""
    };
    const nextAction = setDesignerActionParameter(action, param, symbol);
    action.parameters = nextAction.parameters;
  }
}

// ---------------------------------------------------------------------------
// Semantic parameter defaults — proactive Binder safety net.
//
// `ensureParameterSymbols` below populates a fixedValue placeholder for every
// otherwise-unset action parameter (because the action panel UI invariant
// requires every parameter to carry a symbol — see
// `designer-action-actions.ts::getDefaultDesignerActionSymbol`). That placeholder
// fires as an EMPTY string / zero Guid at runtime if the Binder forgets to
// override it: a Foo_Insert button click writes a row of empty columns instead
// of failing loudly. The pipeline-coordinator's post-Binder gates
// (`applyUserContextRebinds`, `applyMutationParamBindings` in
// `mcp-chat-server/src/pipeline/pipeline-completion.ts`) detect this and
// re-bind the obvious cases, but only when the gates actually run — a Binder
// bounce-back that exhausts the retry cap, an aborted pipeline, or a
// classifier mis-route all bypass them.
//
// `applySemanticParameterDefaults` shifts that safety net one layer earlier:
// at action-creation time, before the placeholder symbol lands, we ALREADY
// know the action name (so we know if it's an `_Insert`) and the parameter
// names (so we know which one is the PK or a user FK). Bind those two
// well-defined cases proactively to the canonical platform defaults.
//
// Why these two cases:
//   - Insert.Id → NewGuid: rows are client-generated Guids per
//     `binder/pk-defaults` skill. Every Insert action that ships unbound
//     would write rows with `00000000-0000-0000-0000-000000000000` as PK,
//     which is either rejected by FK constraints or silently overwrites
//     a real row.
//   - user-FK params (UserId, CreatedByUserId, OwnerId, …) → CurrentUserid:
//     the platform resolves CurrentUserid from the authenticated session
//     at action-fire time. NewGuid here would fabricate a Guid pointing
//     at no user, breaking audit + every join into the user table.
//
// Anything else (Title, Description, Status, FK to non-user entity, …)
// stays on the fixedValue placeholder — the Binder must wire those, and the
// post-Binder gates will catch what the Binder misses. We DELIBERATELY do
// NOT expand this list: a wrong proactive default is worse than the
// placeholder because the Binder treats `default:X` as a real binding and
// skips it, while it actively rebinds a fixedValue placeholder.
//
// Pattern source-of-truth note: these patterns are intentionally kept in
// sync with `mcp-chat-server/src/agents/shared/runtime/current-user-defaults.ts`
// (`USER_FK_NAME_PATTERN`) and pipeline-completion's user-FK rebind.
// Browser bundle cannot import from the Node server, so duplication is
// structural — when one changes, update both. A drift would show up as
// post-gate rebinds firing on params the proactive helper already bound,
// which is a cheap no-op; a more subtle drift the other way (gate misses,
// helper misses) would surface as runtime empty-insert reports.

// Matches names ending in a user-FK suffix optionally preceded by a
// `By` / `User` / `Member` connector. Optional underscore before id/guid
// so platform SQL-style names match (`KUser_Id`, `User_Id`).
// Examples that match: `UserId`, `KUser_Id`, `CreatedByUserId`,
// `UpdatedByUserGuid`, `OwnerUserId`, `AssignedById`, `ModifiedByUserId`.
// Examples that do NOT match: `AccountId`, `TaskOwnerName`, `SuperUserBlob`.
// Keep byte-identical with mcp-chat-server
// `agents/shared/runtime/current-user-defaults.ts` → `USER_FK_NAME_PATTERN`.
const USER_FK_PARAM_PATTERN =
  /(?:^|[A-Z_])(?:user|owner|assigned|created|modified|updated)(?:by)?(?:user|member)?_?(?:id|guid)$/i;

const PK_PARAM_NORMALIZED_NAMES: ReadonlySet<string> = new Set(["id"]);

// Verbs that name a READ and verbs that name a WRITE. Both spellings the
// platform produces are covered: the intent verb the planner leads with
// (`ListOrders`, `GetExpenseRequest`) and the snake suffix of generated CRUD
// (`Orders_GetAll`, `Orders_GetById`).
const READ_ACTION_VERBS: ReadonlySet<string> = new Set([
  "list",
  "get",
  "search",
  "find",
  "fetch",
  "query",
  "count",
  "report",
  "read"
]);

// An explicit mutation verb outranks a read-ish lead word, so `Report_Update`
// and `Listing_Insert` stay writes.
const WRITE_ACTION_VERBS: ReadonlySet<string> = new Set([
  "insert",
  "update",
  "delete",
  "save",
  "create",
  "remove",
  "add",
  "upsert"
]);

/** Leading word of one camel-cased name segment, lowercased. */
function leadingActionWord(segment: string): string {
  const words: string[] = segment.split(/(?=[A-Z])/);
  const first: string = words[0] ?? "";
  return first.toLowerCase();
}

/**
 * Is this action a READ?
 *
 * The distinction decides who a user-FK parameter is ABOUT. On a write, a
 * user-FK column stamps ownership — "this row is mine" — so the current user is
 * right. On a read, the very same parameter name is a FILTER, and the filter's
 * subject comes from the caller's context (the process, the selected row, a
 * screen input), never from whoever happens to be looking at the screen.
 *
 * Pinning the viewer onto a read is not a missing binding — it is a confidently
 * wrong one, which is worse: it looks bound in the panel and returns the wrong
 * person's rows at runtime. On 2026-08-13 an approver's task screen asked for
 * "the REQUESTER's previous expense requests" and got a filter on the approver,
 * whose own history is empty — a blank list that reads as a broken query
 * (KOA-18136).
 */
function isReadActionName(name: unknown): boolean {
  if (typeof name !== "string") return false;
  const segments: string[] = name.split("_").filter((s: string): boolean => s.trim().length > 0);
  if (segments.length === 0) return false;
  const head: string = leadingActionWord(segments[0]);
  const tail: string = leadingActionWord(segments[segments.length - 1]);
  if (WRITE_ACTION_VERBS.has(head) || WRITE_ACTION_VERBS.has(tail)) return false;
  return READ_ACTION_VERBS.has(head) || READ_ACTION_VERBS.has(tail);
}

// Matches action names that LOOK like a row-insert mutation. Update /
// delete / save are intentionally excluded — Update's `Id` is bound
// from `screenInput:Id` by the Binder's detail-screen flow, not by
// NewGuid; Delete's `Id` from `current.Id` in row scope.
const INSERT_ACTION_NAME_PATTERN = /_insert$/i;

/**
 * Fix #20 — Mutation actions on `pageInit` block.
 *
 * Matches action names that LOOK like data-mutating CRUD (Insert /
 * Update / Delete / Upsert / Save / Create) — attaching these to the
 * page-load event auto-fires them on every screen visit, which is
 * almost never the intent. The Wirer skill (rule 8d) says don't do
 * this; the LLM does it anyway. We surface an explicit teaching error
 * instead of silently passing.
 *
 * Conservative: only matches snake-style suffixes (`Foo_Insert`,
 * `Foo_Update`, …) so built-ins with `insert`/`update` in the middle
 * of a longer name (`ChangeVisibilityOfUpdate` — hypothetical, but the
 * principle holds) don't trip the guard.
 */
const MUTATION_ACTION_NAME_PATTERN = /_(?:insert|update|delete|upsert|save|create|remove|add)$/i;

function isMutationActionName(name: unknown): boolean {
  return typeof name === "string" && MUTATION_ACTION_NAME_PATTERN.test(name);
}

interface PageInitMutationHostScope {
  kuikaId?: unknown;
  eventName?: unknown;
}

/**
 * When `hostScope` points at screen-level `pageInit` (kuikaId `-1`)
 * AND `actionName` matches the mutation pattern, return a teaching
 * error result; otherwise return `null`.
 */
function checkMutationOnPageInit(
  actionName: string,
  hostScope: PageInitMutationHostScope,
  statement?: string
): { success: false; error: string } | null {
  const kuikaId = hostScope?.kuikaId != null ? String(hostScope.kuikaId) : "";
  const eventName = typeof hostScope?.eventName === "string" ? hostScope.eventName : "";
  if (kuikaId !== "-1" || eventName !== "pageInit") return null;
  // KOA-18227 — the SQL decides. `CreateExpense` (an `INSERT ... OUTPUT ...`)
  // passed this guard for as long as it was a name test, and the orchestrator's
  // pageInit parameter-defaulting pass trusted the guard's silence: it treats
  // every pageInit action as a read and fills leftover params with type
  // defaults, which on a mutation means an auto-firing insert with blank form
  // fields on every screen visit. Two belts, one broken buckle.
  const sqlKind: SqlStatementKind | null = classifySqlStatementKind(statement);
  if (sqlKind === "query") return null;
  if (sqlKind !== "mutation" && !isMutationActionName(actionName)) return null;
  return {
    success: false,
    error:
      `[VALIDATION] Mutation action "${actionName}" cannot be attached to pageInit (screen onLoad). ` +
      `pageInit fires automatically when the screen opens — auto-firing an Insert / Update / Delete ` +
      `would create or destroy data on every visit, which is almost never the intent. ` +
      `If you meant to LOAD data on screen open, attach a GetAll / GetById / read-style action ` +
      `(or an SqlDb_GetAll datasource) to pageInit instead. ` +
      `If the mutation should fire from a button click, attach it to that button's onClick event.`
  };
}

/**
 * Fix #74 — Block NAVIGATE on pageInit.
 *
 * pageInit fires automatically when the screen opens. Attaching
 * NAVIGATE to pageInit means the runtime navigates AWAY from the
 * screen the moment it loads — the screen becomes unreachable
 * (visually identical to a permanent redirect). The May-2026
 * fikir-havuzu rerun at 09:39 hit this: the Wirer attached NAVIGATE
 * to NewIdea's pageInit after Update/GetAll/GetById attaches were
 * rejected by other guards, and the form screen was DOA from then on.
 *
 * Conservative: matches the literal verb name "NAVIGATE" (any case)
 * and the built-in actionDefinition Guid form. Mid-string matches
 * (e.g. a hypothetical custom action "RefreshAndNavigate") don't
 * trip the guard.
 */
const NAVIGATE_BUILTIN_GUID: string = "3260278d-dee0-49b0-809e-28843536d7ce";

function isNavigateActionName(name: unknown): boolean {
  if (typeof name !== "string") return false;
  const lower: string = name.toLowerCase();
  return lower === "navigate" || lower === NAVIGATE_BUILTIN_GUID;
}

function checkNavigateOnPageInit(
  actionName: string,
  hostScope: PageInitMutationHostScope
): { success: false; error: string } | null {
  const kuikaId = hostScope?.kuikaId != null ? String(hostScope.kuikaId) : "";
  const eventName = typeof hostScope?.eventName === "string" ? hostScope.eventName : "";
  if (kuikaId !== "-1" || eventName !== "pageInit") return null;
  if (!isNavigateActionName(actionName)) return null;
  return {
    success: false,
    error:
      `[VALIDATION] NAVIGATE cannot be attached to pageInit (screen onLoad). ` +
      `pageInit fires automatically when the screen opens — attaching NAVIGATE there means the ` +
      `runtime immediately leaves the screen, and the user never gets to interact with it ` +
      `(visually identical to a permanent redirect). This is a screen-killer pattern. ` +
      `If you actually need conditional navigation on load (e.g. redirect unauthenticated users), ` +
      `attach an IfThenElse with the condition on pageInit, with NAVIGATE only in the branch ` +
      `that should leave. Otherwise, move NAVIGATE to a button's onClick (Save → IdeasList, ` +
      `Cancel → previous, etc.).`
  };
}

function normalizeIdentifierForParamMatch(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Returns `true` when the parameter already has a meaningful binding —
 * componentValue / a named default / screenInput / an actionField, etc.
 * — so the semantic-default helper must NOT overwrite it. Fixed empty
 * values and fixed Guid placeholders left over from a previous
 * `ensureParameterSymbols` run ARE considered overwritable.
 */
function isMeaningfullyBoundParameter(param: any): boolean {
  const symbol = param?.symbol;
  if (!symbol) return false;
  const cat = symbol.category;
  if (
    cat === ESymbolCategory.default &&
    typeof symbol.defaultValueName === "string" &&
    symbol.defaultValueName.length > 0
  ) {
    return true;
  }
  // `displayItemProp` is the symbol category the platform stores for a
  // `bindingType: "componentValue"` binding (see designer-property-actions
  // `buildDesignerSymbol` → `case "componentValue"`). There is no separate
  // ESymbolCategory.componentValue; the bindingType is just the input shape.
  if (cat === ESymbolCategory.displayItemProp) return true;
  if (cat === ESymbolCategory.screenInput) return true;
  if (cat === ESymbolCategory.actionField || cat === ESymbolCategory.actionResult) return true;
  if (cat === ESymbolCategory.displayItem || cat === ESymbolCategory.current) return true;
  if (cat === ESymbolCategory.fixed) {
    // A user-supplied literal value is "bound"; the placeholder
    // ("" / 0 / false / a freshly-minted random Guid) is not.
    const v = symbol.fixedValue;
    if (v == null || v === "") return false;
    if (typeof v === "string") {
      if (v === "00000000-0000-0000-0000-000000000000") return false;
      // 36-char Guid pattern — produced by `ensureParameterSymbols` for
      // Guid-typed params. Treat as placeholder so we can override.
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) return false;
      return true;
    }
    if (typeof v === "number") return v !== 0;
    if (typeof v === "boolean") return v !== false;
    return true;
  }
  // Any other category (formula, bag, mediaSourceList, …) — assume
  // intentional and don't overwrite.
  return true;
}

/** One parameter, read only for the two fields this question needs. */
interface RequiredParamCandidate {
  name?: unknown;
  isOptionalParameter?: unknown;
}

/**
 * A project DATASOURCE action — one whose parameters the platform declares
 * required by construction. Marked by its datasource id, or by carrying SQL.
 */
function isDatasourceBackedAction(actionDef: unknown): boolean {
  if (actionDef === null || actionDef === undefined || typeof actionDef !== "object") return false;
  const def = actionDef as { kDatasource_Id?: unknown; datasourceId?: unknown; statement?: unknown };
  if (typeof def.kDatasource_Id === "string" && def.kDatasource_Id.length > 0) return true;
  if (typeof def.datasourceId === "string" && def.datasourceId.length > 0) return true;
  return typeof def.statement === "string" && def.statement.trim().length > 0;
}

/**
 * The action DEFINITION behind an action INSTANCE.
 *
 * `collectUnboundRequiredParameters` refuses to answer without one
 * (`isDatasourceBackedAction`), because optionality is a property of the
 * definition and a built-in's is platform metadata this code cannot read. An
 * instance carries `actionDefinitionId`, which is the definition's id for a
 * project datasource action and a VERB for a built-in (`NAVIGATE`, `GoBack`) —
 * the verb matches nothing here, so a built-in resolves to `undefined` and the
 * predicate answers "no debt", which is the correct conservative answer.
 *
 * The name / displayName fallback mirrors the attach path's own canonicalization
 * (`ui_logic_add_action_to_event`): the two must agree, or an action would owe
 * parameters at attach time and owe none when asked again.
 */
function resolveActionDefinitionForInstance(sd: any, action: any): unknown {
  const definitionId: unknown = action?.actionDefinitionId;
  if (typeof definitionId !== "string" || definitionId.length === 0) return undefined;
  const definitions: any[] = getAllActionDefinitions(sd);
  const byId = definitions.find((candidate: any) => candidate.id === definitionId);
  if (byId) return byId;
  const searchName = definitionId.toLowerCase().replace(/\s+/g, "");
  return definitions.find((candidate: any) => {
    const name = (candidate.name || "").toLowerCase().replace(/\s+/g, "");
    const displayName = (candidate.displayName || "").toLowerCase().replace(/\s+/g, "");
    return name === searchName || displayName === searchName;
  });
}

/** An action as this check reads it — the parameter list and nothing else. */
interface ParameterCarryingAction {
  parameters?: unknown;
}

/**
 * KOA-18193 — the parameters this action REQUIRES and nothing fills.
 *
 * Call it AFTER `applySemanticParameterDefaults` + `ensureParameterSymbols`, so
 * the auto-fillers (PK-on-insert → NewGuid, user FK → CurrentUserid, required
 * enum → first member) have already claimed everything they own. The
 * placeholders `ensureParameterSymbols` installs are visible as what they are:
 * `isMeaningfullyBoundParameter` reads `""`, a zero Guid, and a freshly minted
 * random Guid as UNBOUND, which is exactly how the backend reads them.
 *
 * WHY THIS LIVES IN THE DESIGNER AND NOT IN THE SERVER'S PRE-DISPATCH GUARD.
 * `tool-executor.checkActionParameters` never sees the action DEFINITION, so it
 * cannot know which parameters are optional. That is why it asks the only
 * question it can answer — "is EVERY parameter unbound" — and lets a half-bound
 * action through; an empty `Notes` beside a bound `Name` is a legitimate
 * optional value, and rejecting it there would break working calls
 * (`unbound-action-validator.test.ts` pins that fail-open as intended).
 *
 * Here the definition IS in hand. `isOptionalParameter` is the platform's own
 * flag — the same one `ValidationExtensions.cs` consults before raising
 * `KV1170 "The required parameter is not set"` and aborting code generation.
 * So a name returned here is not a guess about what might be missing: it is the
 * error the build WILL raise, named at the moment the attach happens instead of
 * after Preview refuses to open.
 *
 * Scope is `action.parameters` only. A NAVIGATE's unfilled target screen input
 * is a different failure (KV1017) already refused by `navigateUnlandedScreenInputError`.
 */
/**
 * KOA-18158 — platform built-in verbs, by NAME.
 *
 * Mirrors the closed set in `mcp-chat-server/src/agents/shared/builtin-verbs.ts`
 * (`KUIKA_BUILTIN_VERBS`); the two are a wire contract between packages that
 * cannot import each other, like `SCREEN_LINK_SCHEME`. Change one, change the
 * other. GUIDs are here too because an instance's `actionDefinitionId` is
 * sometimes the definition id rather than the verb.
 */
const BUILTIN_ACTION_IDENTITIES: ReadonlySet<string> = new Set<string>(
  [
    "NAVIGATE",
    "Navigate",
    "GoBack",
    "GoTo",
    "Notify",
    "SetValueOf",
    "ChangeVisibilityOf",
    "ChangeEnabledOf",
    "ToggleVisibilityOf",
    "ToggleEditabilityOf",
    "OpenCollapsePanelOf",
    "CloseCollapsePanelOf",
    "ToggleCollapsePanelOf",
    "ScrollTo",
    "SetFocusTo",
    "TriggerEvent",
    "IfThenElse",
    "Search",
    "SendMail",
    "OpenInBrowser",
    "OpenPdf",
    "PrintReport",
    NAVIGATE_BUILTIN_GUID,
    "b481b149-45be-49a2-b2cc-687e9f2383c7" // GoBack
  ].map((identity: string): string => identity.toLowerCase())
);

/**
 * KOA-18158 — a platform built-in, whatever its definition looks like.
 *
 * `isDatasourceBackedAction` was written to separate a project datasource action
 * from a built-in, on the reasoning that only the former has a datasource. That is
 * FALSE on this platform: Kuika ships its built-ins AS action definitions with a
 * datasource id (`NAVIGATE` = `3260278d-…`), so the guard answers "backed" for
 * them and every built-in parameter — none of which carries `isOptionalParameter`
 * — reads as required-and-unbound.
 *
 * Measured cost (session `96b3c45f-…`, request `chat_1787883081008_y5hntx`): every
 * NAVIGATE attach in a six-screen build came back claiming nine unbound required
 * parameters (`fromScreenId, toScreenId, toUrl, navigationKuikaId, title, placemet,
 * closable, width, height`), each one carrying the "code generation will block
 * with KV1170" warning into the Designer's own context — and the deterministic
 * unwire, which trusts this list, then removed eight working navigations.
 *
 * The name is the discriminator because it is the one thing a built-in cannot
 * share with a project action: the platform owns the verb set.
 */
function isBuiltinActionIdentity(action: unknown, actionDef: unknown): boolean {
  const candidates: unknown[] = [
    (action as { actionDefinitionId?: unknown } | null | undefined)?.actionDefinitionId,
    (action as { label?: unknown } | null | undefined)?.label,
    (actionDef as { name?: unknown } | null | undefined)?.name,
    (actionDef as { displayName?: unknown } | null | undefined)?.displayName
  ];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const folded: string = candidate.trim().toLowerCase().replace(/\s+/g, "");
    if (folded.length > 0 && BUILTIN_ACTION_IDENTITIES.has(folded)) return true;
  }
  return false;
}

export function collectUnboundRequiredParameters(action: unknown, actionDef?: unknown): string[] {
  // POSITIVE EVIDENCE ONLY, and `isOptionalParameter` is not it.
  //
  // The flag is absent on platform built-ins, so `!== true` read every one of
  // them as required: a NAVIGATE came back owing `toUrl`, `title`, `placemet`,
  // `closable`, `width`, `height` (live `83c81647`). That is absence of evidence
  // treated as evidence — the exact move this programme refuses everywhere else.
  //
  // The platform rule that IS positive: every declared `@param` on a project
  // datasource action is required (see `SignatureAmendment`'s doc — "a parameter
  // nobody fills is a new KV1170"). A built-in's optionality lives in platform
  // metadata this code cannot read, and its own defaulting fills what it needs.
  // So the question is only asked where the answer is known.
  // A built-in verb first: its definition IS datasource-backed on this platform,
  // so the check below cannot keep it out. See `isBuiltinActionIdentity`.
  if (isBuiltinActionIdentity(action, actionDef)) return [];
  if (!isDatasourceBackedAction(actionDef)) return [];
  const parameters: unknown = (action as ParameterCarryingAction | null | undefined)?.parameters;
  if (!Array.isArray(parameters)) return [];
  const unbound: string[] = [];
  for (const param of parameters) {
    if (param === null || typeof param !== "object") continue;
    const candidate: RequiredParamCandidate = param as RequiredParamCandidate;
    const name: string = typeof candidate.name === "string" ? candidate.name : "";
    if (name.length === 0) continue;
    if (candidate.isOptionalParameter === true) continue;
    if (isMeaningfullyBoundParameter(param)) continue;
    unbound.push(name);
  }
  return unbound;
}

/**
 * Apply proactive semantic defaults to an action's parameters. Idempotent
 * and conservative: only touches parameters whose symbol is missing or
 * still on the placeholder shape. Called from `resolveAndCreateAction`
 * BEFORE `ensureParameterSymbols` so the helper either lands a real
 * default-typed symbol (success) or leaves the parameter untouched and
 * lets the placeholder fallback fire (failure / unrecognized param).
 *
 * Exported solely for direct unit testing — the full add_to_event flow
 * is mock-heavy, so the targeted PK / user-FK behavior tests bypass
 * the surrounding handlers and call this helper directly.
 */
export function __testOnly_resolveParameterSymbol(override: any, sd: any, action?: any, parameter?: any): any | null {
  return resolveParameterSymbol(override, sd, action, parameter);
}

/**
 * KOA-18136 — exported for the row-context enrichment test. The add_to_event
 * handler around it is mock-heavy, but the parameter-override lane inside is
 * exactly where a row-scoped `current.<field>` must gain its `diId`, so the test
 * calls this directly with a host id instead of driving the whole tool.
 */
export function __testOnly_resolveAndCreateAction(
  sd: any,
  allDefs: any[],
  actionSpec: ResolveAndCreateActionSpec
): { action: any; actionId: string; droppedParameters?: string[] } | { error: string } {
  return resolveAndCreateAction(sd, allDefs, actionSpec);
}

export function __testOnly_applySemanticParameterDefaults(action: any, actionDef: any): void {
  applySemanticParameterDefaults(action, actionDef);
}

export function __testOnly_collectUnboundRequiredParameters(action: unknown, actionDef?: unknown): string[] {
  return collectUnboundRequiredParameters(action, actionDef);
}

export function __testOnly_collectActionsFromDisplayItems(displayItems: any[]): unknown[] {
  return collectActionsFromDisplayItems(displayItems);
}

/** Test-only export — boolean/switch parameter default-symbol safety net. */
export function __testOnly_ensureParameterSymbols(action: any): void {
  ensureParameterSymbols(action);
}

/** Test-only export — Fix #18 parameter-name tolerance lookup. */
export function __testOnly_validateRowScopeBindingShape(binding: any): string | null {
  return validateRowScopeBindingShape(binding);
}

export function __testOnly_findParameterWithTolerance(parameters: any[], parameterName: string): any | undefined {
  return findParameterWithTolerance(parameters, parameterName);
}

/** Test-only export — Fix #20 mutation-on-pageInit guard. */
export function __testOnly_checkMutationOnPageInit(
  actionName: string,
  hostScope: PageInitMutationHostScope,
  statement?: string
): { success: false; error: string } | null {
  return checkMutationOnPageInit(actionName, hostScope, statement);
}

/** Test-only export — Fix #74 NAVIGATE-on-pageInit guard. */
export function __testOnly_checkNavigateOnPageInit(
  actionName: string,
  hostScope: PageInitMutationHostScope
): { success: false; error: string } | null {
  return checkNavigateOnPageInit(actionName, hostScope);
}

/**
 * KOA-18193 — build a platform-default symbol through the CANONICAL factory.
 *
 * `applySemanticParameterDefaults` used to hand-write these objects, and the
 * hand-written shape diverged from `buildDesignerSymbol` in exactly the place
 * that matters to the user: for the current-user family the factory persists the
 * human LABEL ("Current Userid") as `defaultValueName`, because that is what the
 * symbol PICKER writes and therefore what the property panel matches. A
 * hand-built `defaultValueName: "CurrentUserid"` still generates correctly — both
 * spellings resolve at codegen — but the panel does not recognise it, so a
 * binding that exists in the tree reads as UNBOUND to the person looking at it
 * (live `2f54a5fb`: "ui da bağlı görünmüyor").
 *
 * The factory also fills `currentType` / `description`, which the hand-written
 * shape left empty. One authority, and an AI-authored binding byte-identical to
 * a hand-made one.
 *
 * Returns null when the factory refuses; the caller then leaves the parameter to
 * the placeholder fallback exactly as before.
 */
function buildPlatformDefaultSymbol(defaultName: string): any | null {
  const built = buildDesignerSymbol({ bindingType: "default", defaultName } as never, getSdInstance());
  if (built !== null && typeof built === "object" && "error" in built) return null;
  return built;
}

function applySemanticParameterDefaults(action: any, actionDef: any): void {
  if (!Array.isArray(action.parameters)) return;
  const defName =
    typeof actionDef?.name === "string"
      ? actionDef.name
      : typeof actionDef?.displayName === "string"
        ? actionDef.displayName
        : "";
  // KOA-18227 — an insert mints its own primary key. Which action inserts comes
  // from the SQL; the name suffix is only the fallback for definitions that
  // carry no statement (built-ins, Swagger, SAP, C#).
  const insertShape: SqlMutationShape | null = classifySqlMutationShape(
    typeof actionDef?.statement === "string" ? actionDef.statement : undefined
  );
  const isInsert = insertShape !== null ? insertShape === "insert" : INSERT_ACTION_NAME_PATTERN.test(defName);
  const isNavigate = defName === "NAVIGATE";
  for (const param of action.parameters) {
    const paramName = typeof param?.name === "string" ? param.name : "";
    if (!paramName) continue;
    if (isMeaningfullyBoundParameter(param)) continue;

    // KOA-17797 (AC-02) — NAVIGATE `openAs` → default "currentPage" when the
    // caller omitted it. Parity with action-panel.tsx / ai-screen-generation-
    // helper.ts, the two non-MCP sites that already inject it. Omitting it
    // leaves fixedValue:"" → the backend's Symbol.IsEmpty() flags KV1012 at
    // codegen (the AC-02 defect). Only fires on the unset param (a real
    // openAs value was already skipped by isMeaningfullyBoundParameter).
    if (isNavigate && paramName.toLowerCase() === "openas") {
      param.symbol = {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue: "currentPage",
        description: ""
      };
      continue;
    }

    // PK on Insert → default:NewGuid. Other PK contexts (Update,
    // Delete, row-scope) are handled by Binder skills + post-gates.
    if (isInsert && PK_PARAM_NORMALIZED_NAMES.has(normalizeIdentifierForParamMatch(paramName))) {
      const built = buildPlatformDefaultSymbol("NewGuid");
      if (built !== null) {
        param.symbol = built;
        continue;
      }
    }

    // User-FK params → default:CurrentUserid. Applies on any mutation
    // (Insert / Update / Save), not just Insert — Update'lerin
    // `UpdatedByUserId` kolonu da CurrentUserid'e bağlanır.
    // Platform ownership FK (KUser_Id) — same pin as server current-user-defaults.
    //
    // NOT on a read. The comment above always said "on any mutation"; the code
    // said "on anything", and a read's user filter is about a subject the
    // caller supplies, not about the viewer (see `isReadActionName`). Leaving
    // the parameter untouched is the correct outcome there: it stays visibly
    // open, so the downstream repair offers it to the real sources — the
    // process's own requester id among them — instead of silently accepting a
    // wrong one that no later pass can tell from a deliberate choice.
    const normParam: string = paramName.toLowerCase().replace(/[^a-z0-9]/g, "");
    const isPlatformKUserOwnership: boolean = normParam === "kuserid" || normParam.endsWith("kuserid");
    // The canonical factory builds the symbol (one spelling of what the panel recognises), and the
    // READ exclusion stays: a read action must not be stamped with the session user.
    if (!isReadActionName(defName) && (isPlatformKUserOwnership || USER_FK_PARAM_PATTERN.test(paramName))) {
      const built = buildPlatformDefaultSymbol("CurrentUserid");
      if (built !== null) {
        param.symbol = built;
        continue;
      }
    }

    // Enum params → seed a valid Fixed value instead of leaving an empty
    // placeholder that codegen serializes to `null` (a REST call like
    // findPetsByStatus(null)). The symbol picker offers exactly these enum
    // members under "Fixed Value"; there is no null option, so an unbound
    // required enum is always wrong. Multi-enum (Many) → ALL members (a
    // no-filter "list everything" default; codegen splits the comma list and
    // Enum.Parse's each). Single enum → the first member, but only when the
    // param is REQUIRED — an optional single enum may legitimately stay unset.
    const enumValues: string[] = Array.isArray(param.enumTypeValues)
      ? param.enumTypeValues.filter((v: unknown): v is string => typeof v === "string" && v.length > 0)
      : [];
    if (param.isEnum === true && enumValues.length > 0) {
      let enumFixedValue: string | undefined;
      if (param.cardinality === ECardinality.Many) {
        enumFixedValue = enumValues.join(",");
      } else if (param.isOptionalParameter !== true) {
        enumFixedValue = enumValues[0];
      }
      if (enumFixedValue !== undefined) {
        param.symbol = {
          category: ESymbolCategory.fixed,
          isFixed: true,
          label: "Fixed Value",
          fixedValue: enumFixedValue,
          description: ""
        };
        continue;
      }
    }
  }
}

/**
 * A boolean parameter's symbol is "unset" — and so safe to default to
 * `false` — when it is missing entirely or is a fixed placeholder with no
 * concrete value (`""` / null / undefined). A real binding (`componentValue`,
 * `default`, `current`, `screenInput`, …) or an explicit `true`/`false`
 * fixed value is left untouched.
 */
function isUnsetBooleanSymbol(symbol: any): boolean {
  if (!symbol) return true;
  if (symbol.category !== undefined && symbol.category !== ESymbolCategory.fixed) return false;
  const v: unknown = symbol.fixedValue;
  return v === undefined || v === null || v === "";
}

/**
 * Ensure every action parameter has a valid symbol structure.
 * Parameters without symbols cause downstream issues — binding/update tools can't
 * read or modify them. Generate type-appropriate defaults for unset parameters.
 */
function ensureParameterSymbols(action: any): void {
  if (!Array.isArray(action.parameters)) return;

  for (const param of action.parameters) {
    // Boolean / switch parameters must never be left empty. A `bit` column
    // surfaces its type in `Typename` (capital T — the IActionParameter
    // field), but the generic default below reads only the lowercase
    // `typeName`/`type` slots, so a switch param was never recognised as
    // boolean and got stamped with `fixedValue: ""`. An empty string is not a
    // valid boolean and fails the backend's `Symbol.IsEmpty()` check (KV1012)
    // at codegen — the correct default is `false`. Handle it up front, across
    // every field casing, and treat an already-seeded empty-string placeholder
    // ("") as overwritable (the generic path below would `continue` past it).
    if (
      isBooleanActionParameterType(param.Typename ?? param.typeName ?? param.type) &&
      isUnsetBooleanSymbol(param.symbol)
    ) {
      param.symbol = {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue: false,
        description: ""
      };
      continue;
    }

    if (param.symbol?.fixedValue !== undefined && param.symbol?.fixedValue !== null) continue;
    if (param.symbol?.category !== undefined && param.symbol?.category !== ESymbolCategory.fixed) continue;

    // Generate type-appropriate default
    const typeName = (param.typeName || param.type || "").toLowerCase();
    let defaultValue: any = "";

    if (typeName === "guid" || typeName === "uniqueidentifier") {
      defaultValue = Guid.create().toString();
    } else if (typeName === "boolean" || typeName === "bool" || typeName === "bit") {
      defaultValue = false;
    } else if (
      typeName === "int32" ||
      typeName === "int64" ||
      typeName === "decimal" ||
      typeName === "double" ||
      typeName === "float"
    ) {
      defaultValue = 0;
    }
    // String, Date, DateTime → empty string is fine as placeholder

    param.symbol = {
      category: ESymbolCategory.fixed,
      isFixed: true,
      label: "Fixed Value",
      fixedValue: defaultValue,
      description: ""
    };
  }
}

/** Empty GUID sentinel — semantically "no record selected", and a non-empty
 *  string so the backend's `Symbol.IsEmpty()` (which checks
 *  `IsNullOrWhiteSpace`) treats it as set. A form's pageInit GetById against
 *  this key returns no row, which is exactly the new-record path. */
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/**
 * Type-appropriate NON-EMPTY placeholder for an unbound NAVIGATE target
 * screen-input (KOA-17394 Sorun 2). The backend rejects an empty target
 * param (KV1012) regardless of optionality, so every type must resolve to a
 * value that `Symbol.IsEmpty()` accepts as set:
 *   - Guid → empty GUID ("no record" → new-record mode)
 *   - bool → false, numerics → 0 (both stringify non-blank)
 *   - String / Date / DateTime → empty string: there is no honest non-empty
 *     sentinel, and a fabricated one would be worse than surfacing the
 *     planner gap. These are vanishingly rare as navigate targets (form
 *     screen-inputs are overwhelmingly the Guid PK).
 */
export function placeholderFixedValueForType(typeName: unknown): string | number | boolean {
  const t = String(typeName ?? "").toLowerCase();
  if (t === "guid" || t === "uniqueidentifier") return EMPTY_GUID;
  if (t === "boolean" || t === "bool" || t === "bit") return false;
  if (t === "int32" || t === "int64" || t === "decimal" || t === "double" || t === "float") return 0;
  return "";
}

/**
 * Populate targetScreenInputParams on a NAVIGATE action.
 * Reads the target screen's publicMetaData.screenInputs to build the param array,
 * then applies LLM-provided overrides (screenInputs) for value binding.
 */
interface ExplicitNavScreenInputs {
  /** The TARGET screen's declared inputs, read FRESH by the caller (bypasses
   *  the stale `screenList` cache). When provided, the screenList lookup is
   *  skipped entirely. */
  readonly targetScreenInputs?: readonly {
    name?: unknown;
    type?: unknown;
    typeName?: unknown;
    cardinality?: unknown;
  }[];
  /** The SOURCE screen's declared inputs, read FRESH by the caller. Used for
   *  source-context pass-through in place of `resolveSourceScreenInputs(sd)`. */
  readonly sourceScreenInputs?: readonly { name?: unknown }[];
  /**
   * KOA-18173 — the caller READ the target back and it genuinely declares no inputs.
   *
   * An empty `targetScreenInputs` is otherwise ambiguous: it is equally the signature
   * of a failed read or of an input creation refused upstream, which is why clearing
   * real bindings on it is refused. Only a caller that verified the emptiness may
   * assert this — the screen-input prune, having deleted every input on the screen and
   * re-read it. Without the assertion a pruned screen would keep navigate params
   * pointing at inputs that no longer exist, trading KV1017 for its mirror.
   */
  readonly targetInputsVerifiedEmpty?: boolean;
}

/** A target screen-input param symbol counts as a re-derivable PLACEHOLDER
 *  (not a real binding to preserve) when it is a Fixed symbol holding a
 *  type placeholder value (empty-GUID / 0 / false / empty string). Any other
 *  category (current / screenInput / default / displayItemProp / …) is a real
 *  binding the reconciliation MUST keep. */
function isPlaceholderNavSymbol(symbol: any): boolean {
  if (!symbol) return true;
  if (symbol.category !== ESymbolCategory.fixed) return false;
  const v: unknown = symbol.fixedValue;
  return v === "" || v === EMPTY_GUID || v === 0 || v === "0" || v === false || v === undefined || v === null;
}

/**
 * KOA-17717 — Resolve the kuikaId of the component an action instance is
 * attached to, by id, walking the screen's event chains (incl. if/then/else
 * branches via `collectActionsFromDisplayItems`). Used to find a row-navigate's
 * host button so a `current.<field>` screen-input can be typed against the
 * enclosing repeater's datasource. Returns undefined when the action isn't found.
 */
function findActionHostKuikaIdById(sd: any, actionId: string | undefined): string | undefined {
  if (!actionId) return undefined;
  const screenDesign = sd?.screenProvider?.getScreenDesign?.();
  if (!screenDesign?.displayItems) return undefined;
  const allActions = collectActionsFromDisplayItems(screenDesign.displayItems);
  const hit = allActions.find((a: { actionId?: string; componentKuikaId?: string }) => a.actionId === actionId);
  return hit?.componentKuikaId ? String(hit.componentKuikaId) : undefined;
}

function applyTargetScreenInputParams(
  action: any,
  sd: any,
  screenInputOverrides?: any,
  explicit?: ExplicitNavScreenInputs,
  // KOA-17717 — explicit host (the component the navigate is attached to). When
  // the navigate is being ADDED (add_to_event), the action isn't on the screen
  // tree yet, so `findActionHostKuikaIdById` can't find it. The caller knows the
  // host kuikaId; pass it so a `current.<field>` screen-input gets its row
  // context (diId + real type). Falls back to the by-id lookup for persisted
  // callers that don't pass it.
  hostKuikaIdOverride?: string | number | null
): string[] {
  // Overrides refused for lack of row context — returned so the caller can report
  // them instead of the navigate silently opening its target without the value.
  const droppedRowScopeInputs: string[] = [];
  // Resolve the TARGET screen's declared inputs. Prefer the caller-supplied
  // FRESH list (KOA-17394 reconciliation — bypasses the stale `screenList`
  // cache that drops cross-screen-created inputs); otherwise fall back to the
  // screenList lookup keyed by the navigate's toScreenId.
  let targetScreenInputs: readonly { name?: unknown; type?: unknown; typeName?: unknown; cardinality?: unknown }[];
  if (explicit?.targetScreenInputs !== undefined) {
    targetScreenInputs = explicit.targetScreenInputs;
  } else {
    const toScreenIdParam = findDesignerActionParameter(action.parameters, "toScreenId");
    const targetScreenId = toScreenIdParam?.symbol?.fixedValue;
    if (!targetScreenId) return droppedRowScopeInputs;
    const screenList = sd.screenProvider?.state?.screenList;
    if (!Array.isArray(screenList)) return droppedRowScopeInputs;
    const targetScreen = screenList.find((s: any) => s.id === targetScreenId);
    const fromCache = targetScreen?.publicMetaData?.screenInputs;
    if (!Array.isArray(fromCache)) return droppedRowScopeInputs;
    targetScreenInputs = fromCache;
  }
  if (targetScreenInputs.length === 0) {
    // No declared inputs → nothing to populate (a navigate to a list screen).
    // In the explicit reconciliation path, clear any stale params; in the
    // attach-time cache path, leave the action untouched (legacy behavior).
    //
    // KOA-18173 — but an EMPTY fresh read is not proof the target has no inputs.
    // It is equally the signature of a read that failed, or of a screen whose input
    // creation was refused upstream. Clearing on that evidence destroys a binding
    // the caller had already established: on `chat_1786432954662_qgkxex` the
    // reconcile pass emptied six navigates and reported
    // `navigatesRepopulated: 6, failureCount: 0`, while the canvas showed
    // `targetScreenInputParams: []` on navigates whose target DID declare `Id`.
    //
    // So the wipe now requires agreement from both sides: clear only when the
    // action itself holds nothing worth keeping (every param still on a
    // re-derivable placeholder). A real binding outranks an empty read, and the
    // refusal is reported so the pass can count it as a failure instead of a win.
    if (explicit?.targetScreenInputs !== undefined) {
      const existing: readonly { name?: unknown; symbol?: unknown }[] = Array.isArray(action.targetScreenInputParams)
        ? action.targetScreenInputParams
        : [];
      const realBindings: readonly { name?: unknown; symbol?: unknown }[] = existing.filter(
        (p: { name?: unknown; symbol?: unknown }): boolean => !isPlaceholderNavSymbol(p.symbol)
      );
      // A caller that re-read the target and found it genuinely empty overrides the
      // refusal: keeping params for inputs that no longer exist is the worse outcome.
      if (realBindings.length > 0 && explicit.targetInputsVerifiedEmpty !== true) {
        droppedRowScopeInputs.push(
          `targetScreenInputParams kept: refused to clear ${realBindings.length} bound target input(s) ` +
            `(${realBindings.map((p) => String(p.name)).join(", ")}) against an empty target-input read`
        );
        return droppedRowScopeInputs;
      }
      action.targetScreenInputParams = [];
    }
    return droppedRowScopeInputs;
  }

  // Build targetScreenInputParams from screen metadata.
  //
  // KOA-17394 (Sorun 2 — new-record navigate). Backend CheckNavigationAction
  // requires EVERY target ScreenInput to have a TargetScreenInputParam whose
  // Symbol is non-empty (missing → KV1017, empty → KV1012; neither check
  // honours any optional flag). A navigate the planner left UNBOUND — e.g. a
  // "Yeni Proje"/"Yeni Görev" button reaching a dual-purpose form whose `Id`
  // screen-input exists only because the sibling "Düzenle" navigate binds it
  // — used to land here with `fixedValue: ""`, which IsEmpty()=true → KV1012
  // blocks code generation. Seed each target input with a type-appropriate
  // NON-EMPTY placeholder so an unbound navigate is codegen-safe and opens the
  // form in new-record mode (GetById on an empty/zero key returns no row).
  // Bound inputs are replaced by their real binding in the override pass below.
  const builtParams = targetScreenInputs.map((si: any) => ({
    name: si.name,
    type: si.type || si.typeName || "String",
    typeName: si.typeName || si.type || "String",
    cardinality: si.cardinality ?? 0,
    symbol: {
      category: ESymbolCategory.fixed,
      isFixed: true,
      fixedValue: placeholderFixedValueForType(si.typeName || si.type)
    }
  }));

  // Apply overrides from the navigate's screen-input bindings.
  //
  // KOA-17394 (screen-input correctness, Fix B). A row "Düzenle" navigate must
  // pass the row's id INTO the target form's `Id` input (edit mode) rather than
  // leaving the empty-GUID placeholder (new-record). The Wirer routes the row
  // paramBinding as `{ name: "Id", value: "current.Id" }`. Resolve each
  // override through the SHARED `resolveParameterSymbol` so a screen-input value
  // gets the SAME treatment as an inline action parameter: `current.<field>` →
  // Current symbol, `default.<name>` / `screenInput.<name>` → their canonical
  // symbols, a `bindingType` payload → buildDesignerSymbol, a plain literal →
  // Fixed. Overrides with neither value nor binding keep the placeholder.
  const boundNames: Set<string> = new Set<string>();

  // KOA-17394 (reconciliation, merge-idempotent) — preserve any EXISTING real
  // binding on the action's current targetScreenInputParams. A row "Düzenle"
  // navigate already had `Id → current.Id` set correctly at attach time; a
  // re-population pass must keep it and only (re)fill placeholders. Only Fixed
  // placeholder symbols are eligible for re-derivation (see isPlaceholderNavSymbol).
  const existingParams: readonly { name?: unknown; symbol?: unknown }[] = Array.isArray(action.targetScreenInputParams)
    ? action.targetScreenInputParams
    : [];
  const existingRealBindingByName: Map<string, unknown> = new Map<string, unknown>();
  for (const ep of existingParams) {
    if (typeof ep?.name !== "string") continue;
    if (isPlaceholderNavSymbol(ep.symbol)) continue;
    existingRealBindingByName.set(ep.name.toLowerCase(), ep.symbol);
  }
  if (existingRealBindingByName.size > 0) {
    for (const paramEntry of builtParams) {
      const preserved = existingRealBindingByName.get(paramEntry.name.toLowerCase());
      if (preserved !== undefined) {
        paramEntry.symbol = preserved as never;
        boundNames.add(paramEntry.name.toLowerCase());
      }
    }
  }

  // KOA-17717 — the component this navigate is attached to (a row "Düzenle"
  // button for a row-navigate). Needed to enrich a `current.<field>` screen-input
  // override with the real row-context column type (Guid/Int/…) from the
  // enclosing repeater's datasource, instead of the hardcoded "String" the
  // promotion emits. Resolved once; undefined → enrichment is a safe no-op.
  const navigateHostKuikaId: string | undefined =
    hostKuikaIdOverride != null ? String(hostKuikaIdOverride) : findActionHostKuikaIdById(sd, action?.id);
  const overrides = safeParse(screenInputOverrides);
  if (Array.isArray(overrides)) {
    for (const override of overrides) {
      const overrideName: string | undefined = override?.name ?? override?.parameterName;
      const paramEntry = builtParams.find((p: any) => p.name === overrideName);
      if (!paramEntry) continue;
      // A navigate's screen-input override may only read row context when the
      // navigate's HOST button actually sits in a repeater row. A screen-level
      // button on a form has no row, so `current.X` would hand the target screen
      // an empty value (codegen emits null) — the exact defect behind a masraf
      // build's `btn-nav-MasrafDetayi` carrying `{ExpenseId: current.Id}` on a
      // create form. Skip it and leave the target input on its placeholder rather
      // than pretending the row id was passed.
      if (/^current[.:]/.test(String(override?.value ?? "")) && !hostHasRowContext(sd, navigateHostKuikaId)) {
        console.warn(
          `[Navigate/screenInputs] dropped "${overrideName}=${override.value}" — host ${String(navigateHostKuikaId)} is not inside a data repeater, so there is no row context`
        );
        droppedRowScopeInputs.push(`screenInputs.${overrideName} (${override.value} needs a repeater row)`);
        continue;
      }
      const symbol = resolveParameterSymbol(override, sd);
      if (symbol) {
        const paramDeclaredType: string | undefined =
          typeof paramEntry.type === "string" ? paramEntry.type : paramEntry.typeName;
        paramEntry.symbol = enrichCurrentRowSymbol(symbol, sd, navigateHostKuikaId, paramDeclaredType) as never;
        boundNames.add(paramEntry.name.toLowerCase());
      }
    }
  }

  // KOA-17394 (screen-input correctness) — source-context pass-through.
  //
  // This is the SINGLE deterministic rule that unifies the row and trigger
  // navigate paths. The Wirer only knows row-context bindings it can derive
  // from the brief (the `current.X` overrides above); a screen-top navigate
  // ("Yeni Görev" on GorevlerListesi, or a form's "Kaydet → back to list")
  // carries no override, yet the target often declares a parent-context key
  // (e.g. `ProjeId`) that the SOURCE screen itself received as a screen input.
  // Leaving it at the empty-GUID placeholder silently drops the parent
  // association (a new task with no project; a list reopened with no filter).
  //
  // Only the designer holds both screens' declared inputs, so this structural
  // pass-through belongs here — not in the Wirer, which would have to
  // reconstruct the declarations from the brief (the brittle, planner-fidelity
  // dependence that caused prior regressions). For every target input still at
  // its placeholder, if the source screen declares an input of the same name,
  // forward it as `screenInput.<name>` (ESymbolCategory.screenInput → codegen
  // `this.props.screenInputs.<name>`). Explicit overrides win; the form's own
  // PK (`Id`, which the source list does not declare) keeps the new-record
  // placeholder. Locale-independent name match (`toLowerCase`, ASCII
  // identifiers) — culture-invariant per the platform's transliteration.
  const sourceInputs: readonly { name?: unknown }[] =
    explicit?.sourceScreenInputs !== undefined ? explicit.sourceScreenInputs : resolveSourceScreenInputs(sd);
  if (sourceInputs.length > 0) {
    for (const paramEntry of builtParams) {
      if (boundNames.has(paramEntry.name.toLowerCase())) continue;
      const targetKey: string = String(paramEntry.name).toLowerCase();
      const sourceMatch = sourceInputs.find(
        (si) => typeof si?.name === "string" && si.name.toLowerCase() === targetKey
      );
      if (!sourceMatch || typeof sourceMatch.name !== "string") continue;
      const symbol = resolveParameterSymbol({ name: paramEntry.name, value: `screenInput.${sourceMatch.name}` }, sd);
      if (symbol) paramEntry.symbol = symbol;
    }
  }

  action.targetScreenInputParams = builtParams;
  return droppedRowScopeInputs;
}

/**
 * Resolve the SOURCE (currently-designed) screen's declared screen inputs.
 * The navigate is attached on this screen; its inputs are the candidates for
 * source-context pass-through. Prefer the live `getScreenDesign()` (freshest
 * declarations) and fall back to the matching `screenList` entry so the shape
 * matches how the target screen's inputs are read. Returns [] when unknown.
 */
function resolveSourceScreenInputs(sd: any): readonly { name?: unknown }[] {
  const provider = sd?.screenProvider;
  const live = typeof provider?.getScreenDesign === "function" ? provider.getScreenDesign() : undefined;
  const currentScreenId: string = live?.id ?? provider?.state?.sdId ?? "";
  if (currentScreenId.length === 0) return [];

  // Ask the provider's single reader (screen-provider `resolvePublicMetaData`): the OPEN screen
  // answers from its own authoritative metadata — the copy the setters patch — and it already
  // falls back to the project list itself.
  //
  // Do NOT read `live.publicMetaData` directly. It holds the LOAD-TIME blob: the field rides on
  // the tree only as transport from the single-screen GET into the provider's side-channel
  // (KOA-18173 follow-up) and is never patched afterwards. An input the user added in this
  // session would be invisible here, so the target's same-named parameter would stay at its
  // empty-GUID placeholder — KOA-17394's KV1017 symptom, reintroduced from the opposite side.
  if (typeof provider?.getScreenInputsByScreenId === "function") {
    const inputs = provider.getScreenInputsByScreenId(currentScreenId);
    if (Array.isArray(inputs)) return inputs;
  }

  const screenList = provider?.state?.screenList;
  if (Array.isArray(screenList)) {
    const source = screenList.find((s: any) => s.id === currentScreenId);
    const sourceInputs = source?.publicMetaData?.screenInputs;
    if (Array.isArray(sourceInputs)) return sourceInputs;
  }
  return [];
}

/**
 * Exported solely for direct unit testing — the full add_to_event flow is
 * mock-heavy, so the targeted screen-input override tests call this directly.
 */
/**
 * KOA-18173 — a NAVIGATE that cannot carry the value it was asked to carry is not a
 * navigate. Returns the refusal text, or null when the attach may proceed.
 *
 * Reporting the drop while still returning `success: true` (the previous behavior)
 * shipped a button that opens the detail screen with no record: on
 * `chat_1786432954662_qgkxex` the Wirer sent
 * `screenInputs: [{name: "WorkOrderId", value: "current.Id"}]`, the designer answered
 * `{"success": true, "droppedParameters": ["screenInputs.WorkOrderId"]}`, nothing
 * downstream read `droppedParameters`, and Preview blocked on KV1017.
 *
 * Scope is narrow on purpose: ONLY a screen input the caller explicitly ASKED to pass
 * that did not land — the target does not declare it, or the host has no row context
 * (both are reported with the `screenInputs.` prefix). A navigate supplying no
 * `screenInputs` is untouched: a "New" button legitimately opens its target with
 * nothing, and the KOA-17394 non-empty placeholders keep that codegen-safe. An
 * ordinary dropped parameter is likewise untouched — it is reported, not fatal.
 */
/**
 * KOA-18173 — an action whose identity comes from the row cannot be attached outside
 * the repeater that supplies it. Returns the refusal text, or null to proceed.
 *
 * `hostHasRowContext` already detects this; the consequence was wrong. It dropped the
 * `current.<field>` parameter and KEPT the button, producing a control that is
 * unfillable by construction — every later gate then saw an attached action with an
 * unset required parameter and no way to fill it.
 *
 * Read off a live canvas (`PurchaseOrderDetail`): `row-UpdateOrderLine`, inside
 * `Table:V_ORDER_LINES`, had `Id` bound from row context, while
 * `btn-UpdateOrderLine` in the panel beside the table had `Id` unset — same action,
 * two buttons, one of which could never work. `btn-DeleteOrderLine` the same. Those
 * orphans accounted for three of that screen's seventeen validation errors, and are
 * what makes the pair look like a duplicated button.
 *
 * The row-context drops are reported with a "needs a repeater row" reason by both the
 * inline-parameter loop and the navigate screen-input path, which is what this reads.
 */
export function rowScopeUnavailableError(droppedParameters: readonly string[], actionLabel: string): string | null {
  const rowScoped: string[] = droppedParameters.filter((d: string): boolean => d.includes("needs a repeater row"));
  if (rowScoped.length === 0) return null;
  return (
    `[VALIDATION] "${actionLabel}" needs row context for ${rowScoped.length} parameter(s): ` +
    `${rowScoped.join(", ")} — but the component this is being attached to is not inside a data ` +
    `repeater (Table/GalleryView/FlexGrid row template), so there is no row to read them from. ` +
    `Attach this action to a button INSIDE the row template, or bind the parameter(s) from a ` +
    `screen input / form field instead of \`current.<field>\`.`
  );
}

export function navigateUnlandedScreenInputError(
  droppedParameters: readonly string[],
  declaredTargetInputNames: readonly string[]
): string | null {
  const unlanded: string[] = droppedParameters.filter((d: string): boolean => d.startsWith("screenInputs."));
  if (unlanded.length === 0) return null;
  const declaredText: string = declaredTargetInputNames.length > 0 ? declaredTargetInputNames.join(", ") : "(none)";
  return (
    `[VALIDATION] NAVIGATE could not carry ${unlanded.length} requested screen input(s): ` +
    `${unlanded.join(", ")}. The target screen declares: ${declaredText}. ` +
    `A navigate that drops the value it was asked to pass opens its target without the record ` +
    `(backend KV1017/KV1012). Either create the missing screen input on the target screen and ` +
    `re-attach, or attach this NAVIGATE without \`screenInputs\` if it is meant to open a blank/new record.`
  );
}

/** KOA-18173 — returns the refusal list so tests can assert what was reported,
 *  not just what was mutated. A silent refusal is the defect this guards. */
export function __testOnly_applyTargetScreenInputParams(
  action: any,
  sd: any,
  screenInputOverrides?: any,
  explicit?: ExplicitNavScreenInputs
): string[] {
  return applyTargetScreenInputParams(action, sd, screenInputOverrides, explicit);
}

/**
 * KOA-18136 — the host component id to enrich a `current.<field>` symbol against,
 * or undefined when there is none.
 *
 * `enrichCurrentRowSymbol` stamps `diId` whenever `Number(host)` is finite, and
 * `Number("")` is 0 — an empty host would silently claim component 0 as the row
 * owner. Normalizing here keeps the "unresolvable host ⇒ safe no-op" contract.
 */
function rowHostKuikaId(value: string | number | undefined | null): string | undefined {
  if (value === undefined || value === null) return undefined;
  const text: string = String(value).trim();
  return text.length > 0 ? text : undefined;
}

interface ResolveAndCreateActionSpec {
  actionDefinitionId: string;
  parameters?: any;
  fromScreenId?: string;
  screenInputs?: any;
  /** Host event scope (Fix #20). When set, blocks attaching a mutation
   *  action (Insert / Update / Delete / Upsert / Save) to pageInit. */
  hostScope?: PageInitMutationHostScope;
  /** KOA-17717 — the component this action is being attached to. The action
   *  isn't on the screen tree yet at this point, so the NAVIGATE screen-input
   *  population needs the host explicitly to give a `current.<field>` its row
   *  context (diId + real type). */
  hostKuikaId?: string | number;
  /** Opt-in: attach a NAVIGATE that targets the current screen (warning) instead of refusing it. */
  allowSameScreen?: boolean;
}

/**
 * Shared helper that resolves an action definition (by ID or name)
 * and creates an action instance with parameter overrides applied.
 */
/** A def is deprecated if its flag is set OR its Name ends with `_DEPRECATED`. */
export function isDeprecatedActionDef(def: { name?: string; isDeprecated?: boolean }): boolean {
  if (def.isDeprecated === true) return true;
  return (def.name || "").toLowerCase().endsWith("_deprecated");
}

/**
 * KOA-17797 — pick the preferred def among same-name matches. The action catalog
 * can hold multiple defs with the SAME Name (a deprecated variant + its canonical
 * replacement — e.g. two "SendMail"), and the upstream action-list order is
 * non-deterministic (parallel projection over 600+ actions), so a bare `.find`
 * attaches the deprecated one at random. Prefer a NON-deprecated match; fall back
 * to the first match only when EVERY match is deprecated (no replacement exists —
 * attaching it beats failing). Pure — exported for testing.
 */
export function pickPreferredActionDef<T extends { name?: string; isDeprecated?: boolean }>(
  matches: readonly T[]
): T | undefined {
  return matches.find((a: T): boolean => !isDeprecatedActionDef(a)) ?? matches[0];
}

/** A built, not-yet-saved action plus everything the caller must be told about it. */
interface ResolvedActionResult {
  action: any;
  actionId: string;
  /** Overrides that named no parameter on this action's signature. */
  droppedParameters?: string[];
  /** KOA-18193 — required parameters left with no source; a KV1170 in waiting. */
  unboundRequiredParameters?: string[];
  /** Attached, but the NAVIGATE targets the screen it lives on (see `checkNavigateSameScreen`). */
  navigateSameScreenWarning?: string;
}

/** Machine code for "that action does not exist" — the recovery keys on this. */
export const UNKNOWN_ACTION_CODE: string = "unknown_action";

function resolveAndCreateAction(
  sd: any,
  allDefs: any[],
  actionSpec: ResolveAndCreateActionSpec
): ResolvedActionResult | { error: string } {
  const { actionDefinitionId } = actionSpec;

  // Resolve by ID first, then case-insensitive name search
  let actionDef = allDefs.find((a: any) => a.id === actionDefinitionId);
  if (!actionDef) {
    const searchName = actionDefinitionId.toLowerCase().replace(/\s+/g, "");
    const nameMatches = allDefs.filter((a: any) => {
      const name = (a.name || "").toLowerCase().replace(/\s+/g, "");
      const displayName = (a.displayName || "").toLowerCase().replace(/\s+/g, "");
      return name === searchName || displayName === searchName;
    });
    actionDef = pickPreferredActionDef(nameMatches);
  }
  if (!actionDef) {
    // KOA-18193 — carry the CATALOG with the refusal.
    //
    // `allDefs` holds every action the project has, system and datasource
    // alike. Answering with prose threw that away, and the server's rewrite
    // then filled the gap from its own index — which holds SYSTEM actions only.
    // So an invented project action (`SubmitExpenseForApproval`) was answered
    // with `Or` / `SlackGetUserProfile` / `RemoveUserFromRole` on a save button
    // (live `ac73f23f`): the correct name was structurally impossible to
    // suggest, and the suggestions that appeared were an invitation to wire the
    // wrong thing.
    //
    // The names ride along so the recovery can rank against the REAL catalog and
    // ask the user, and so a free-text answer can be checked against something.
    const names: string[] = Array.from(
      new Set(
        allDefs
          .flatMap((a: any): string[] => [a?.name, a?.displayName])
          .filter((n: unknown): n is string => typeof n === "string" && n.trim().length > 0)
      )
    ).sort((a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0));
    return {
      error: JSON.stringify({
        error: true,
        code: UNKNOWN_ACTION_CODE,
        badName: actionDefinitionId,
        message: `Action definition "${actionDefinitionId}" not found on this project.`,
        availableActions: names
      })
    };
  }

  // Fix #20 — reject mutation actions on pageInit.
  if (actionSpec.hostScope) {
    const mutationGuard = checkMutationOnPageInit(
      actionDef.name ?? actionDefinitionId,
      actionSpec.hostScope,
      typeof actionDef.statement === "string" ? actionDef.statement : undefined
    );
    if (mutationGuard) return { error: mutationGuard.error };
    // Fix #74 — reject NAVIGATE on pageInit (screen-killer pattern).
    // Check both the resolved action name and the originally-passed
    // actionDefinitionId so the guard fires whether the LLM passed
    // "NAVIGATE" verbatim or the built-in Guid form.
    const navigateGuardByName = checkNavigateOnPageInit(actionDef.name ?? actionDefinitionId, actionSpec.hostScope);
    if (navigateGuardByName) return { error: navigateGuardByName.error };
    const navigateGuardByDefId = checkNavigateOnPageInit(actionDefinitionId, actionSpec.hostScope);
    if (navigateGuardByDefId) return { error: navigateGuardByDefId.error };
  }

  // Create action instance
  const actionId = Guid.create().toString();
  const isScreenNavigation: boolean = isScreenNavigationDefinition(actionDef);
  const action: any = buildAttachedActionInstance(actionId, actionDef);

  // Apply parameter overrides — collect names of unmatched parameters so
  // the caller can warn the LLM instead of silently dropping them.
  // Historical bug: LLM sends `parameters: [{name: "ScreenName", ...}]`
  // for a NAVIGATE action (whose real param is `toScreenId`), the name
  // doesn't match, the loop `continue`s, and the response is success
  // with the parameter silently gone. Downstream the LLM thinks the
  // value is set and wastes turns trying to bind a non-existent param.
  const droppedParameters: string[] = [];
  const paramOverrides = safeParse(actionSpec.parameters);
  // Fix #10 — NAVIGATE parameter alias / screen-name normalisation. The
  // LLM consistently calls add_to_event for NAVIGATE with
  // `parameters: [{name: "ScreenName", value: "<screen-name>"}]` even
  // though the real parameter is `toScreenId` and its value must be a
  // screen Guid. Without this normalisation the override is silently
  // dropped, NAVIGATE lands with toScreenId="", and at runtime the
  // button does nothing. Mutates `paramOverrides` in place so the
  // dedup / symbol-build path downstream sees the corrected shape.
  normalizeNavigateParameterOverrides(actionDef, paramOverrides, sd);

  // KOA-17797 — custom-panel actions (every *-symbols-group editor) name their
  // TARGET component param differently per verb; the LLM mis-names it and the
  // override is silently dropped (same trap as NAVIGATE above). Read the action's
  // own params and rewrite the common target aliases onto the real one.
  normalizeCustomActionTargetParam(action.parameters, paramOverrides);

  // Fix #26 — NAVIGATE same-screen rejection (opt-out: `allowSameScreen`).
  //
  // BRAID generators occasionally emit `Navigate <ThisScreen>.<anchor>`
  // when they meant the orchestrator's outbound nav target (e.g. the
  // BRAID anchor `NewIdea.save-success` is supposed to be resolved by
  // App-Planner to "go to IdeaList"). The LLM passes that anchor name
  // verbatim as `ScreenName`, the screen-name suffix-strip recovers it
  // to the CURRENT screen, and the result is a button that navigates
  // a screen to itself — an infinite no-op in production.
  // Reject the attachment so the LLM picks a real outbound target — unless the
  // caller explicitly opted in, in which case it lands with a warning.
  const navigateSameScreenMessage: string | null = checkNavigateSameScreen(actionDef, paramOverrides, sd);
  if (navigateSameScreenMessage !== null && actionSpec.allowSameScreen !== true) {
    return { error: navigateSameScreenMessage };
  }
  const navigateSameScreenWarning: string | null = navigateSameScreenMessage;

  // KOA-17797 (AC-02) — reject a NAVIGATE to a fabricated/unresolvable screen
  // (the `scr_11` hallucination) instead of silently creating a dead button.
  const navigateUnknownScreenError = checkNavigateUnknownScreen(actionDef, paramOverrides, sd);
  if (navigateUnknownScreenError) return { error: navigateUnknownScreenError };
  if (Array.isArray(paramOverrides)) {
    for (const override of paramOverrides) {
      const overrideName = normalizeOverrideName(override);
      if (!overrideName) continue;
      // KOA-17797 — use the tolerant finder (case-insensitive + separator-strip +
      // X↔XId) so the add_to_event override loop drops far fewer mis-named params
      // across ALL actions, not just the ones the basic case-insensitive matcher
      // happened to catch.
      const param = findParameterWithTolerance(action.parameters, overrideName);
      if (!param) {
        droppedParameters.push(overrideName);
        continue;
      }

      // Row scope is only real inside a repeater. This is the THIRD door for a
      // `current.X` value (the other two are the binding calls and a navigate's
      // screenInputs) — the row-action projector sends a catalog row action's
      // `paramBindings` inline here, so a row Delete/Update attached to a
      // screen-level button would otherwise land `id=current.Id` and codegen
      // would emit null for it. Refuse and report instead of attaching a
      // parameter that can never resolve.
      if (/^current[.:]/.test(String(override?.value ?? "")) && !hostHasRowContext(sd, actionSpec.hostKuikaId)) {
        console.warn(
          `[add_to_event] dropped "${overrideName}=${override.value}" — host ${String(actionSpec.hostKuikaId)} is not inside a data repeater, so there is no row context`
        );
        droppedParameters.push(`${overrideName} (${override.value} needs a repeater row)`);
        continue;
      }

      const symbol = resolveParameterSymbol(override, sd, action, param);
      if (!symbol) continue;

      // KOA-18136 — a row-scoped ACTION PARAMETER needs the same enrichment the
      // navigate screen-input override above already gets.
      //
      // `resolveParameterSymbol`'s Fix #42 promotion returns a bare
      // `current.<field>` symbol: right category and fieldName, but NO `diId` and a
      // hardcoded "String" type. Codegen resolves the row value THROUGH `diId`
      // (`ESymbolCategory.Current` looks the row component up by DisplayItemId and
      // walks to the enclosing repeater); without it, it emits `this.Null` and the
      // parameter lands EMPTY at runtime — a row button that looks wired and does
      // nothing. That is the very bug KOA-17717 fixed for navigate screen-inputs;
      // the ACTION-parameter lane (every `expected_row_actions` attach the Wirer
      // makes: NavigateToTask ← current.StepKey, a row Get ← current.Id, a row
      // delete ← current.Id) went through this loop and stayed unenriched.
      // Enrichment is a no-op on any non-`current` symbol and on an unresolvable
      // host, so this is safe for every other parameter shape.
      const paramDeclaredType: string | undefined =
        typeof param?.Typename === "string" ? String(param.Typename) : undefined;
      const enriched = enrichCurrentRowSymbol(symbol, sd, rowHostKuikaId(actionSpec.hostKuikaId), paramDeclaredType);

      const nextAction = setDesignerActionParameter(action, param, enriched);
      action.parameters = nextAction.parameters;
    }
  }

  // KOA-18173 — row scope is a PRECONDITION for attaching, not a reason to drop a
  // parameter. Checked here, before any auto-set/default fills in over the gap.
  const rowScopeError: string | null = rowScopeUnavailableError(droppedParameters, action.label);
  if (rowScopeError) return { error: rowScopeError };

  // Auto-inject auto-set parameters
  applyAutoSetParameters(action, sd, actionSpec.fromScreenId);

  // Proactive semantic defaults — bind the two well-defined cases
  // (PK on Insert → NewGuid, user-FK params → CurrentUserid) before
  // the placeholder fallback fires. Mirrors the post-Binder gates in
  // pipeline-completion.ts so the same defaults land regardless of
  // whether those gates run (they don't fire on aborted runs).
  // Runs BEFORE ensureParameterSymbols so the helper's overwritable
  // detection (which treats placeholder fixedValues as eligible) is
  // never given the chance to look at a freshly-installed placeholder
  // and skip it.
  applySemanticParameterDefaults(action, actionDef);

  // Ensure every parameter has a valid symbol structure.
  // Parameters left without a symbol after overrides/auto-set cause downstream issues
  // (binding tools can't read/modify them). Generate appropriate defaults.
  ensureParameterSymbols(action);

  // For NAVIGATE actions, populate targetScreenInputParams from target screen's metadata
  if (isScreenNavigation) {
    droppedParameters.push(
      ...applyTargetScreenInputParams(action, sd, actionSpec.screenInputs, undefined, actionSpec.hostKuikaId)
    );
    // A `screenInputs` entry naming an input the TARGET screen does not declare
    // is dropped inside `applyTargetScreenInputParams` — previously in silence,
    // so the caller believed it had handed the detail screen a row id when it
    // had not. Report it alongside the parameter drops instead of guessing.
    droppedParameters.push(...unmatchedScreenInputNames(actionSpec.screenInputs, action));

    // KOA-18173 — a NAVIGATE that cannot carry the value it was asked to carry is
    // not a navigate. Reporting the drop while still returning `success: true` (the
    // previous behavior) shipped a button that opens the detail screen with no
    // record: on `chat_1786432954662_qgkxex` the Wirer sent
    // `screenInputs: [{name: "WorkOrderId", value: "current.Id"}]`, the designer
    // answered `{"success": true, "droppedParameters": ["screenInputs.WorkOrderId"]}`,
    // and nothing downstream reads `droppedParameters` — so every gate believed the
    // navigate was wired and Preview blocked on KV1017.
    //
    // Refuse instead, and say what would have to change. Scope is narrow on purpose:
    // ONLY a screen-input the caller explicitly ASKED to pass and that did not land
    // (the target does not declare it, or the host has no row context). A navigate
    // that supplies no `screenInputs` is untouched — a "New" button legitimately
    // opens its target with nothing, and the KOA-17394 non-empty placeholders keep
    // that codegen-safe.
    const declaredTargetInputs: string[] = (action.targetScreenInputParams ?? []).map((p: any) =>
      String(p?.name ?? "")
    );
    const unlandedError: string | null = navigateUnlandedScreenInputError(droppedParameters, declaredTargetInputs);
    if (unlandedError) return { error: unlandedError };
  }

  // WS1c companion — StartProcess targets the CURRENT project by default (the
  // "Start External Process" switch is off on add), so its projectId must be the
  // current project id, never empty ("" is classified as an EXTERNAL project by
  // `_checkIsExternalProcess`). The panel fills this via `_fixProjectId`
  // (workflow-start-symbols-group.tsx); the headless attach path has no panel, so
  // replicate it here — AFTER ensureParameterSymbols so it wins over any
  // placeholder. Without it the workflow-INPUT expansion (getResolvedActionParameter
  // → expandWorkflowStartInputParameters) can't resolve the workflow at bind time.
  if (action.label === "StartProcess") {
    applyDefaultStartProcessProjectId(action, getSdInstance() as unknown as ISdWorkflowStartSource);
  }

  // KOA-18193 — read the FINAL parameter state, after every auto-filler and
  // after `ensureParameterSymbols`. Whatever is still required-and-unbound here
  // is the KV1170 this attach just created, and the caller is the only one in a
  // position to fix it while the context is still on the table.
  const unboundRequiredParameters: string[] = collectUnboundRequiredParameters(action, actionDef);

  return {
    action,
    actionId,
    ...(droppedParameters.length > 0 ? { droppedParameters } : {}),
    ...(unboundRequiredParameters.length > 0 ? { unboundRequiredParameters } : {}),
    ...(navigateSameScreenWarning !== null ? { navigateSameScreenWarning } : {})
  };
}

/** Opt-in flags arrive as a boolean or, from some callers, its string form. */
function isExplicitTrue(value: unknown): boolean {
  return value === true || value === "true";
}

/** What applying an attach payload's parameters to an EXISTING action instance achieved. */
interface ExistingActionParameterUpdate {
  applied: string[];
  dropped: string[];
}

/**
 * KOA-18136 — A DUPLICATE ATTACH MUST NOT SWALLOW THE CALLER'S PARAMETERS.
 *
 * The dedup below (both the single and the batched attach) exists so a retrying
 * caller cannot create a SECOND instance of the same action on one event — that
 * part is right and stays. What it also did was discard the parameters the caller
 * brought, in silence, and answer `success: true`. So a caller whose whole purpose
 * was to supply a value had no way to learn that the value never landed.
 *
 * Live 2026-08-17 (session f0d6f51c): the initiator form's submit button got
 * `StartProcess` attached by the wiring lane with an EMPTY parameter payload (that
 * layer does not know the process id). The dedicated process-start pass then
 * attached it again WITH `workflow: <processId>` — and was answered "already exists
 * — skipped duplicate". `workflow` stayed empty, so the process's 15 business
 * INPUT parameters (which are materialized FROM that value) could not be resolved
 * and every one of the 15 binds failed with "parameter not found". The process was
 * built correctly; the submit button simply never learned which process to start.
 *
 * The fix keeps the "never a second instance" law and drops the silence: the
 * parameters are applied to the instance that already exists, through the SAME
 * symbol resolution + row-context guard + persist path the create branch uses, and
 * what landed (or did not) is reported.
 */
function applyParametersToExistingAction(
  sd: any,
  kuikaId: string,
  eventName: string,
  existingActionId: string | undefined,
  parameters: unknown
): ExistingActionParameterUpdate {
  const applied: string[] = [];
  const dropped: string[] = [];
  const overrides: any = safeParse(parameters);
  if (!Array.isArray(overrides) || overrides.length === 0) return { applied, dropped };
  const overrideNames: string[] = overrides.map((o: any): string => normalizeOverrideName(o) ?? "(unnamed)");
  if (!existingActionId) return { applied, dropped: overrideNames };

  const found: any = findContextAction({ kuikaId, eventName, actionId: existingActionId });
  if (!found || "error" in found) return { applied, dropped: overrideNames };

  const action: any = found.action;
  for (const override of overrides) {
    const overrideName: string | undefined = normalizeOverrideName(override);
    if (!overrideName) continue;
    // Same tolerant finder the create branch uses, so a mis-cased / X↔XId name is
    // matched here exactly as it would be on a first attach.
    const param: any = findParameterWithTolerance(action.parameters, overrideName);
    if (!param) {
      dropped.push(overrideName);
      continue;
    }
    const rawValue: string = String(override?.value ?? "");
    if (/^current[.:]/.test(rawValue) && !hostHasRowContext(sd, kuikaId)) {
      dropped.push(`${overrideName} (${rawValue} needs a repeater row)`);
      continue;
    }
    const symbol: any = resolveParameterSymbol(override, sd, action, param);
    if (!symbol) {
      dropped.push(overrideName);
      continue;
    }
    const paramDeclaredType: string | undefined =
      typeof param?.Typename === "string" ? String(param.Typename) : undefined;
    const enriched: any = enrichCurrentRowSymbol(symbol, sd, rowHostKuikaId(kuikaId), paramDeclaredType);
    const nextAction: any = setDesignerActionParameter(action, param, enriched);
    action.parameters = nextAction.parameters;
    applied.push(typeof param?.name === "string" ? param.name : overrideName);
  }

  if (applied.length > 0) persistResolvedAction(found, action);
  return { applied, dropped };
}

/**
 * Fix #39 — resolve an IfThenElse condition `field1` / `field2` shape into a
 * proper `ISymbol`, NOT a blind fixed-value wrapper.
 *
 * Background: the previous implementation wrapped whatever shape the caller
 * passed into `{ category: ESymbolCategory.fixed, fixedValue: <as-is> }`.
 * So `field1: "screenInput.id"` ended up displayed as the LITERAL string
 * `"screenInput.id"` (treated as a fixed value), and `field1: { kuikaId, propertyName }`
 * stringified to `[object Object]` in the condition pill — both of which the
 * May-2026 fikir-havuzu reproducer surfaced ("[object Object] isEmpty").
 *
 * The fix dispatches on the shape:
 *   1. Object with explicit `bindingType` discriminator → forward to
 *      `buildDesignerSymbol` directly.
 *   2. Object with `kuikaId` + `propertyName` → componentValue binding.
 *   3. String like `"screenInput.<name>"` or `"screenInput:<name>"` →
 *      screenInput binding.
 *   4. String / number / boolean primitive without prefix → fixed value
 *      (preserves backwards compat for `field2: "5"`-style literal compares).
 *
 * Errors from `buildDesignerSymbol` are surfaced so the caller sees
 * `inputName is required for 'screenInput' bindingType` etc. instead of a
 * silent fixed-string substitution.
 */
interface ConditionFieldResult {
  symbol?: any;
  error?: string;
}

function buildConditionFieldSymbol(field: unknown, sd: any, fieldLabel: string): ConditionFieldResult {
  if (field === null || field === undefined || field === "") {
    return { error: `${fieldLabel} is required` };
  }

  // Case 1: explicit bindingType discriminator → use canonical factory.
  if (typeof field === "object" && field !== null && "bindingType" in field) {
    const built = buildDesignerSymbol(field as never, sd);
    if ("error" in built) return { error: `${fieldLabel}: ${built.error}` };
    return { symbol: built };
  }

  // Case 2: object with kuikaId + propertyName → componentValue binding.
  if (
    typeof field === "object" &&
    field !== null &&
    "kuikaId" in (field as Record<string, unknown>) &&
    "propertyName" in (field as Record<string, unknown>)
  ) {
    const obj = field as { kuikaId: unknown; propertyName: unknown };
    const sourceKuikaId = String(obj.kuikaId ?? "");
    const sourcePropertyName = String(obj.propertyName ?? "");
    if (!sourceKuikaId || !sourcePropertyName) {
      return { error: `${fieldLabel}: kuikaId and propertyName must be non-empty` };
    }
    const built = buildDesignerSymbol(
      { bindingType: "componentValue", sourceKuikaId, sourcePropertyName } as never,
      sd
    );
    if ("error" in built) return { error: `${fieldLabel}: ${built.error}` };
    return { symbol: built };
  }

  // Case 3: prefixed string. Order matters — check longest prefixes first.
  if (typeof field === "string") {
    const screenInputMatch = field.match(/^screenInput[.:]([A-Za-z_][A-Za-z0-9_]*)$/);
    if (screenInputMatch) {
      const inputName = screenInputMatch[1];
      const built = buildDesignerSymbol({ bindingType: "screenInput", inputName } as never, sd);
      if ("error" in built) return { error: `${fieldLabel}: ${built.error}` };
      return { symbol: built };
    }

    // Case 4: bare string → fixed-value (back-compat).
    return {
      symbol: {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue: field,
        description: ""
      }
    };
  }

  // Number / boolean primitives → fixed value as well.
  if (typeof field === "number" || typeof field === "boolean") {
    return {
      symbol: {
        category: ESymbolCategory.fixed,
        isFixed: true,
        label: "Fixed Value",
        fixedValue: String(field),
        description: ""
      }
    };
  }

  return { error: `${fieldLabel}: unrecognised shape (${typeof field})` };
}

/**
 * Fix #40 — Dual-mode upsert mis-grouping guard for `create_conditional`.
 *
 * Reject calls that mis-group Insert + Update across the IfThenElse branches.
 * Three cases:
 *   1. Same mutation name appears in BOTH branches → reject (the second run
 *      duplicates the side-effect — always a mistake).
 *   2. ifActions contains BOTH `<X>_Insert` AND `<X>_Update` with empty
 *      elseActions → reject. This is the canonical reproducer from the
 *      May-2026 fikir-havuzu trace: the LLM lumped both mutations into the
 *      "create" branch, leaving "edit" empty. Result: every save inserts a
 *      duplicate AND silently fails the edit flow.
 *   3. Mirror case (both in elseActions, empty ifActions) — defensive guard
 *      against future schema relaxation; today the empty-ifActions branch
 *      is already rejected earlier.
 *
 * Detection key: name suffixes `_Insert` / `_Update`. The Wirer skill always
 * passes these via the Backend Architect's naming, and the runtime persists
 * the labels verbatim from action definitions, so suffix-matching is stable.
 * Exported for unit testing.
 */
export function validateDualModeUpsertSplit(ifLabels: string[], elseLabels: string[]): string | null {
  const insertInIf: string[] = ifLabels.filter((l) => l.endsWith("_Insert"));
  const updateInIf: string[] = ifLabels.filter((l) => l.endsWith("_Update"));
  const insertInElse: string[] = elseLabels.filter((l) => l.endsWith("_Insert"));
  const updateInElse: string[] = elseLabels.filter((l) => l.endsWith("_Update"));

  // Case 1a: same Insert appears in both branches.
  const sameInsertBoth: string | undefined = insertInIf.find((name) => insertInElse.includes(name));
  if (sameInsertBoth) {
    return (
      `[VALIDATION] dual-mode upsert: action "${sameInsertBoth}" appears in BOTH ` +
      `ifActions and elseActions. Each branch must contain a different mutation — ` +
      `typically ifActions:[<Entity>_Insert], elseActions:[<Entity>_Update].`
    );
  }
  // Case 1b: same Update appears in both branches.
  const sameUpdateBoth: string | undefined = updateInIf.find((name) => updateInElse.includes(name));
  if (sameUpdateBoth) {
    return (
      `[VALIDATION] dual-mode upsert: action "${sameUpdateBoth}" appears in BOTH ` +
      `ifActions and elseActions. Each branch must contain a different mutation — ` +
      `typically ifActions:[<Entity>_Insert], elseActions:[<Entity>_Update].`
    );
  }

  // Case 2: ifActions has BOTH Insert AND Update, elseActions empty.
  if (insertInIf.length > 0 && updateInIf.length > 0 && elseLabels.length === 0) {
    return (
      `[VALIDATION] dual-mode upsert: ifActions contains BOTH an Insert ` +
      `(${insertInIf.join(", ")}) AND an Update (${updateInIf.join(", ")}) ` +
      `with empty elseActions. The 'if PK is empty then Insert else Update' ` +
      `pattern requires ONE mutation per branch — split as ` +
      `ifActions:[${insertInIf[0]}], elseActions:[${updateInIf[0]}] and retry.`
    );
  }

  // Case 3: mirror — both in elseActions, ifActions empty (defensive).
  if (insertInElse.length > 0 && updateInElse.length > 0 && ifLabels.length === 0) {
    return (
      `[VALIDATION] dual-mode upsert: elseActions contains BOTH an Insert ` +
      `AND an Update with empty ifActions. Split as ` +
      `ifActions:[<Insert>], elseActions:[<Update>] and retry.`
    );
  }

  return null;
}

interface IConfirmationInput {
  kind?: string;
  message?: string;
  header?: string;
  icon?: string;
  iconBackgroundColor?: string;
  confirmButtonColor?: string;
  cancelButtonColor?: string;
}

/**
 * Run a synchronous binding write, loading the screen's process first if the
 * write turns out to need it (KOA-18136).
 *
 * The write path is synchronous down through the picker engine, so it cannot
 * fetch and resolves the managed-screen inventory from designer state — which a
 * headless turn leaves without any workflow parameters. The inventory READ does
 * fetch, so the two halves disagreed and every binding to a process parameter was
 * refused while being offered. Costs nothing on the ordinary path: the load only
 * happens after a refusal that names a missing process value.
 */
function withProcessWorkflowLoad(input: ToolInput, runSync: () => any): Promise<any> {
  return runProcessBindingWrite({
    runSync,
    // Read only when a refusal makes it matter — a write that landed never
    // touches designer state on this path.
    resolveProcessId: () =>
      resolveProcessBindingWriteId(getValues(getSdInstance()), input as IProcessBindingWriteScope),
    fetchWorkflow: fetchManagedScreenWorkflow
  });
}

export const actionTools: ToolHandlerGroup = {
  sync: {
    ui_logic_get_action_parameter_options: (input) => getDesignerActionParameterOptions(getSdInstance(), input),

    ui_logic_list_screen_actions: () => {
      const sd = getSdInstance();
      const screenDesign = sd.screenProvider.getScreenDesign();
      if (!screenDesign || !screenDesign.displayItems) {
        return { error: "No screen design loaded" };
      }

      const actions = collectActionsFromDisplayItems(screenDesign.displayItems, buildActionSummaryScreenIndex(sd));
      return {
        screenName: screenDesign.name,
        count: actions.length,
        actions
      };
    },

    ui_logic_list_current_actions: (input) => {
      const scope = resolveDesignerActionScope(getSdInstance(), input);
      if ("error" in scope) return scope;

      const actions = collectDesignerActionsFromCollection(scope.actions || [], scope);
      return {
        success: true,
        contextType: scope.kind,
        contextLabel: scope.label,
        count: actions.length,
        actions
      };
    },

    ui_logic_get_action_details: (input) => {
      const { actionId } = input;
      if (!actionId) return { error: "actionId is required" };

      const sd = getSdInstance();

      // Look up action definition
      const actionDef = getActionDefinitions(sd).find((a: any) => a.id === actionId);
      if (actionDef) {
        return {
          id: actionDef.id,
          name: actionDef.name,
          displayName: actionDef.displayName || actionDef.name,
          datasourceId: actionDef.kDatasource_Id,
          actionType: actionDef.actionType,
          actionCategory: actionDef.actionCategory,
          statement: actionDef.statement || null,
          parameters: (actionDef.parameters || []).map((p: any) => ({
            name: p.name,
            type: p.type,
            defaultValue: p.defaultValue || null
          }))
        };
      }

      const found = findContextAction(input);
      if (!("error" in found)) {
        // KOA-18158 law 7 — the same debt `add_to_event` reports at attach time,
        // answerable LATER. The attach response's `unboundRequiredParameters` is a
        // snapshot: the Binder binds those parameters after the Wirer attached, so
        // by the time anything decides what to do about them the snapshot is stale.
        // A caller that must act destructively (the deterministic unwire) has to be
        // able to re-ask, and it must get the SAME answer from the SAME predicate —
        // re-deriving "required and unbound" outside the designer would need
        // `isOptionalParameter` and the symbol semantics, i.e. a second copy of the
        // rule that decides whether wiring gets deleted.
        const unboundRequiredParameters: string[] = collectUnboundRequiredParameters(
          found.action,
          resolveActionDefinitionForInstance(sd, found.action)
        );
        // KOA-18422 — the same question about EVERY parameter, optional ones
        // included. `unboundRequiredParameters` answers "what will fail at
        // runtime"; a caller checking whether the edit it just asked for landed
        // needs "is THIS parameter carrying a value", and `Deadline` on an
        // update form is optional. Answered HERE because
        // `isMeaningfullyBoundParameter` is the authority on what a bound
        // parameter looks like — the `ensureParameterSymbols` placeholders
        // (`""`, a zero Guid, a freshly minted one) all carry a symbol and none
        // of them is a binding. A second copy of that rule on the server would
        // read every placeholder as bound.
        const boundParameters: string[] = [];
        const unboundParameters: string[] = [];
        for (const param of Array.isArray(found.action?.parameters) ? found.action.parameters : []) {
          const name: unknown = (param as { name?: unknown })?.name;
          if (typeof name !== "string" || name.length === 0) continue;
          if (isMeaningfullyBoundParameter(param)) boundParameters.push(name);
          else unboundParameters.push(name);
        }
        return {
          success: true,
          contextType: found.scope.kind,
          contextLabel: found.scope.label,
          action: found.action,
          actionId: found.action.id,
          parentRootActionId: found.rootAction?.id || null,
          boundParameters,
          unboundParameters,
          ...(unboundRequiredParameters.length > 0 ? { unboundRequiredParameters } : {})
        };
      }

      // If not found in the current context, search in display items for action instance
      const screenDesign = sd.screenProvider.getScreenDesign();
      if (!screenDesign?.displayItems) return { error: `Action "${actionId}" not found` };

      const allActions = collectActionsFromDisplayItems(screenDesign.displayItems, buildActionSummaryScreenIndex(sd));
      const actionInstance = allActions.find((a: any) => a.actionId === actionId);
      if (!actionInstance) return { error: `Action "${actionId}" not found` };

      // The row alone is a projection; `action` is the full instance (parameters,
      // `targetScreenInputParams`, symbols) so a write can be read back from any scope.
      return { ...actionInstance, action: findActionInstanceInDisplayItems(screenDesign.displayItems, actionId) };
    },

    ui_logic_export_action_clipboard: (input) => {
      const found = findContextAction(input);
      if ("error" in found) return found;

      return {
        success: true,
        clipboard: buildDesignerActionClipboard(
          getSdInstance(),
          found.action,
          input.eventName || found.scope.eventName || ""
        )
      };
    },

    ui_logic_add_action_to_event: (input) => {
      const sd = getSdInstance();
      // Fix #31 — reject literal `<...>` placeholders before any
      // resolution. The Wirer LLM occasionally emits an unfilled
      // template like `kuikaId: "<button-id>"`; without this check the
      // downstream resolver falls through, the dispatch returns a
      // success-looking shape with a freshly-minted actionId that
      // never lands on a real component, and the Binder later can't
      // find the action.
      //
      // Artifact/canvas ownership for deterministic Wirer-Binder is NOT
      // enforced here: PEA projectors are artifactScope-aware and call
      // ensureArtifactCanvas before tools. Do not require artifactId on
      // the tool payload (tools stay canvas-local; scope lives in the harness).
      if (isPlaceholderLiteralKuikaId(input.kuikaId)) {
        return { error: placeholderLiteralError("kuikaId", String(input.kuikaId)) };
      }
      // Fix #8 — auto-resolve friendlyName / id-alias kuikaId before any
      // downstream lookup. Eliminates the "Component with kuikaId
      // 'btn-save' not found" loop that burns 40+ tool calls per build.
      autoResolveKuikaIdField(input, "kuikaId", sd);
      const { kuikaId, eventName } = input;

      // LLMs sometimes send the add_multiple schema (actions array) to add_to_event.
      // If actionDefinitionId is missing but actions[0] exists, delegate to add_multiple.
      let { actionDefinitionId } = input;
      if (!actionDefinitionId) {
        const actions = safeParse(input.actions);
        if (Array.isArray(actions) && actions.length > 0) {
          return actionTools.sync!.ui_logic_add_multiple_actions_to_event(input);
        }
        return { error: "actionDefinitionId is required" };
      }
      const isScreenEventTarget =
        kuikaId || eventName || input.contextType === "screen_event" || input.context === "screen_event";

      if (isScreenEventTarget) {
        if (!kuikaId) return { error: "kuikaId is required" };
        if (!eventName) return { error: "eventName is required" };

        const dict = sd.screenProvider.state.screenDesignDictionary;
        const numericId = parseInt(kuikaId, 10);
        const di = dict[numericId];
        if (di) {
          const availableEvents = getAvailableEvents(di);
          if (availableEvents.length > 0 && !availableEvents.includes(eventName)) {
            return {
              success: false,
              error: `Event '${eventName}' not available on this component. Available events: [${availableEvents.join(", ")}]`
            };
          }

          // Idempotency guard: prevent duplicate actions on the same event.
          // LLMs sometimes re-add the same action (e.g. after a continuation nudge).
          // Resolve the actionDefinitionId to the canonical ID first.
          const allDefsForCheck = getAllActionDefinitions(sd);
          let canonicalDefId = actionDefinitionId;
          let resolvedDefMeta: ResolvedActionDefMeta | undefined;
          const defMatch = allDefsForCheck.find((a: any) => a.id === actionDefinitionId);
          if (defMatch) {
            resolvedDefMeta = { name: defMatch.name, displayName: defMatch.displayName, statement: defMatch.statement };
          } else {
            const searchName = actionDefinitionId.toLowerCase().replace(/\s+/g, "");
            const nameMatch = allDefsForCheck.find((a: any) => {
              const name = (a.name || "").toLowerCase().replace(/\s+/g, "");
              const displayName = (a.displayName || "").toLowerCase().replace(/\s+/g, "");
              return name === searchName || displayName === searchName;
            });
            if (nameMatch) {
              canonicalDefId = nameMatch.id;
              resolvedDefMeta = {
                name: nameMatch.name,
                displayName: nameMatch.displayName,
                statement: nameMatch.statement
              };
            }
          }
          // Multi-instance-friendly built-ins (SetValueOf, Notify, ChangeVisibilityOf, …)
          // legitimately stack on a single event — one Clear button needs three
          // SetValueOf instances to clear three inputs. Skip the dedup ONLY for
          // these; everything else (NAVIGATE, user-defined Insert/Update/Delete,
          // IfThenElse, …) keeps the existing nudge-resistance behavior.
          const skipDedupForMultiInstance = isMultiInstanceFriendlyAction(actionDefinitionId, resolvedDefMeta);
          const existingEvent = di.item?.events?.find((e: any) => e.eventName === eventName);
          if (
            !skipDedupForMultiInstance &&
            existingEvent?.actions?.some((a: any) => a.actionDefinitionId === canonicalDefId)
          ) {
            // Two obligations, not a choice between them. The caller's OWN parameters are applied to
            // the instance that exists instead of being discarded in silence (KOA-18136), and what the
            // instance still OWES afterwards is reported (KOA-18193) — an action attached by an earlier
            // turn kept its unbound required parameters forever because every re-attach answered
            // "already exists" and no pass looked inside (live `a693174e`, `CreateExpense`, five owed).
            //
            // ORDER IS THE CONTRACT: apply first, then read the debt. Reading first would name
            // parameters this very call is about to fill. The write here is not the "second hidden
            // write" that argument warns about — it is the caller's own explicit parameter list, and
            // the reply states exactly what landed and what did not.
            const existing: any = existingEvent.actions.find((a: any) => a.actionDefinitionId === canonicalDefId);
            const existingActionId: string | undefined = existing?.id;
            const update: ExistingActionParameterUpdate = applyParametersToExistingAction(
              sd,
              kuikaId,
              eventName,
              existingActionId,
              input.parameters
            );
            const existingUnbound: string[] = collectUnboundRequiredParameters(existing, resolvedDefMeta);
            return {
              success: true,
              alreadyExists: true,
              actionId: existingActionId,
              label: actionDefinitionId,
              eventName,
              message:
                `Action "${actionDefinitionId}" already exists on ${eventName} — no duplicate created` +
                (update.applied.length > 0
                  ? `; applied parameter(s) to the existing instance: ${update.applied.join(", ")}`
                  : "") +
                (update.dropped.length > 0
                  ? `; NOT applied (not on this action's signature): ${update.dropped.join(", ")}`
                  : ""),
              ...(update.applied.length > 0 ? { appliedParameters: update.applied } : {}),
              ...(update.dropped.length > 0 ? { droppedParameters: update.dropped } : {}),
              ...(existingUnbound.length > 0 ? { unboundRequiredParameters: existingUnbound } : {})
            };
          }

          // Fix #32 — block mutation-as-sibling-of-IfThenElse.
          const existingIfThenElse = findIfThenElseOnEvent(existingEvent?.actions);
          if (existingIfThenElse && isUserDefinedMutationAction(actionDefinitionId, resolvedDefMeta)) {
            return {
              error: ifThenElseMutationBlockError(actionDefinitionId, existingIfThenElse, eventName, kuikaId)
            };
          }
        }
      }

      const allDefs = getAllActionDefinitions(sd);
      const result = resolveAndCreateAction(sd, allDefs, {
        actionDefinitionId,
        parameters: input.parameters,
        fromScreenId: input.fromScreenId,
        screenInputs: input.screenInputs,
        allowSameScreen: isExplicitTrue(input.allowSameScreen),
        hostScope: isScreenEventTarget ? { kuikaId, eventName } : undefined,
        hostKuikaId: kuikaId
      });
      if ("error" in result) {
        return result;
      }

      const { action, actionId, droppedParameters, unboundRequiredParameters, navigateSameScreenWarning } = result;

      if (isScreenEventTarget) {
        const notPersisted = attachToScreenEventOrExplain(sd, kuikaId, eventName, action);
        if (notPersisted) return notPersisted;
      } else {
        const scope = resolveDesignerActionScope(sd, input);
        if ("error" in scope) return scope;
        scope.saveTopLevelAction(action);
      }

      const validParamNames = action.parameters?.map((p: any) => p.name).filter(Boolean) || [];
      return {
        success: true,
        actionId,
        label: action.label,
        eventName: eventName || undefined,
        parameterNames: validParamNames,
        targetScreenInputParams:
          action.targetScreenInputParams?.length > 0
            ? action.targetScreenInputParams.map((p: any) => ({ name: p.name, value: p.symbol?.fixedValue }))
            : undefined,
        // Surface unmatched parameter overrides so the caller sees they
        // were dropped. Previously these vanished silently — the LLM
        // thought `ScreenName: "X"` was set on NAVIGATE when the real
        // param is `toScreenId`, and every downstream binding attempt
        // on ScreenName failed with "Parameter not found".
        ...(droppedParameters && droppedParameters.length > 0
          ? {
              droppedParameters,
              warning: `Ignored ${droppedParameters.length} parameter override(s) not in this action's signature: ${droppedParameters.join(", ")}. Valid parameter names: ${validParamNames.join(", ")}.`
            }
          : {}),
        // KOA-18193 — the attach landed, but these required parameters have no
        // source. Code generation will refuse the project with KV1170 ("The
        // required parameter is not set") and Preview will not open. Naming them
        // in the attach response is what lets the caller finish the job in the
        // same turn instead of discovering it after the build.
        ...(unboundRequiredParameters && unboundRequiredParameters.length > 0
          ? {
              unboundRequiredParameters,
              unboundRequiredWarning:
                `Attached, but ${unboundRequiredParameters.length} REQUIRED parameter(s) have no source: ` +
                `${unboundRequiredParameters.join(", ")}. The action will run with them unset and code generation ` +
                `will block with KV1170. Bind each one now — inline via \`bindingType\` in this same ` +
                `ui_action.add_to_event call, or with ui_action.set_multiple_parameter_bindings using actionId ` +
                `"${actionId}".`
            }
          : {}),
        ...(navigateSameScreenWarning !== undefined ? { navigateSameScreenWarning } : {})
      };
    },

    ui_logic_add_multiple_actions_to_event: (input) => {
      // Fix #31 — reject literal `<...>` placeholder kuikaId, same
      // rationale as the single-add path.
      if (isPlaceholderLiteralKuikaId(input.kuikaId)) {
        return { error: placeholderLiteralError("kuikaId", String(input.kuikaId)) };
      }
      // Fix #8 — friendlyName / alias kuikaId auto-resolve, same as
      // single-add path.
      autoResolveKuikaIdField(input, "kuikaId", getSdInstance());
      const { kuikaId, eventName } = input;

      const actions = safeParse(input.actions);
      if (!Array.isArray(actions) || actions.length === 0) {
        return { error: "actions must be a non-empty array of {actionDefinitionId, parameters?} objects" };
      }

      // Deduplicate: if the same actionDefinitionId appears multiple times without
      // different parameters, only add it once. LLMs often duplicate the same action
      // (e.g. 5x GetAll on pageInit) when they should use one instance for all bindings.
      //
      // EXCEPT: multi-instance-friendly built-ins (SetValueOf, Notify,
      // ChangeVisibilityOf, …) legitimately stack — a Clear button needs N
      // SetValueOf instances for N inputs. Resolve names lazily so the
      // skip-list lookup also covers Guid-keyed adds.
      const sdForDedup = getSdInstance();
      const allDefsForDedup = getAllActionDefinitions(sdForDedup);
      const resolveDef = (defId: string): ResolvedActionDefMeta | undefined => {
        const direct = allDefsForDedup.find((a: any) => a.id === defId);
        if (direct) return { name: direct.name, displayName: direct.displayName, statement: direct.statement };
        const searchName = defId.toLowerCase().replace(/\s+/g, "");
        const nameMatch = allDefsForDedup.find((a: any) => {
          const name = (a.name || "").toLowerCase().replace(/\s+/g, "");
          const displayName = (a.displayName || "").toLowerCase().replace(/\s+/g, "");
          return name === searchName || displayName === searchName;
        });
        return nameMatch
          ? { name: nameMatch.name, displayName: nameMatch.displayName, statement: nameMatch.statement }
          : undefined;
      };

      const deduped: typeof actions = [];
      const seenDefIds = new Set<string>();
      const skippedDuplicates: string[] = [];
      /** Parameters a duplicate spec handed to the instance that already exists (KOA-18136). */
      const appliedToExisting: string[] = [];
      /** Parameters a duplicate spec brought that the existing action's signature has no slot for. */
      const notAppliedToExisting: string[] = [];
      /**
       * KOA-18193 — what the instances we REFUSED to duplicate still owe.
       *
       * The batch path reported duplicates as a count and nothing else, so an
       * action attached by an earlier turn kept its unbound required parameters
       * invisible: the re-attach that would have surfaced them is exactly the
       * call that gets deduplicated (live `ac73f23f` — `CreateExpense` skipped
       * as a duplicate, five parameters still owed, result carried no sign of
       * it). Refusing the duplicate stays right; going silent does not.
       */
      const duplicateDebts: { actionId: string; actionName: string; unboundRequiredParameters: string[] }[] = [];
      for (const spec of actions) {
        const defId = spec.actionDefinitionId;
        if (!defId) {
          deduped.push(spec);
          continue;
        }
        const resolved = resolveDef(defId);
        if (isMultiInstanceFriendlyAction(defId, resolved)) {
          deduped.push(spec);
          continue;
        }
        // Fix #22 — dedup ALSO when `parameters` / `screenInputs` are
        // present. The earlier carve-out ("if you brought parameters,
        // you must want a fresh instance") backfired: the Binder's
        // retry path called `add_to_event` repeatedly with parameter
        // values trying to fix bindings, and each call created a NEW
        // instance instead of reusing the existing one. The result
        // was 5+ Ideas_Insert instances stacked on one button. If the
        // caller really wants to override parameters on an existing
        // instance, the right tool is `set_multiple_parameter_bindings`.
        if (seenDefIds.has(defId)) {
          skippedDuplicates.push(defId);
          continue;
        }
        seenDefIds.add(defId);
        deduped.push(spec);
      }

      const sd = getSdInstance();
      const isScreenEventTarget =
        kuikaId || eventName || input.contextType === "screen_event" || input.context === "screen_event";

      if (isScreenEventTarget) {
        if (!kuikaId) return { error: "kuikaId is required" };
        if (!eventName) return { error: "eventName is required" };

        const dict = sd.screenProvider.state.screenDesignDictionary;
        const numericId = parseInt(kuikaId, 10);
        const di = dict[numericId];
        if (di) {
          const availableEvents = getAvailableEvents(di);
          if (availableEvents.length > 0 && !availableEvents.includes(eventName)) {
            return {
              success: false,
              error: `Event '${eventName}' not available on this component. Available events: [${availableEvents.join(", ")}]`
            };
          }

          // Cross-call dedup: filter out actions that already exist on this event.
          // This prevents nudge-induced duplicates across separate tool calls.
          // Multi-instance-friendly built-ins are exempt — see the in-batch
          // dedup above for the rationale.
          const existingEvent = di.item?.events?.find((e: any) => e.eventName === eventName);
          if (existingEvent && (existingEvent.actions?.length ?? 0) > 0) {
            const existingDefIds = new Set(existingEvent.actions.map((a: any) => a.actionDefinitionId));
            const allDefsForResolve = getAllActionDefinitions(sd);
            const beforeCount = deduped.length;
            for (let i = deduped.length - 1; i >= 0; i--) {
              const defId = deduped[i].actionDefinitionId;
              if (!defId) continue;
              // Resolve name-based IDs to canonical IDs
              let canonicalId = defId;
              let resolvedMeta: ResolvedActionDefMeta | undefined;
              const defMatch = allDefsForResolve.find((a: any) => a.id === defId);
              if (defMatch) {
                resolvedMeta = {
                  name: defMatch.name,
                  displayName: defMatch.displayName,
                  statement: defMatch.statement
                };
              } else {
                const searchName = defId.toLowerCase().replace(/\s+/g, "");
                const nameMatch = allDefsForResolve.find((a: any) => {
                  const name = (a.name || "").toLowerCase().replace(/\s+/g, "");
                  const displayName = (a.displayName || "").toLowerCase().replace(/\s+/g, "");
                  return name === searchName || displayName === searchName;
                });
                if (nameMatch) {
                  canonicalId = nameMatch.id;
                  resolvedMeta = {
                    name: nameMatch.name,
                    displayName: nameMatch.displayName,
                    statement: nameMatch.statement
                  };
                }
              }
              if (isMultiInstanceFriendlyAction(defId, resolvedMeta)) {
                continue;
              }
              // Fix #22 — cross-call dedup ALSO triggers when caller
              // brings parameters. See in-batch dedup above for the
              // rationale: Binder retry loops were creating a fresh
              // Ideas_Insert/Update/… instance on every call.
              if (existingDefIds.has(canonicalId)) {
                // No SECOND instance — but this spec's parameters go onto the instance
                // that already exists instead of vanishing (KOA-18136; the batched shape
                // is how the process-start pass attaches, so this is the path that lost
                // `workflow` on the submit button).
                const existingActionId: string | undefined = existingEvent.actions.find(
                  (a: any) => a.actionDefinitionId === canonicalId
                )?.id;
                const update: ExistingActionParameterUpdate = applyParametersToExistingAction(
                  sd,
                  kuikaId,
                  eventName,
                  existingActionId,
                  deduped[i].parameters
                );
                appliedToExisting.push(...update.applied.map((name: string): string => `${defId}.${name}`));
                notAppliedToExisting.push(...update.dropped.map((name: string): string => `${defId}.${name}`));
                skippedDuplicates.push(defId);
                const existingInstance: any = existingEvent?.actions?.find(
                  (a: any) => a.actionDefinitionId === canonicalId
                );
                const owed: string[] = collectUnboundRequiredParameters(existingInstance, resolvedMeta);
                if (owed.length > 0) {
                  duplicateDebts.push({
                    actionId: typeof existingInstance?.id === "string" ? existingInstance.id : "(unknown)",
                    actionName: defId,
                    unboundRequiredParameters: owed
                  });
                }
                deduped.splice(i, 1);
              }
            }
            if (deduped.length === 0 && beforeCount > 0) {
              return {
                success: true,
                alreadyExists: true,
                message:
                  `All ${beforeCount} actions already exist on ${eventName} — no duplicates created` +
                  (appliedToExisting.length > 0
                    ? `; applied parameter(s) to the existing instance(s): ${appliedToExisting.join(", ")}`
                    : "") +
                  (notAppliedToExisting.length > 0
                    ? `; NOT applied (no such parameter on the action): ${notAppliedToExisting.join(", ")}`
                    : ""),
                skippedDuplicates,
                ...(appliedToExisting.length > 0 ? { appliedParameters: appliedToExisting } : {}),
                ...(notAppliedToExisting.length > 0 ? { droppedParameters: notAppliedToExisting } : {}),
                ...(duplicateDebts.length > 0 ? { duplicates: duplicateDebts } : {})
              };
            }

            // Fix #32 — block mutation-as-sibling-of-IfThenElse, batched.
            // If the event already has an IfThenElse and the batch
            // contains any mutation, the whole batch must be rejected:
            // the LLM is composing the canonical pattern incorrectly and
            // splitting the batch (some attached, some rejected) would
            // leave a half-wired event timeline.
            const existingIfThenElseBatch = findIfThenElseOnEvent(existingEvent?.actions);
            if (existingIfThenElseBatch) {
              for (const spec of deduped) {
                const defId = spec.actionDefinitionId;
                if (!defId) continue;
                const resolvedMeta = resolveDef(defId);
                if (isUserDefinedMutationAction(defId, resolvedMeta)) {
                  return {
                    error: ifThenElseMutationBlockError(defId, existingIfThenElseBatch, eventName, kuikaId)
                  };
                }
              }
            }
          }
        }
      }

      const scope = !isScreenEventTarget ? resolveDesignerActionScope(sd, input) : null;
      if (scope && "error" in scope) return scope;

      const allDefs = getAllActionDefinitions(sd);
      const addedActions: { actionId: string; actionName: string; parameterNames: string[]; index: number }[] = [];
      const errors: { index: number; actionDefinitionId: string; error: string }[] = [];

      for (let i = 0; i < deduped.length; i++) {
        const spec = deduped[i];
        if (!spec.actionDefinitionId) {
          errors.push({ index: i, actionDefinitionId: "", error: "actionDefinitionId is required" });
          continue;
        }

        const result = resolveAndCreateAction(sd, allDefs, {
          actionDefinitionId: spec.actionDefinitionId,
          parameters: spec.parameters,
          fromScreenId: input.fromScreenId,
          screenInputs: spec.screenInputs,
          allowSameScreen: isExplicitTrue(spec.allowSameScreen ?? input.allowSameScreen),
          hostScope: isScreenEventTarget ? { kuikaId, eventName } : undefined,
          hostKuikaId: kuikaId
        });

        if ("error" in result) {
          errors.push({ index: i, actionDefinitionId: spec.actionDefinitionId, error: result.error });
          continue;
        }

        if (isScreenEventTarget) {
          // KOA-18226 — a write that did not land is an error for THIS entry, never an
          // entry in `addedActions`. Reporting it as added is what let a whole batch read
          // as wired while the canvas carried none of it.
          const notPersisted = attachToScreenEventOrExplain(sd, kuikaId, eventName, result.action);
          if (notPersisted) {
            errors.push({ index: i, actionDefinitionId: spec.actionDefinitionId, error: notPersisted.error });
            continue;
          }
        } else {
          scope?.saveTopLevelAction(result.action);
        }
        addedActions.push({
          actionId: result.actionId,
          actionName: result.action.label,
          parameterNames: result.action.parameters?.map((p: any) => p.name).filter(Boolean) || [],
          index: i,
          // KOA-18193 — per-action, because a batch attach fails per-action: one
          // entry's missing `Id` must not be hidden behind the batch's success.
          ...(result.unboundRequiredParameters && result.unboundRequiredParameters.length > 0
            ? { unboundRequiredParameters: result.unboundRequiredParameters }
            : {}),
          ...(result.navigateSameScreenWarning !== undefined
            ? { navigateSameScreenWarning: result.navigateSameScreenWarning }
            : {})
        });
      }

      return {
        success: addedActions.length > 0,
        addedActions,
        totalAdded: addedActions.length,
        ...(skippedDuplicates.length > 0
          ? {
              deduplicated: skippedDuplicates.length,
              note:
                "A duplicate actionDefinitionId created no second instance; any parameters it " +
                "carried were applied to the instance that already exists (see appliedParameters)"
            }
          : {}),
        ...(appliedToExisting.length > 0 ? { appliedParameters: appliedToExisting } : {}),
        ...(notAppliedToExisting.length > 0 ? { droppedParameters: notAppliedToExisting } : {}),
        ...(duplicateDebts.length > 0 ? { duplicates: duplicateDebts } : {}),
        ...(errors.length > 0 ? { errors } : {})
      };
    },

    ui_logic_delete_action_from_event: (input) => {
      // The tool schema is `eventName`, but the planner routinely emits the
      // shorter `event`; accept it so scope resolution doesn't fail spuriously.
      if (!input.eventName && typeof input.event === "string") {
        input.eventName = input.event;
      }

      // Resolve the target instance id(s). The planner cannot know an action's
      // per-instance Guid (it only sees the screen), so it names the action by
      // TYPE (e.g. "NAVIGATE"); resolve that to a concrete instance on the event.
      const targets = resolveDeleteActionTargets(input, getSdInstance());
      if ("error" in targets) return targets;

      const deleted: string[] = [];
      for (const actionId of targets.ids) {
        const found = findContextAction({ ...input, actionId });
        if ("error" in found) return found;
        deleteResolvedAction(found);
        deleted.push(actionId);
      }

      return {
        success: true,
        deletedActionIds: deleted,
        deletedActionId: deleted.length === 1 ? deleted[0] : undefined,
        eventName: input.eventName,
        ...(targets.matchedByType !== undefined ? { matchedByType: targets.matchedByType } : {})
      };
    },

    ui_logic_reorder_actions: (input) => {
      const { fromIndex, toIndex } = input;
      if (fromIndex == null) return { error: "fromIndex is required" };
      if (toIndex == null) return { error: "toIndex is required" };

      const scope = resolveDesignerActionScope(getSdInstance(), input);
      if ("error" in scope) return scope;

      const actions = scope.actions || [];
      if (fromIndex < 0 || fromIndex >= actions.length) {
        return { error: `fromIndex ${fromIndex} out of range (0-${actions.length - 1})` };
      }
      if (toIndex < 0 || toIndex >= actions.length) {
        return { error: `toIndex ${toIndex} out of range (0-${actions.length - 1})` };
      }

      // Reorder using splice (mirrors action-picker.tsx pattern)
      const reordered = Array.from(actions);
      const [removed] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, removed);

      scope.replaceTopLevelActions(reordered);

      return {
        success: true,
        eventName: scope.eventName,
        actionCount: reordered.length,
        newOrder: reordered.map((a: any) => a.label || a.description || "Unnamed")
      };
    },

    ui_logic_update_action_parameters: (input) => {
      const parameters = safeParse(input.parameters);
      if (!Array.isArray(parameters) || parameters.length === 0) {
        return { error: "parameters must be a non-empty array of {name, value} objects" };
      }

      const found = findContextAction(input);
      if ("error" in found) return found;
      const sd = getSdInstance();
      const action = found.action;

      const updatedNames: string[] = [];
      let nextAction = action;
      for (const override of parameters) {
        const overrideName = normalizeOverrideName(override);
        if (!overrideName) continue;
        const parameter = findActionParameter(nextAction, overrideName);
        if (!parameter) continue;

        const symbol = resolveParameterSymbol(override, sd, nextAction, parameter);
        if (!symbol) continue;
        nextAction = setDesignerActionParameter(nextAction, parameter, symbol);
        updatedNames.push(overrideName);
      }

      if (updatedNames.length === 0) {
        return { error: "No matching parameters found to update" };
      }

      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        eventName: input.eventName || found.scope.eventName,
        updatedParameters: updatedNames
      };
    },

    // KOA-17394 (cache-immune navigate reconciliation). Re-populate a NAVIGATE
    // action's `targetScreenInputParams` from FRESH screen-input lists supplied
    // by the pipeline (`targetScreenInputs` / `sourceScreenInputs`), bypassing
    // the stale in-memory `screenList` cache that drops cross-screen-created
    // inputs at wire time. Merge-idempotent: existing real bindings (a row
    // `current.Id`) are preserved; only placeholders are (re)filled via
    // source-context pass-through or left as the new-record placeholder. Runs
    // as a final pass once every screen's inputs are finalized on the server.
    ui_logic_repopulate_target_screen_inputs: (input) => {
      const found = findContextAction(input);
      if ("error" in found) return found;
      const targetScreenInputs = safeParse(input.targetScreenInputs);
      if (!Array.isArray(targetScreenInputs)) {
        return { error: "targetScreenInputs must be an array of {name, type} objects" };
      }
      const sourceScreenInputs = safeParse(input.sourceScreenInputs);
      const explicit: ExplicitNavScreenInputs = {
        targetScreenInputs,
        ...(Array.isArray(sourceScreenInputs) ? { sourceScreenInputs } : {}),
        ...(input.targetInputsVerifiedEmpty === true ? { targetInputsVerifiedEmpty: true } : {})
      };
      // KOA-18173 — the return value used to be discarded, so every refusal this
      // helper reports (a row-scope drop, or a refused wipe) vanished and the caller
      // counted the call as a clean repopulate. Surface it: `reconcileNavigateTargetScreenInputs`
      // can then record a failure instead of reporting `failureCount: 0` over
      // navigates it did not actually fix.
      const dropped: string[] = applyTargetScreenInputParams(
        found.action,
        getSdInstance(),
        input.screenInputs,
        explicit
      );
      persistResolvedAction(found, found.action);
      const resultParams: { name: string }[] = Array.isArray(found.action.targetScreenInputParams)
        ? found.action.targetScreenInputParams.map((p: any) => ({ name: p.name }))
        : [];
      return {
        success: true,
        actionId: input.actionId,
        eventName: input.eventName || found.scope.eventName,
        targetScreenInputParams: resultParams,
        ...(dropped.length > 0 ? { droppedParameters: dropped } : {})
      };
    },

    ui_logic_paste_action_clipboard: (input) => {
      const clipboard = safeParse(input.clipboard);
      if (!clipboard?.action) {
        return { error: "clipboard with action payload is required" };
      }

      const found = findContextAction(input);
      if ("error" in found) return found;

      const result = pasteDesignerActionClipboard(getSdInstance(), found.action, clipboard);
      persistResolvedAction(found, result.action);
      if (result.screenDesignChanged) {
        const sd2 = getSdInstance();
        sd2.screenProvider.setScreenDesign(result.screenDesign, 2);
      }

      return {
        success: true,
        actionId: input.actionId,
        eventName: input.eventName
      };
    },

    ui_logic_list_action_parameter_binding_options: (input) => {
      const resolved = getResolvedActionParameter(input);
      if ("error" in resolved) return resolved;
      const { found, parameter } = resolved;

      const result = listDesignerActionParameterBindingOptions(getSdInstance(), found.action, parameter);
      // Strip pickerOptions tree to save ~3K tokens — the LLM uses bindingType + field names directly
      if (result && result.pickerOptions) {
        result.pickerOptions = [];
        result.supportsOptionPath = false;
      }
      return result;
    },

    ui_logic_get_action_parameter_editor_options: (input) => {
      const resolved = getResolvedActionParameter(input);
      if ("error" in resolved) return resolved;
      const { found, parameter } = resolved;
      const { parameterName } = input;

      const actionDefinitions = getActionDefinitions(found.sd);
      const actionDefinition = actionDefinitions.find(
        (candidate: any) => candidate.id === found.action.actionDefinitionId
      );
      if (!actionDefinition) {
        return {
          error: `Action definition "${found.action.actionDefinitionId}" not found for action "${input.actionId}"`
        };
      }

      const editor = getDesignerActionParameterEditorConfig(getSdInstance(), found.action, actionDefinition, parameter);
      return {
        success: true,
        parameterName,
        editor: editor || null,
        currentFixedValue: parameter?.symbol?.fixedValue
      };
    },

    ui_logic_set_action_parameter_binding: (input) => {
      // Fix #31 — reject literal `<...>` placeholders on kuikaId /
      // sourceKuikaId before any resolution kicks in.
      if (isPlaceholderLiteralKuikaId(input.kuikaId)) {
        return { error: placeholderLiteralError("kuikaId", String(input.kuikaId)) };
      }
      if (isPlaceholderLiteralKuikaId(input.sourceKuikaId)) {
        return { error: placeholderLiteralError("sourceKuikaId", String(input.sourceKuikaId)) };
      }
      // Fix #8 — auto-resolve kuikaId / sourceKuikaId from friendlyName.
      // Fix #9 — auto-resolve actionId from actionDefinitionId.
      // Fix #12 — recover from host/source confusion + defaultName misuse.
      // Fix #17 — default sourcePropertyName for componentValue bindings.
      const sdForResolve = getSdInstance();
      autoResolveKuikaIdField(input, "kuikaId", sdForResolve);
      autoResolveKuikaIdField(input, "sourceKuikaId", sdForResolve);
      autoResolveActionInstanceId(input, sdForResolve, {
        kuikaId: input.kuikaId,
        eventName: input.eventName
      });
      correctParameterBindingHostScope(input, sdForResolve);
      applyComponentValueSourcePropertyDefault(input, sdForResolve);

      const navigateError = validateNavigateBinding({
        actionName: input.actionName,
        parameterName: input.parameterName,
        bindingType: input.bindingType,
        source: input.source
      });
      if (navigateError) {
        return {
          error: `${navigateError.reason} ${navigateError.suggestion}`
        };
      }
      const rowScopeError = validateRowScopeBindingShape(input);
      if (rowScopeError) {
        return { error: rowScopeError };
      }

      const resolved = getResolvedActionParameter(input);
      if ("error" in resolved) return resolved;
      const { found, parameter } = resolved;
      const { parameterName } = input;

      const symbol = buildDesignerActionParameterSymbol(getSdInstance(), found.action, parameter, input);
      if ((symbol as any)?.error) return symbol;

      const nextAction = setDesignerActionParameter(found.action, parameter, symbol as any);
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        eventName: input.eventName || found.scope.eventName,
        parameterName,
        message: `Bound ${parameterName} on action ${input.actionId}`
      };
    },

    ui_logic_set_multiple_parameter_bindings: (input) => {
      // Fix #31 — reject literal `<...>` placeholders on top-level
      // kuikaId before any resolution.
      if (isPlaceholderLiteralKuikaId(input.kuikaId)) {
        return { error: placeholderLiteralError("kuikaId", String(input.kuikaId)) };
      }
      // Fix #8 / #9 — auto-resolve id-shape mistakes at handler entry.
      // Done at the TOP-LEVEL fields here; each individual binding gets
      // the same treatment below (per-binding kuikaId / sourceKuikaId /
      // actionId may differ from the top-level values for cross-action
      // batches).
      const sdForResolve = getSdInstance();
      autoResolveKuikaIdField(input, "kuikaId", sdForResolve);
      autoResolveActionInstanceId(input, sdForResolve, {
        kuikaId: input.kuikaId,
        eventName: input.eventName
      });

      const bindings = safeParse(input.bindings);
      if (!Array.isArray(bindings) || bindings.length === 0) {
        return { error: "bindings array is required and must not be empty" };
      }

      // Fix #31 — reject literal `<...>` placeholders on any per-binding
      // kuikaId / sourceKuikaId. Done in a pre-pass so a single bad
      // entry doesn't slip through with the rest looking OK.
      for (const binding of bindings) {
        if (!binding || typeof binding !== "object") continue;
        if (isPlaceholderLiteralKuikaId(binding.kuikaId)) {
          return { error: placeholderLiteralError("bindings[].kuikaId", String(binding.kuikaId)) };
        }
        if (isPlaceholderLiteralKuikaId(binding.sourceKuikaId)) {
          return { error: placeholderLiteralError("bindings[].sourceKuikaId", String(binding.sourceKuikaId)) };
        }
      }

      // Per-binding pass — apply the same Fix #8 / #9 / #12 resolution
      // to every entry so cross-action batches and binding-specific
      // sources get the friendlyName / definition-Guid auto-resolve
      // AND the host/source confusion recovery.
      for (const binding of bindings) {
        if (!binding || typeof binding !== "object") continue;
        autoResolveKuikaIdField(binding, "kuikaId", sdForResolve);
        autoResolveKuikaIdField(binding, "sourceKuikaId", sdForResolve);
        autoResolveActionInstanceId(binding, sdForResolve, {
          kuikaId: binding.kuikaId ?? input.kuikaId,
          eventName: binding.eventName ?? input.eventName
        });
        // After the actionId has been resolved to an instance id we
        // can look up the action's real host and correct any
        // host/source confusion in the kuikaId / eventName /
        // sourceKuikaId / defaultName fields. See
        // `correctParameterBindingHostScope` for the full recovery
        // policy.
        correctParameterBindingHostScope(binding, sdForResolve);
        // Fix #17 — sourcePropertyName default for componentValue.
        // Runs AFTER host/source recovery so the default looks at the
        // corrected sourceKuikaId (if recovery moved a misplaced
        // kuikaId into the source slot).
        applyComponentValueSourcePropertyDefault(binding, sdForResolve);
      }

      const results: Array<{
        parameterName: string;
        actionId: string;
        success: boolean;
        message?: string;
        error?: string;
        warning?: string;
      }> = [];

      for (const binding of bindings) {
        // Effective actionId — per-binding wins, then top-level. Used for
        // error reporting so results rows carry the real id instead of
        // "?" when the LLM only provided actionId at top level.
        const effectiveActionId = binding.actionId || input.actionId;
        try {
          const navigateError = validateNavigateBinding(binding);
          if (navigateError) {
            results.push({
              parameterName: binding.parameterName || "?",
              actionId: effectiveActionId || "?",
              success: false,
              error: `${navigateError.reason} ${navigateError.suggestion}`
            });
            continue;
          }
          const rowScopeError = validateRowScopeBindingShape(binding);
          if (rowScopeError) {
            results.push({
              parameterName: binding.parameterName || "?",
              actionId: effectiveActionId || "?",
              success: false,
              error: rowScopeError
            });
            continue;
          }

          // Parameter ↔ source name affinity guard. When the Binder picks a
          // sourceKuikaId whose friendlyName shares NO meaningful tokens
          // with parameterName AND a better candidate exists on the same
          // screen, reject the binding with a teaching error. Catches the
          // KOA-17026 mode where Binder paired KayitTarihi (DateTime) with
          // a TextArea (`olcum-not`) because no Date/DateTime input was
          // returned by its strict-type lookup. Conservative: warns
          // (instead of erroring) when no better candidate exists.
          let affinityWarning: string | undefined;
          if (binding.bindingType === "componentValue" && binding.sourceKuikaId && binding.parameterName) {
            try {
              const dict = getSdInstance().screenProvider?.state?.screenDesignDictionary;
              if (dict) {
                const sourceId = parseInt(String(binding.sourceKuikaId), 10);
                const sourceDi = dict[sourceId];
                const sourceFriendly = sourceDi?.friendlyName;
                // Only VALUE-holding components are valid componentValue sources, so only
                // they are valid "better candidates". Without this, a name-token match on a
                // Button (e.g. 'btn-Masraflar_Filter' vs param 'MasrafAdi') was suggested as a
                // better source than the actual search input — falsely rejecting a sound bind.
                const componentDefs = getSdInstance().projectProvider?.state?.project?.componentSetObj?.components;
                const hasValueProp = (di: any): boolean => {
                  const def = componentDefs?.find((c: any) => c.id === di?.item?.id);
                  return Array.isArray(def?.properties) && def.properties.some((p: any) => p?.isValueProp === true);
                };
                if (sourceFriendly) {
                  const otherComponents = Object.values(dict)
                    .filter(
                      (di: any) => di?.id !== sourceId && typeof di?.friendlyName === "string" && hasValueProp(di)
                    )
                    .map((di: any) => ({
                      kuikaId: String(di.id),
                      friendlyName: String(di.friendlyName),
                      componentName: di?.item?.name as string | undefined
                    }));
                  const verdict = validateParameterSourceAffinity({
                    parameterName: binding.parameterName,
                    sourceKuikaId: String(binding.sourceKuikaId),
                    sourceFriendlyName: sourceFriendly,
                    sourceComponentName: sourceDi?.item?.name,
                    otherComponents
                  });
                  if (verdict.error) {
                    results.push({
                      parameterName: binding.parameterName,
                      actionId: effectiveActionId || "?",
                      success: false,
                      error: verdict.error
                    });
                    continue;
                  }
                  if (verdict.warning) affinityWarning = verdict.warning;
                }
              }
            } catch {
              // Affinity check is best-effort — don't fail the binding on
              // dictionary access errors.
            }
          }

          // Merge top-level context (kuikaId/eventName/contextType/actionId)
          // into each binding. The action-scope resolver needs these to
          // locate the screen_event scope; individual bindings only carry
          // parameterName. The binding's own fields win on conflict so
          // cross-action batches still resolve per-binding. `actionId`
          // was previously NOT merged — that broke the common shape where
          // the LLM puts actionId once at the top level and per-binding
          // entries only carry parameterName + bindingType + source, and
          // the resolver would reject every entry with "actionId is
          // required" even though it was provided.
          const resolved = getResolvedActionParameter({
            kuikaId: input.kuikaId,
            eventName: input.eventName,
            contextType: input.contextType,
            context: input.context,
            actionId: input.actionId,
            ...binding
          });
          if ("error" in resolved) {
            results.push({
              parameterName: binding.parameterName || "?",
              actionId: effectiveActionId || "?",
              success: false,
              error: (resolved as any).error
            });
            continue;
          }
          const { found, parameter } = resolved;

          const symbol = buildDesignerActionParameterSymbol(getSdInstance(), found.action, parameter, binding);
          if ((symbol as any)?.error) {
            results.push({
              parameterName: binding.parameterName,
              actionId: effectiveActionId,
              success: false,
              error: (symbol as any).error
            });
            continue;
          }

          // KOA-17717 — a `current.<field>` binding (row-action / row-navigate
          // screen-input) is promoted to a bare Current symbol with a hardcoded
          // "String" type. Enrich it with the REAL column type (Guid/Int/…) from
          // the enclosing repeater's datasource, matching the manual cascader.
          // No-op for non-current symbols and when the host/field can't resolve.
          const paramDeclaredType: string | undefined =
            typeof parameter?.type === "string" ? parameter.type : parameter?.typeName;
          const enrichedSymbol: ISymbol = enrichCurrentRowSymbol(
            symbol as ISymbol,
            getSdInstance(),
            binding.kuikaId,
            paramDeclaredType
          );

          const nextAction = setDesignerActionParameter(found.action, parameter, enrichedSymbol as any);
          persistResolvedAction(found, nextAction);

          // KOA-17394 (Faz 11B) — When the NAVIGATE.toScreenId binding is
          // updated (the typical path is applyCrossScreenNavigation
          // rewriting the interim name to a real screen UUID), re-populate
          // `targetScreenInputParams` from the new target's screenInputs.
          // The first `applyTargetScreenInputParams` call at attach-time
          // bailed early because toScreenId still held the screen NAME
          // (no screenList hit). Without this re-trigger, paramsız
          // Navigate'lar end up with empty targetScreenInputParams and
          // the backend validator fires KV1017 ("Parameter 'X' of screen
          // input '<screen>' no longer exists") for every screen-input
          // declared on the target.
          if (parameter.name?.toLowerCase() === "toscreenid") {
            applyTargetScreenInputParams(nextAction, getSdInstance());
            persistResolvedAction(found, nextAction);
          }

          // Warn if binding source is a non-data component (Button, Icon, etc.)
          let warning: string | undefined = affinityWarning;
          if (binding.bindingType === "componentValue" && binding.sourceKuikaId) {
            try {
              const dict = getSdInstance().screenProvider?.state?.screenDesignDictionary;
              if (dict) {
                const sourceId = parseInt(String(binding.sourceKuikaId), 10);
                const sourceDi = dict[sourceId];
                const NON_DATA_TYPES = new Set(["Button", "Icon", "Image", "Divider", "Panel"]);
                if (sourceDi?.item?.name && NON_DATA_TYPES.has(sourceDi.item.name)) {
                  const nonDataMsg = `Bound to ${sourceDi.item.name} (${binding.sourceKuikaId}) which typically doesn't hold data. Consider binding to a Table row column, form input, or data-bound Label instead.`;
                  warning = warning ? `${warning}\n${nonDataMsg}` : nonDataMsg;
                }
              }
            } catch {
              // Dictionary lookup is best-effort — don't fail the binding
            }
          }

          results.push({
            parameterName: binding.parameterName,
            actionId: effectiveActionId,
            success: true,
            message: `Bound ${binding.parameterName} on action ${effectiveActionId}`,
            ...(warning ? { warning } : {})
          });
        } catch (err: any) {
          results.push({
            parameterName: binding.parameterName || "?",
            actionId: effectiveActionId || "?",
            success: false,
            error: err.message || "Unknown error"
          });
        }
      }

      const okCount = results.filter((r) => r.success).length;
      return {
        success: okCount === results.length,
        ok: okCount,
        errors: results.length - okCount,
        total: results.length,
        results
      };
    },

    ui_logic_update_action_properties: (input) => {
      const patch = safeParse(input.patch);
      if (!patch || typeof patch !== "object") {
        return { error: "patch object is required" };
      }

      const found = findContextAction(input);
      if ("error" in found) return found;

      let nextAction = found.action;

      if ("condition" in patch) {
        nextAction = setDesignerActionCondition(nextAction, patch.condition).action;
      }
      if (
        "confirmationMessage" in patch ||
        "confirmationFontFamily" in patch ||
        "confirmationHeader" in patch ||
        "confirmationIcon" in patch ||
        "confirmationIconBackgroundColor" in patch ||
        "confirmationConfirmButtonColor" in patch ||
        "confirmationCancelButtonColor" in patch
      ) {
        // setDesignerActionConfirmation writes every field, so a partial patch
        // must carry the untouched ones over instead of blanking them.
        const keep = (key: string, current: string | undefined): string | undefined =>
          key in patch ? patch[key] : current;

        nextAction = setDesignerActionConfirmation(
          nextAction,
          keep("confirmationMessage", nextAction.confirmationMessage),
          keep("confirmationFontFamily", nextAction.confirmationFontFamily),
          keep("confirmationHeader", nextAction.confirmationHeader),
          keep("confirmationIcon", nextAction.confirmationIcon),
          keep("confirmationIconBackgroundColor", nextAction.confirmationIconBackgroundColor),
          keep("confirmationConfirmButtonColor", nextAction.confirmationConfirmButtonColor),
          keep("confirmationCancelButtonColor", nextAction.confirmationCancelButtonColor)
        ).action;
      }
      if ("recurringInterval" in patch) {
        nextAction = setDesignerActionRecurringInterval(nextAction, patch.recurringInterval).action;
      }
      if ("dontTriggerValidations" in patch) {
        nextAction = setDesignerActionDontTriggerValidations(nextAction, patch.dontTriggerValidations);
      }
      if ("addGoBackAction" in patch) {
        nextAction = setDesignerActionAddGoBackAction(nextAction, patch.addGoBackAction);
      }
      if ("tag" in patch) {
        nextAction = setDesignerActionTag(nextAction, patch.tag);
      }
      if ("note" in patch) {
        nextAction = setDesignerActionNote(nextAction, patch.note);
      }
      if ("sqlLikeSetting" in patch) {
        nextAction = setDesignerActionSqlLikeSetting(nextAction, patch.sqlLikeSetting);
      }
      if ("isAuditEnabled" in patch) {
        nextAction = setDesignerActionAuditEnabled(nextAction, patch.isAuditEnabled);
      }
      if ("auditLevel" in patch) {
        nextAction = setDesignerActionAuditLevel(nextAction, patch.auditLevel).action;
      }
      if ("enabled" in patch) {
        nextAction = setDesignerActionEnabled(nextAction, patch.enabled);
      }

      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        eventName: input.eventName || found.scope.eventName,
        updatedKeys: Object.keys(patch)
      };
    },

    ui_logic_set_action_for_each_data: (input) => {
      const forEachData = safeParse(input.forEachData);
      if (!forEachData || typeof forEachData !== "object") {
        return { error: "forEachData object is required" };
      }

      const found = findContextAction(input);
      if ("error" in found) return found;

      const nextAction = setDesignerActionForEachData(found.action, forEachData).action;
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        actionLabel: found.action.label,
        eventName: input.eventName || found.scope.eventName,
        updated: "forEachData"
      };
    },

    ui_logic_set_action_recurring_actions_data: (input) => {
      const recurringActionsData = safeParse(input.recurringActionsData);
      if (!recurringActionsData || typeof recurringActionsData !== "object") {
        return { error: "recurringActionsData object is required" };
      }

      const found = findContextAction(input);
      if ("error" in found) return found;

      const nextAction = setDesignerActionRecurringActionsData(found.action, recurringActionsData).action;
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        actionLabel: found.action.label,
        eventName: input.eventName || found.scope.eventName,
        updated: "recurringActionsData"
      };
    },

    ui_logic_set_action_managed_db_data: (input) => {
      const managedDbData = safeParse(input.managedDbData);
      if (!managedDbData || typeof managedDbData !== "object") {
        return { error: "managedDbData object is required" };
      }

      const found = findContextAction(input);
      if ("error" in found) return found;

      const nextAction = setDesignerActionManagedDbData(found.action, managedDbData).action;
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        actionLabel: found.action.label,
        eventName: input.eventName || found.scope.eventName,
        updated: "managedDbData"
      };
    },

    ui_logic_set_action_mqtt_subscribe_data: (input) => {
      const mqttSubscribeActionData = safeParse(input.mqttSubscribeActionData);
      if (!mqttSubscribeActionData || typeof mqttSubscribeActionData !== "object") {
        return { error: "mqttSubscribeActionData object is required" };
      }

      const found = findContextAction(input);
      if ("error" in found) return found;

      const nextAction = setDesignerActionMqttSubscribeData(found.action, mqttSubscribeActionData).action;
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        actionLabel: found.action.label,
        eventName: input.eventName || found.scope.eventName,
        updated: "mqttSubscribeActionData"
      };
    },

    ui_logic_add_action_to_for_each: (input) => {
      const { actionDefinitionId } = input;
      if (!actionDefinitionId) return { error: "actionDefinitionId is required" };

      const found = findContextAction(input);
      if ("error" in found) return found;

      const result = resolveAndCreateAction(found.sd, getActionDefinitions(found.sd), {
        actionDefinitionId,
        parameters: input.parameters,
        fromScreenId: input.fromScreenId
      });
      if ("error" in result) return result;

      const nextAction = addDesignerActionToCollection(found.action, "forEach", result.action).action;
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        parentActionId: input.actionId,
        actionId: result.actionId,
        label: result.action.label,
        collectionType: "forEach"
      };
    },

    ui_logic_reorder_for_each_actions: (input) => {
      const { fromIndex, toIndex } = input;
      if (fromIndex == null) return { error: "fromIndex is required" };
      if (toIndex == null) return { error: "toIndex is required" };

      const found = findContextAction(input);
      if ("error" in found) return found;

      const actions = getDesignerActionCollectionActions(found.action, "forEach");
      if (fromIndex < 0 || fromIndex >= actions.length) {
        return { error: `fromIndex ${fromIndex} out of range (0-${actions.length - 1})` };
      }
      if (toIndex < 0 || toIndex >= actions.length) {
        return { error: `toIndex ${toIndex} out of range (0-${actions.length - 1})` };
      }

      const nextAction = reorderDesignerActionCollection(found.action, "forEach", fromIndex, toIndex).action;
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        parentActionId: input.actionId,
        collectionType: "forEach",
        actionCount: actions.length
      };
    },

    ui_logic_add_action_to_recurring_actions: (input) => {
      const { actionDefinitionId } = input;
      if (!actionDefinitionId) return { error: "actionDefinitionId is required" };

      const found = findContextAction(input);
      if ("error" in found) return found;

      const result = resolveAndCreateAction(found.sd, getActionDefinitions(found.sd), {
        actionDefinitionId,
        parameters: input.parameters,
        fromScreenId: input.fromScreenId
      });
      if ("error" in result) return result;

      const nextAction = addDesignerActionToCollection(found.action, "recurring", result.action).action;
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        parentActionId: input.actionId,
        actionId: result.actionId,
        label: result.action.label,
        collectionType: "recurring"
      };
    },

    ui_logic_reorder_recurring_actions: (input) => {
      const { fromIndex, toIndex } = input;
      if (fromIndex == null) return { error: "fromIndex is required" };
      if (toIndex == null) return { error: "toIndex is required" };

      const found = findContextAction(input);
      if ("error" in found) return found;

      const actions = getDesignerActionCollectionActions(found.action, "recurring");
      if (fromIndex < 0 || fromIndex >= actions.length) {
        return { error: `fromIndex ${fromIndex} out of range (0-${actions.length - 1})` };
      }
      if (toIndex < 0 || toIndex >= actions.length) {
        return { error: `toIndex ${toIndex} out of range (0-${actions.length - 1})` };
      }

      const nextAction = reorderDesignerActionCollection(found.action, "recurring", fromIndex, toIndex).action;
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        parentActionId: input.actionId,
        collectionType: "recurring",
        actionCount: actions.length
      };
    },

    ui_logic_add_action_to_mqtt_subscribe: (input) => {
      const { actionDefinitionId } = input;
      if (!actionDefinitionId) return { error: "actionDefinitionId is required" };

      const found = findContextAction(input);
      if ("error" in found) return found;

      const result = resolveAndCreateAction(found.sd, getActionDefinitions(found.sd), {
        actionDefinitionId,
        parameters: input.parameters,
        fromScreenId: input.fromScreenId
      });
      if ("error" in result) return result;

      const nextAction = addDesignerActionToCollection(found.action, "mqttSubscribe", result.action).action;
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        parentActionId: input.actionId,
        actionId: result.actionId,
        label: result.action.label,
        collectionType: "mqttSubscribe"
      };
    },

    ui_logic_reorder_mqtt_subscribe_actions: (input) => {
      const { fromIndex, toIndex } = input;
      if (fromIndex == null) return { error: "fromIndex is required" };
      if (toIndex == null) return { error: "toIndex is required" };

      const found = findContextAction(input);
      if ("error" in found) return found;

      const actions = getDesignerActionCollectionActions(found.action, "mqttSubscribe");
      if (fromIndex < 0 || fromIndex >= actions.length) {
        return { error: `fromIndex ${fromIndex} out of range (0-${actions.length - 1})` };
      }
      if (toIndex < 0 || toIndex >= actions.length) {
        return { error: `toIndex ${toIndex} out of range (0-${actions.length - 1})` };
      }

      const nextAction = reorderDesignerActionCollection(found.action, "mqttSubscribe", fromIndex, toIndex).action;
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        parentActionId: input.actionId,
        collectionType: "mqttSubscribe",
        actionCount: actions.length
      };
    },

    ui_logic_add_action_confirmation: (input) => {
      input = autoResolveActionId(input);
      const found = findContextAction(input);
      if ("error" in found) return found;

      // Meaningful, localized, context-aware defaults. The Wirer passes
      // `kind: "delete"` for destructive actions → trash icon + red + an
      // "irreversible" warning in the app's language. Explicit per-field
      // overrides (message/header/icon/iconBackgroundColor) always win.
      const ci = input as IConfirmationInput;
      const locale: AiLocale = resolveProjectLocale(getSdInstance());
      const kind: ConfirmationKind = ci.kind === "delete" ? "delete" : "default";
      const defaults: ConfirmationDefaults = getConfirmationDefaults(kind, locale);

      const nextAction = addDesignerActionConfirmation(found.action, {
        message: ci.message ?? defaults.message,
        header: ci.header ?? defaults.header,
        icon: ci.icon ?? defaults.icon,
        iconBackgroundColor: ci.iconBackgroundColor ?? defaults.iconBackgroundColor,
        confirmButtonColor: ci.confirmButtonColor,
        cancelButtonColor: ci.cancelButtonColor
      });
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        actionLabel: found.action.label,
        eventName: input.eventName || found.scope.eventName,
        applied: "confirmation_added"
      };
    },

    ui_logic_remove_action_confirmation: (input) => {
      input = autoResolveActionId(input);
      const found = findContextAction(input);
      if ("error" in found) return found;

      const nextAction = removeDesignerActionConfirmation(found.action);
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        actionLabel: found.action.label,
        eventName: input.eventName || found.scope.eventName,
        applied: "confirmation_removed"
      };
    },

    ui_logic_add_action_recurring: (input) => {
      const found = findContextAction(input);
      if ("error" in found) return found;

      const nextAction = addDesignerActionRecurring(found.action);
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        actionLabel: found.action.label,
        eventName: input.eventName || found.scope.eventName,
        applied: "recurring_added"
      };
    },

    ui_logic_remove_action_recurring: (input) => {
      const found = findContextAction(input);
      if ("error" in found) return found;

      const nextAction = removeDesignerActionRecurring(found.action);
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        actionLabel: found.action.label,
        eventName: input.eventName || found.scope.eventName,
        applied: "recurring_removed"
      };
    },

    ui_logic_add_action_condition: (input) => {
      const found = findContextAction(input);
      if ("error" in found) return found;

      const nextAction = addDesignerActionBlankCondition(found.action);
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        actionLabel: found.action.label,
        eventName: input.eventName || found.scope.eventName,
        applied: "condition_added"
      };
    },

    ui_logic_remove_action_condition: (input) => {
      const found = findContextAction(input);
      if ("error" in found) return found;

      const nextAction = removeDesignerActionCondition(found.action);
      persistResolvedAction(found, nextAction);

      return {
        success: true,
        actionId: input.actionId,
        actionLabel: found.action.label,
        eventName: input.eventName || found.scope.eventName,
        applied: "condition_removed"
      };
    },

    ui_logic_set_action_condition: (input) => {
      // Accept both "operator" (from tool schema) and "conditionOperator" (internal name)
      // Also extract from conditionSet if LLM nested it there (common LLM mistake)
      const conditionOperator =
        input.conditionOperator ||
        input.operator ||
        input.conditionSet?.conditionOperator ||
        input.conditionSet?.operator;
      const { field1, field2 } = input;
      if (!field1) return { error: "field1 is required" };
      if (!conditionOperator)
        return {
          error:
            "conditionOperator (or operator) is required. Pass as top-level param: { actionId, conditionOperator: 'isEqualTo'|'isNotEqualTo'|'isGreaterThan'|'contains'|..., field1: {...}, field2: {...} }"
        };

      const found = findContextAction(input);
      if ("error" in found) return found;
      const { action } = found;

      if (action.label !== "IfThenElse" && action.description !== "IfThenElse") {
        return { error: `Action "${input.actionId}" is not an IfThenElse action` };
      }

      const isUnary = UNARY_OPERATORS.includes(conditionOperator);
      if (!isUnary && !field2) {
        return {
          error: `conditionOperator "${conditionOperator}" requires field2. Only ${UNARY_OPERATORS.join(", ")} are unary.`
        };
      }

      const sdForCondition = getSdInstance();
      const field1Result = buildConditionFieldSymbol(field1, sdForCondition, "field1");
      if (field1Result.error) return { error: field1Result.error };

      const condition: any = {
        field1: field1Result.symbol,
        conditionOperator
      };

      if (!isUnary && field2) {
        const field2Result = buildConditionFieldSymbol(field2, sdForCondition, "field2");
        if (field2Result.error) return { error: field2Result.error };
        condition.field2 = field2Result.symbol;
      }

      if (!action.ifThenElseData) {
        action.ifThenElseData = { condition, ifActions: [], elseActions: [] };
      } else {
        action.ifThenElseData.condition = condition;
      }

      persistResolvedAction(found, action);

      return {
        success: true,
        actionId: input.actionId,
        condition: {
          field1,
          conditionOperator,
          field2: isUnary ? undefined : field2
        }
      };
    },

    ui_logic_add_action_to_branch: (input) => {
      // Normalize branch: accept "then" as alias for "if" (tool docs historically used "then|else")
      const rawBranch = input.branch;
      const branch = rawBranch === "then" ? "if" : rawBranch;
      // Accept both flat actionDefinitionId and actions[] array (matches add_to_event pattern)
      let actionDefinitionId = input.actionDefinitionId;
      if (!actionDefinitionId && Array.isArray(input.actions) && input.actions.length > 0) {
        actionDefinitionId = input.actions[0].actionDefinitionId;
        if (!input.parameters && input.actions[0].parameters) {
          input.parameters = input.actions[0].parameters;
        }
        if (!input.fromScreenId && input.actions[0].fromScreenId) {
          input.fromScreenId = input.actions[0].fromScreenId;
        }
      }
      if (!branch) return { error: "branch is required ('if' or 'else')" };
      if (branch !== "if" && branch !== "else") return { error: "branch must be 'if' or 'else'" };
      if (!actionDefinitionId)
        return { error: "actionDefinitionId is required. Pass directly or as actions[{actionDefinitionId}]" };

      const found = findContextAction(input);
      if ("error" in found) return found;
      const { sd, action } = found;

      if (action.label !== "IfThenElse" && action.description !== "IfThenElse") {
        return { error: `Action "${input.actionId}" is not an IfThenElse action` };
      }

      if (!action.ifThenElseData) {
        action.ifThenElseData = { condition: {}, ifActions: [], elseActions: [] };
      }

      const allDefs = getAllActionDefinitions(sd);
      const result = resolveAndCreateAction(sd, allDefs, {
        actionDefinitionId,
        parameters: input.parameters,
        fromScreenId: input.fromScreenId
      });
      if ("error" in result) return result;

      const { action: newAction, actionId: newActionId } = result;

      // Add to branch
      const branchArray = branch === "if" ? action.ifThenElseData.ifActions : action.ifThenElseData.elseActions;

      // Fix #54 — block duplicate branch attaches. The May-2026
      // fikir-havuzu rerun at 11:35 hit this: Wirer called
      // `create_conditional` (which atomically populated both branches)
      // and then called `add_to_branch(if, Insert)` + `add_to_branch(else, Update)`
      // again — branch arrays ended up with 2x Insert and 2x Update,
      // both firing on every save click. The duplicate has the same
      // actionDefinitionId as the existing entry; that's the cheapest
      // signature to compare against without false positives on
      // legitimately-stacked-different-actions branches.
      const existingWithSameDef: { id?: unknown; actionDefinitionId?: unknown } | undefined = branchArray.find(
        (a: { actionDefinitionId?: unknown }) =>
          typeof a?.actionDefinitionId === "string" && a.actionDefinitionId === newAction.actionDefinitionId
      );
      if (existingWithSameDef) {
        const existingId: string = typeof existingWithSameDef.id === "string" ? existingWithSameDef.id : "(unknown)";
        return {
          error:
            `[VALIDATION] add_to_branch: cannot add a second instance of "${newAction.label}" ` +
            `(actionDefinitionId="${newAction.actionDefinitionId}") to the "${branch}" branch — ` +
            `that branch already contains an instance (actionId="${existingId}"). ` +
            `If you used \`create_conditional\` earlier, the branches are ALREADY populated — ` +
            `there is no second-step \`add_to_branch\` to run. STOP and report done. ` +
            `If the existing instance is wrong (e.g. you want different parameters), ` +
            `use \`ui_action.delete_from_branch\` on the existing branchActionId first, ` +
            `then re-add. Do NOT retry add_to_branch with the same definition — every retry ` +
            `produces a duplicate that fires the mutation twice.`
        };
      }

      branchArray.push(newAction);

      persistResolvedAction(found, action);

      return {
        success: true,
        branchActionId: newActionId,
        label: newAction.label,
        branch,
        parentActionId: input.actionId,
        // KOA-18193 — same KV1170 debt as the plain attach; a branch-resident
        // action is if anything harder to notice afterwards, because the branch
        // has to be taken before anyone sees the failure.
        ...(result.unboundRequiredParameters && result.unboundRequiredParameters.length > 0
          ? {
              unboundRequiredParameters: result.unboundRequiredParameters,
              unboundRequiredWarning:
                `Attached to the "${branch}" branch, but ${result.unboundRequiredParameters.length} REQUIRED ` +
                `parameter(s) have no source: ${result.unboundRequiredParameters.join(", ")}. Code generation ` +
                `will block with KV1170. Bind them with ui_action.set_multiple_parameter_bindings using actionId ` +
                `"${newActionId}".`
            }
          : {})
      };
    },

    ui_logic_delete_action_from_branch: (input) => {
      const { branch, branchActionId } = input;
      if (!branch) return { error: "branch is required ('if' or 'else')" };
      if (branch !== "if" && branch !== "else") return { error: "branch must be 'if' or 'else'" };
      if (!branchActionId) return { error: "branchActionId is required" };

      const found = findContextAction(input);
      if ("error" in found) return found;
      const { action } = found;

      if (action.label !== "IfThenElse" && action.description !== "IfThenElse") {
        return { error: `Action "${input.actionId}" is not an IfThenElse action` };
      }

      if (!action.ifThenElseData) {
        return { error: "This action has no ifThenElseData" };
      }

      const branchArray = branch === "if" ? action.ifThenElseData.ifActions : action.ifThenElseData.elseActions;
      const idx = branchArray.findIndex((a: any) => a.id === branchActionId);
      if (idx === -1) {
        return { error: `Action "${branchActionId}" not found in "${branch}" branch` };
      }

      branchArray.splice(idx, 1);

      persistResolvedAction(found, action);

      return {
        success: true,
        deletedBranchActionId: branchActionId,
        branch,
        parentActionId: input.actionId
      };
    },

    ui_logic_create_conditional_action: (input) => {
      const { kuikaId, eventName } = input;

      let condition = safeParse(input.condition);

      // Fallback: if LLM passed field1/conditionOperator at top level instead of inside condition object, assemble it
      if (!condition && input.field1) {
        condition = {
          field1: input.field1,
          conditionOperator: input.conditionOperator || input.operator,
          field2: input.field2
        };
      }
      // Also try extracting from conditionSet if LLM nested it there
      if (!condition && input.conditionSet) {
        const cs = safeParse(input.conditionSet);
        if (cs?.conditions?.[0]) {
          condition = cs.conditions[0];
          if (!condition.conditionOperator && cs.conditionOperator) {
            condition.conditionOperator = cs.conditionOperator;
          }
        }
      }

      // Accept both "operator" (from tool schema) and "conditionOperator" (internal name)
      if (condition && !condition.conditionOperator && condition.operator) {
        condition.conditionOperator = condition.operator;
      }
      if (!condition || !condition.field1 || !condition.conditionOperator) {
        return {
          error:
            "condition object with field1 and conditionOperator is required. Example: { condition: { field1: {kuikaId: '502347', propertyName: 'value'}, conditionOperator: 'isNotEmpty' }, ifActions: [{actionDefinitionId: 'NAVIGATE', parameters: [{name: 'toScreenId', value: 'screen-guid'}]}] }"
        };
      }

      const ifActions = safeParse(input.ifActions);
      if (!Array.isArray(ifActions) || ifActions.length === 0) {
        return { error: "ifActions must be a non-empty array" };
      }

      const isUnary = UNARY_OPERATORS.includes(condition.conditionOperator);
      if (!isUnary && !condition.field2) {
        return {
          error: `conditionOperator "${condition.conditionOperator}" requires field2. Only ${UNARY_OPERATORS.join(", ")} are unary.`
        };
      }

      const sd = getSdInstance();
      const isScreenEventTarget =
        kuikaId || eventName || input.contextType === "screen_event" || input.context === "screen_event";

      if (isScreenEventTarget) {
        if (!kuikaId) return { error: "kuikaId is required" };
        if (!eventName) return { error: "eventName is required" };

        const dict = sd.screenProvider.state.screenDesignDictionary;
        const numericId = parseInt(kuikaId, 10);
        const di = dict[numericId];
        if (di) {
          const availableEvents = getAvailableEvents(di);
          if (availableEvents.length > 0 && !availableEvents.includes(eventName)) {
            return {
              success: false,
              error: `Event '${eventName}' not available on this component. Available events: [${availableEvents.join(", ")}]`
            };
          }
        }
      }

      const allDefs = getAllActionDefinitions(sd);

      // Helper to resolve action def by ID, name, or catalog fallback
      const resolveActionDef = (spec: any): any => {
        const defId = spec.actionDefinitionId;
        let actionDef = allDefs.find((a: any) => a.id === defId);
        if (!actionDef) {
          const searchName = defId.toLowerCase().replace(/\s+/g, "");
          actionDef = allDefs.find((a: any) => {
            const name = (a.name || "").toLowerCase().replace(/\s+/g, "");
            const displayName = (a.displayName || "").toLowerCase().replace(/\s+/g, "");
            return name === searchName || displayName === searchName;
          });
        }
        return actionDef;
      };

      // Resolve IfThenElse action definition
      const ifThenElseDefId = "EEDB319E-072B-45F7-98AF-8E817EB57A6D";
      let ifThenElseDef = allDefs.find((a: any) => a.id === ifThenElseDefId);
      if (!ifThenElseDef) {
        ifThenElseDef = allDefs.find((a: any) => (a.name || "").toLowerCase() === "ifthenelse");
      }
      if (!ifThenElseDef) {
        return { error: "IfThenElse action definition not found. Action definitions may not be loaded yet." };
      }

      // Create the IfThenElse action
      const ifThenElseActionId = Guid.create().toString();
      const ifThenElseAction: any = {
        id: ifThenElseActionId,
        actionDefinitionId: ifThenElseDef.id,
        label: ifThenElseDef.name,
        description: ifThenElseDef.name,
        parameters: (ifThenElseDef.actionParameters || []).map((p: any) => cloneActionParameter(p)),
        targetScreenInputParams: [],
        returnType: ifThenElseDef.returnTypeName,
        returnCardinality: ifThenElseDef.returnCardinality,
        dontTriggerValidations: false,
        appSupportType: 0
      };

      // Build condition — Fix #39 routes prefixed strings / object shapes to
      // their proper symbol category instead of stuffing everything into
      // ESymbolCategory.fixed (which displayed "[object Object] isEmpty"
      // for the dual-mode upsert case in the May-2026 fikir-havuzu rerun).
      const field1Result = buildConditionFieldSymbol(condition.field1, sd, "condition.field1");
      if (field1Result.error) return { error: field1Result.error };

      const conditionData: any = {
        field1: field1Result.symbol,
        conditionOperator: condition.conditionOperator
      };

      if (!isUnary && condition.field2) {
        const field2Result = buildConditionFieldSymbol(condition.field2, sd, "condition.field2");
        if (field2Result.error) return { error: field2Result.error };
        conditionData.field2 = field2Result.symbol;
      }

      // Build branch actions
      const buildBranchActions = (
        specs: any[],
        branchName: string
      ): { actions: any[]; ids: string[]; labels: string[] } | { error: string } => {
        const actions: any[] = [];
        const ids: string[] = [];
        const labels: string[] = [];

        for (let i = 0; i < specs.length; i++) {
          const spec = specs[i];
          if (!spec.actionDefinitionId) {
            return { error: `${branchName}[${i}]: actionDefinitionId is required` };
          }

          const actionDef = resolveActionDef(spec);
          if (!actionDef) {
            return {
              error: `${branchName}[${i}]: Action definition "${spec.actionDefinitionId}" not found. Use common_get_all_action_names to discover available actions.`
            };
          }

          const newActionId = Guid.create().toString();
          const newAction: any = {
            id: newActionId,
            actionDefinitionId: actionDef.id,
            label: actionDef.name,
            description: actionDef.name,
            parameters: (actionDef.actionParameters || []).map((p: any) => cloneActionParameter(p)),
            targetScreenInputParams: [],
            returnType: actionDef.returnTypeName,
            returnCardinality: actionDef.returnCardinality,
            dontTriggerValidations: false,
            appSupportType: 0
          };

          // Apply parameter overrides
          const paramOverrides = safeParse(spec.parameters);
          // Fix #10 — NAVIGATE alias / screen-name normalisation also
          // applies to conditional branch actions (an IfThenElse wrapping
          // a NAVIGATE).
          normalizeNavigateParameterOverrides(actionDef, paramOverrides, sd);
          if (Array.isArray(paramOverrides)) {
            for (const override of paramOverrides) {
              const overrideName = normalizeOverrideName(override);
              if (!overrideName) continue;
              const param = findDesignerActionParameter(newAction.parameters, overrideName);
              if (!param) continue;

              const symbol = resolveParameterSymbol(override, sd, newAction, param);
              if (!symbol) continue;

              // KOA-18136 — same row-context enrichment as the unbranched loop in
              // `resolveAndCreateAction`. A branch action is the DESTRUCTIVE row
              // path (`isDestructiveAttach` wraps a row delete in an IfThenElse), so
              // an unenriched `current.Id` here means the confirmed delete fires with
              // an empty key.
              const branchParamType: string | undefined =
                typeof param?.Typename === "string" ? String(param.Typename) : undefined;
              const enrichedBranchSymbol = enrichCurrentRowSymbol(symbol, sd, rowHostKuikaId(kuikaId), branchParamType);

              const nextAction = setDesignerActionParameter(newAction, param, enrichedBranchSymbol);
              newAction.parameters = nextAction.parameters;
            }
          }

          // Auto-inject auto-set parameters
          applyAutoSetParameters(newAction, sd, input.fromScreenId);

          // Same proactive semantic defaults as the top-level
          // `resolveAndCreateAction` flow — branch actions wrapped in an
          // IfThenElse (e.g. `if X then Foo_Insert`) need PK/user-FK
          // binding too. Without this, a conditional Insert silently
          // ships with empty Id / null UserId at runtime exactly the
          // same way an unbranched one would.
          applySemanticParameterDefaults(newAction, actionDef);

          actions.push(newAction);
          ids.push(newActionId);
          labels.push(actionDef.name);
        }

        return { actions, ids, labels };
      };

      const ifResult = buildBranchActions(ifActions, "ifActions");
      if ("error" in ifResult) return ifResult;

      const elseSpecs = safeParse(input.elseActions);
      let elseResult: { actions: any[]; ids: string[]; labels: string[] } = { actions: [], ids: [], labels: [] };
      if (Array.isArray(elseSpecs) && elseSpecs.length > 0) {
        const r = buildBranchActions(elseSpecs, "elseActions");
        if ("error" in r) return r;
        elseResult = r;
      }

      const dualModeError = validateDualModeUpsertSplit(ifResult.labels, elseResult.labels);
      if (dualModeError) return { error: dualModeError };

      // Assemble ifThenElseData
      ifThenElseAction.ifThenElseData = {
        condition: conditionData,
        ifActions: ifResult.actions,
        elseActions: elseResult.actions
      };

      // Single save
      if (isScreenEventTarget) {
        const notPersisted = attachToScreenEventOrExplain(sd, kuikaId, eventName, ifThenElseAction);
        if (notPersisted) return notPersisted;
      } else {
        const scope = resolveDesignerActionScope(sd, input);
        if ("error" in scope) return scope;
        scope.saveTopLevelAction(ifThenElseAction);
      }

      return {
        success: true,
        actionId: ifThenElseActionId,
        ifActionIds: ifResult.ids,
        elseActionIds: elseResult.ids,
        ifActionLabels: ifResult.labels,
        elseActionLabels: elseResult.labels
      };
    },

    ui_logic_reorder_branch_actions: (input) => {
      const { branch, fromIndex, toIndex } = input;
      if (!branch) return { error: "branch is required ('if' or 'else')" };
      if (branch !== "if" && branch !== "else") return { error: "branch must be 'if' or 'else'" };
      if (fromIndex == null) return { error: "fromIndex is required" };
      if (toIndex == null) return { error: "toIndex is required" };

      const found = findContextAction(input);
      if ("error" in found) return found;
      const { action } = found;

      if (action.label !== "IfThenElse" && action.description !== "IfThenElse") {
        return { error: `Action "${input.actionId}" is not an IfThenElse action` };
      }

      if (!action.ifThenElseData) {
        return { error: "This action has no ifThenElseData" };
      }

      const branchArray = branch === "if" ? action.ifThenElseData.ifActions : action.ifThenElseData.elseActions;

      if (fromIndex < 0 || fromIndex >= branchArray.length) {
        return { error: `fromIndex ${fromIndex} out of range (0-${branchArray.length - 1})` };
      }
      if (toIndex < 0 || toIndex >= branchArray.length) {
        return { error: `toIndex ${toIndex} out of range (0-${branchArray.length - 1})` };
      }

      const reordered = Array.from(branchArray);
      const [removed] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, removed);

      if (branch === "if") {
        action.ifThenElseData.ifActions = reordered;
      } else {
        action.ifThenElseData.elseActions = reordered;
      }

      persistResolvedAction(found, action);

      return {
        success: true,
        branch,
        parentActionId: input.actionId,
        actionCount: reordered.length,
        newOrder: reordered.map((a: any) => a.label || a.description || "Unnamed")
      };
    }
  },

  async: {
    /**
     * KOA-18136 — bind a parameter to a value the RUNNING PROCESS carries.
     *
     * The synchronous write resolves the managed-screen inventory from designer
     * state, which in a headless turn holds no workflow parameters at all, so it
     * refused every binding to one — while the inventory READ, which fetches,
     * had just offered those same parameters as available. `runProcessBindingWrite`
     * closes that asymmetry: on a refusal naming a process value, load the
     * process and run the identical write once more.
     */
    ui_logic_set_action_parameter_binding: async (input) =>
      withProcessWorkflowLoad(input, () => actionTools.sync!.ui_logic_set_action_parameter_binding(input)),

    ui_logic_set_multiple_parameter_bindings: async (input) =>
      withProcessWorkflowLoad(input, () => actionTools.sync!.ui_logic_set_multiple_parameter_bindings(input)),

    /**
     * KOA-18136 — scope a TASK FEED to one process and choose which of that
     * process's fields ride back on every task row.
     *
     * This is the panel's "Select from single process" switch, made reachable. It
     * exists as its own operation because the configuration is THREE records that
     * must agree (`ProcessDefinitionId`, `ProcessParameters`, and the action
     * instance's dynamic result contract) and only the third makes the fields
     * bindable — the column picker offers `static return fields + this instance's
     * dynamic elements`, the cell-binding enrichment needs the element to stamp
     * `fieldToDisplay`/`fieldType`, and React codegen keys its value reader on the
     * element's `data` prefix. Two generic parameter writes cannot express that,
     * and a half-written scope is invisible: backend validation is deliberately
     * lenient about `data.<Param>` bindings, so the screen generates cleanly and
     * shows nothing.
     *
     * Async because the process is READ from the service: designer state carries a
     * workflow's parameters only while the modeler holds it, and a build turn is
     * nowhere near the BPMN editor.
     *
     * WHY it matters, from four live builds: the approver's inbox was given six
     * columns headed "supplier / visit reason / work area / visit date" bound to
     * `ProcessName` (the definition name — identical on every row), `Description`
     * (the STEP's description), `App` (the project id) and `DueDate` (the task
     * deadline). Those business fields are process parameters. Nothing but a human
     * clicking this panel could bring them into the read, so the model mapped the
     * meanings onto whatever engine field looked closest.
     */
    ui_logic_set_task_feed_process: async (input) => {
      const found = findContextAction(input);
      if ("error" in found) return found;

      const actionLabel: string = typeof found.action?.label === "string" ? found.action.label : "";

      // `clear: true` is the switch going OFF — both parameters emptied and the
      // dynamic contract emptied with them, so no stale field stays offered.
      const clearing: boolean = input.clear === true;
      const requestedFields: string[] = Array.isArray(input.processParameters)
        ? input.processParameters.map((value: unknown): string => String(value ?? ""))
        : typeof input.processParameters === "string" && input.processParameters.trim().length > 0
          ? String(input.processParameters)
              .split(",")
              .map((value: string): string => value.trim())
          : [];

      let plan: ITaskFeedScopePlan;
      if (clearing) {
        plan = clearedTaskFeedScope();
      } else {
        const processId: string = typeof input.processId === "string" ? input.processId.trim() : "";
        if (processId.length === 0) {
          return { error: "processId is required (or pass clear: true to unscope the feed)" };
        }
        const workflow: ITaskFeedWorkflowLike | undefined = await fetchManagedScreenWorkflow(processId);
        const outcome: TaskFeedScopeOutcome = resolveTaskFeedScope({
          actionName: actionLabel,
          workflow,
          processId,
          requestedFields
        });
        if (isTaskFeedScopeRefusal(outcome)) {
          return {
            error: outcome.message,
            refused: outcome.refused,
            availableFields: outcome.availableFields
          };
        }
        plan = outcome;
      }

      // Reuse each parameter's own declared shape when it is already on the action
      // (the signature carries both), and fall back to the metadata's types when a
      // thin signature left one off.
      const parameterWrite = (name: string, value: string, typeName: string, cardinality: ECardinality) => {
        const existing = findActionParameter(found.action, name);
        const base = existing ?? { name, Typename: typeName, cardinality };
        return {
          ...base,
          name: existing?.name ?? name,
          symbol: {
            ...(existing?.symbol ?? {}),
            isFixed: true,
            fixedValue: value,
            category: ESymbolCategory.fixed,
            currentType: typeName,
            targetType: typeName
          } as ISymbol
        } as IActionParameter;
      };

      const nextAction = setDesignerActionValues(
        found.action,
        {
          actionParameters: [
            parameterWrite(TASK_FEED_PROCESS_PARAM, plan.processId, "String", ECardinality.One),
            parameterWrite(TASK_FEED_FIELDS_PARAM, plan.parameterIdList, "String", ECardinality.Many)
          ],
          actionReturnTypes: plan.dynamicInterface
        },
        ESdChangeReason.ActionDescriptionChanged
      ).action;
      persistResolvedAction(found, nextAction, ESdChangeReason.ActionDescriptionChanged);

      // A column bound to `data.<Param>` was written BEFORE these fields existed
      // — the table has to exist before the feed on it can be scoped — so its
      // binding never got the field stamp codegen reads. Finish those columns here
      // rather than making the caller order two calls correctly.
      const repairedColumns: ITaskFeedColumnRepair[] = repairTaskFeedColumns(getSdInstance());

      return {
        success: true,
        actionId: input.actionId,
        actionLabel,
        scoped: !clearing,
        ...(repairedColumns.length > 0
          ? { completedColumns: repairedColumns.map((c: ITaskFeedColumnRepair): string => c.fieldName) }
          : {}),
        processId: plan.processId,
        // The paths a table cell may now bind. Without the contract above these
        // do not exist, so reporting them IS the proof the write landed.
        rowFields: plan.fields.map((field): string => field.fieldPath),
        updated: ["ProcessDefinitionId", "ProcessParameters", "returnTypeDynamicInterface"]
      };
    },

    /**
     * KOA-18136 — make an approver inbox's feed match the columns it shows, and
     * report the columns that can only paint a raw enum.
     *
     * The DETERMINISTIC backstop behind `set_task_feed_process`. A `data.<Param>`
     * column is the screen's own statement of what it needs, so the feed is scoped
     * to exactly those fields — read from the canvas, not from anyone's memory of
     * what was rendered. That closes the case the teaching cannot: a turn that
     * bound the columns and never called the scope write ships a screen whose
     * columns resolve to nothing, and validation stays silent about it (the
     * `data.<Param>` check is deliberately lenient).
     *
     * The process: `processId` when the caller knows it (the build that just
     * created it), else the project's ONLY workflow. With several processes and no
     * id there is nothing to infer, so nothing is written — the feed keeps spanning
     * them all, which is the correct default for a multi-process inbox.
     *
     * Status columns are REPORTED, never deleted here: removal belongs to the
     * component tool that also sweeps the actions a removed subtree released.
     */
    ui_logic_reconcile_task_feed: async (input) => {
      const sd = getSdInstance();
      const state = getValues(sd);
      const screenDesign = sd.screenProvider?.getScreenDesign?.();
      const dictionary = state?.screenDesignDictionary;
      if (!screenDesign || !dictionary) return { error: "No screen design loaded" };

      // Action instances on this screen, so a datasource symbol's `resultActionId`
      // can be read back to the action's name.
      const screenActions = collectActionsFromDisplayItems(screenDesign.displayItems || []);
      const labelByActionId: Map<string, string> = new Map<string, string>();
      for (const entry of screenActions) {
        if (entry.actionId) labelByActionId.set(String(entry.actionId), String(entry.label ?? ""));
      }
      const feedInstances = screenActions.filter((entry: any) => isTaskFeedAction(String(entry.label ?? "")));
      if (feedInstances.length === 0) return { success: true, feeds: 0, message: "No task feed on this screen." };

      const propertySdHelper = new PropertySdHelper(() => state);
      const resolveDatasourceActionName = (di: IDisplayItem): string | undefined => {
        const symbol: ISymbol | undefined = propertySdHelper.getTableOptionPropSymbolByDi(di);
        const resultActionId: string | undefined =
          symbol?.resultActionId === undefined || symbol.resultActionId === null
            ? undefined
            : String(symbol.resultActionId);
        if (resultActionId === undefined) return undefined;
        return labelByActionId.get(resultActionId);
      };

      const hosted: Set<string> = collectTaskFeedHostedComponents(dictionary, resolveDatasourceActionName);
      const reading: ITaskFeedScreenReading = readTaskFeedScreen(dictionary, hosted);

      // Resolve the process: what the caller named, else the project's only one.
      let processId: string = typeof input.processId === "string" ? input.processId.trim() : "";
      let processSource: string = processId.length > 0 ? "caller" : "";
      if (processId.length === 0) {
        const workflows = state?.workFlowList ?? [];
        if (workflows.length === 1) {
          processId = String((workflows[0] as { id?: unknown })?.id ?? "");
          processSource = "only-process";
        }
      }

      const scoped: string[] = [];
      const skipped: string[] = [];
      if (reading.boundFields.length > 0 && processId.length > 0) {
        const workflow = await fetchManagedScreenWorkflow(processId);
        const available: ITaskFeedFieldOption[] = listTaskFeedFieldOptions(workflow);
        const availableFolded: Set<string> = new Set<string>(
          available.map((option: ITaskFeedFieldOption): string => option.name.toLowerCase())
        );
        // Filter to the fields THIS process really has: a screen may host two feeds
        // from two processes, and asking one for the other's field would abort the
        // whole write instead of scoping what it can.
        const wanted: string[] = reading.boundFields
          .map((field: ITaskFeedBoundField): string => field.name)
          .filter((name: string): boolean => availableFolded.has(name.toLowerCase()));
        for (const feed of feedInstances) {
          if (wanted.length === 0) {
            skipped.push(String(feed.actionId));
            continue;
          }
          const result = await actionTools.async!.ui_logic_set_task_feed_process({
            actionId: feed.actionId,
            kuikaId: feed.componentKuikaId,
            eventName: feed.eventName,
            processId,
            processParameters: wanted
          });
          if (result?.success === true) scoped.push(String(feed.actionId));
          else skipped.push(String(feed.actionId));
        }
      }

      return {
        success: true,
        feeds: feedInstances.length,
        ...(processId.length > 0 ? { processId, processSource } : {}),
        boundProcessFields: reading.boundFields.map((f: ITaskFeedBoundField): string => f.name),
        scopedActionIds: scoped,
        ...(skipped.length > 0 ? { unscopedActionIds: skipped } : {}),
        // Reported for the caller to remove with ui_component.delete — a column
        // bound to the task read's raw status enum can only paint a number, and on
        // an inbox every row is WAITING, so it says nothing even when it renders.
        unshowableColumns: reading.unshowableColumns.map((c: ITaskFeedUnshowableColumn) => ({
          fieldName: c.fieldName,
          kuikaId: c.columnKuikaId ?? c.boundKuikaId,
          removes: c.columnKuikaId !== undefined ? "column" : "binding-host"
        }))
      };
    },

    ui_logic_add_action_to_event: async (input) => {
      const syncHandler = actionTools.sync!.ui_logic_add_action_to_event;
      // Attaching an action can carry its bindings inline, so it needs the same
      // process load as an explicit bind — otherwise an action attached to a task
      // screen ships with the very parameters this repair exists to fill empty.
      const result = await withProcessWorkflowLoad(input, () => syncHandler(input));
      const hasNotFoundError =
        (result?.error && /not found/i.test(result.error)) ||
        result?.errors?.some((e: any) => /not found/i.test(e.error));
      if (hasNotFoundError) {
        const sd = getSdInstance();
        await reloadActionDefinitions(sd);
        return withProcessWorkflowLoad(input, () => syncHandler(input));
      }
      return result;
    },

    ui_logic_add_multiple_actions_to_event: async (input) => {
      const syncHandler = actionTools.sync!.ui_logic_add_multiple_actions_to_event;
      const result = syncHandler(input);
      const hasNotFoundError =
        result?.errors?.some((e: any) => /not found/i.test(e.error)) ||
        (result?.error && /not found/i.test(result.error));
      if (hasNotFoundError) {
        const sd = getSdInstance();
        await reloadActionDefinitions(sd);
        return syncHandler(input);
      }
      return result;
    },

    ui_logic_create_conditional_action: async (input) => {
      const syncHandler = actionTools.sync!.ui_logic_create_conditional_action;
      const result = syncHandler(input);
      if (result?.error && /not found/i.test(result.error)) {
        const sd = getSdInstance();
        await reloadActionDefinitions(sd);
        return syncHandler(input);
      }
      return result;
    }
  }
};
